/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package i18n.translations

import i18n.StringKey

/**
 * French translations for the application.
 */
val frenchStrings: Map<StringKey, String> = mapOf(
    // App name and branding
    StringKey.APP_NAME to "wiz\u2261r",
    StringKey.APP_TAGLINE to "Un seul chiffre. Des possibilit\u00e9s infinies.",

    // Navigation
    StringKey.NAV_DASHBOARD to "Tableau",
    StringKey.NAV_TRANSACTIONS to "Transactions",
    StringKey.NAV_GOALS to "Objectifs",
    StringKey.NAV_BUDGET to "Budget",
    StringKey.NAV_SETTINGS to "Param\u00e8tres",
    StringKey.NAV_BACK to "Retour",
    StringKey.NAV_HOME to "Accueil",

    // Authentication
    StringKey.AUTH_LOGIN to "Connexion",
    StringKey.AUTH_LOGOUT to "D\u00e9connexion",
    StringKey.AUTH_REGISTER to "S'inscrire",
    StringKey.AUTH_EMAIL to "Adresse courriel",
    StringKey.AUTH_PASSWORD to "Mot de passe",
    StringKey.AUTH_CONFIRM_PASSWORD to "Confirmer le mot de passe",
    StringKey.AUTH_FIRST_NAME to "Pr\u00e9nom",
    StringKey.AUTH_LAST_NAME to "Nom de famille",
    StringKey.AUTH_POSTAL_CODE to "Code postal",
    StringKey.AUTH_PHONE to "Num\u00e9ro de t\u00e9l\u00e9phone (optionnel)",
    StringKey.AUTH_DATE_OF_BIRTH to "Date de naissance (optionnel)",
    StringKey.AUTH_CREATE_ACCOUNT to "Cr\u00e9er un compte",
    StringKey.AUTH_TRY_DEMO to "Essayer \u2014 aucun compte requis",
    StringKey.DEMO_BANNER to "D\u00e9mo en lecture seule \u2014 vos modifications ne seront pas enregistr\u00e9es",
    StringKey.AUTH_SIGN_IN to "Se connecter",
    StringKey.AUTH_FORGOT_PASSWORD to "Mot de passe oubli\u00e9?",
    StringKey.AUTH_ALREADY_HAVE_ACCOUNT to "Vous avez d\u00e9j\u00e0 un compte?",
    StringKey.AUTH_NO_ACCOUNT to "Pas encore de compte?",
    StringKey.AUTH_CREATING_ACCOUNT to "Cr\u00e9ation du compte...",
    StringKey.AUTH_SIGNING_IN to "Connexion en cours...",
    StringKey.AUTH_PASSWORD_REQUIREMENTS to "Exigences du mot de passe",
    StringKey.AUTH_PASSWORD_MIN_LENGTH to "Au moins 8 caract\u00e8res",
    StringKey.AUTH_PASSWORD_UPPERCASE to "Une lettre majuscule",
    StringKey.AUTH_PASSWORD_LOWERCASE to "Une lettre minuscule",
    StringKey.AUTH_PASSWORD_NUMBER to "Un chiffre",
    StringKey.AUTH_PASSWORDS_MATCH to "Les mots de passe correspondent",
    StringKey.AUTH_PASSWORDS_DONT_MATCH to "Les mots de passe ne correspondent pas",
    StringKey.AUTH_MFA_TITLE to "Authentification \u00e0 deux facteurs",
    StringKey.AUTH_MFA_DESCRIPTION to "Entrez le code \u00e0 6 chiffres de votre application d'authentification",
    StringKey.AUTH_MFA_CODE to "Code de v\u00e9rification",
    StringKey.AUTH_MFA_VERIFY to "V\u00e9rifier",
    StringKey.AUTH_MFA_VERIFYING to "V\u00e9rification...",
    StringKey.AUTH_CHECK_EMAIL_TITLE to "V\u00e9rifiez votre courriel",
    StringKey.AUTH_CHECK_EMAIL_SENT to "Nous avons envoy\u00e9 un lien de v\u00e9rification \u00e0",
    StringKey.AUTH_CHECK_EMAIL_INSTRUCTIONS to "Cliquez sur le lien dans le courriel pour v\u00e9rifier votre compte et commencer.",
    StringKey.AUTH_CHECK_EMAIL_GO_TO_LOGIN to "Aller \u00e0 la connexion",
    StringKey.AUTH_CHECK_EMAIL_RESEND to "Renvoyer le courriel",
    StringKey.AUTH_CHECK_EMAIL_SENT_SUCCESS to "Courriel envoy\u00e9 !",

    // Landing page
    StringKey.LANDING_HERO_TITLE to "Un seul chiffre.",
    StringKey.LANDING_HERO_SUBTITLE to "Votre taux d'\u00e9pargne est le chiffre le plus puissant en finances personnelles. wiz\u2261r vous aide \u00e0 le voir, le comprendre et l'am\u00e9liorer\u2014jusqu'\u00e0 ce que l'\u00e9pargne devienne automatique.",
    StringKey.LANDING_HERO_SUBTITLE_SHORT to "Oubliez les budgets compliqu\u00e9s. Concentrez-vous sur une seule m\u00e9trique. Regardez votre patrimoine grandir.",
    StringKey.LANDING_SAVINGS_RATE to "taux d'\u00e9pargne",
    StringKey.LANDING_ONE_NUMBER to "Un seul chiffre.",
    StringKey.LANDING_INFINITE_POSSIBILITIES to "Tout y revient.",
    StringKey.LANDING_GET_STARTED to "Commencer",
    StringKey.LANDING_SEE_HOW_IT_WORKS to "Voir comment \u00e7a fonctionne",
    StringKey.LANDING_DISCOVER_YOUR_NUMBER to "D\u00e9couvrez votre chiffre",
    StringKey.LANDING_FIND_MY_SAVINGS_RATE to "Trouver mon taux d'\u00e9pargne",
    StringKey.LANDING_BANK_LEVEL_SECURITY to "S\u00e9curit\u00e9 de niveau bancaire",
    StringKey.LANDING_INSTITUTIONS_COUNT to "12 000+ institutions",
    StringKey.LANDING_NO_CREDIT_CARD to "Aucune carte de cr\u00e9dit requise",
    StringKey.LANDING_WHY_ONE_NUMBER to "Pourquoi un seul chiffre change tout",
    StringKey.LANDING_WHY_ONE_NUMBER_DESC to "La plupart des applis de finances vous noient dans les donn\u00e9es\u2014des dizaines de cat\u00e9gories de budget, des graphiques \u00e0 l'infini, des tableaux de bord \u00e9crasants. wiz\u2261r adopte une approche diff\u00e9rente. Une approche plus <em style=\"font-style: italic;\">sage</em>. Concentrez-vous sur un seul chiffre, et tout le reste se met en place.",
    StringKey.LANDING_SIMPLE_MATH to "Approche simple, r\u00e9sultats puissants",
    StringKey.LANDING_SIMPLE_MATH_DESC to "Votre taux d'\u00e9pargne ne progresse que lorsque vous assignez de l'argent \u00e0 vos objectifs. Pas de bruit des d\u00e9penses quotidiennes\u2014juste des progr\u00e8s intentionnels que vous contr\u00f4lez.",
    StringKey.LANDING_PSYCHOLOGY_TITLE to "La psychologie d'une seule m\u00e9trique",
    StringKey.LANDING_PSYCHOLOGY_DESC to "La recherche montre que nous sommes mauvais pour suivre plusieurs m\u00e9triques. Mais donnez \u00e0 quelqu'un UN seul chiffre \u00e0 am\u00e9liorer? C'est l\u00e0 que les habitudes se forment. C'est l\u00e0 que le changement persiste.",
    StringKey.LANDING_PSYCHOLOGY_STAT to "3x plus susceptible d'atteindre ses objectifs",
    StringKey.LANDING_SMALL_CHANGES to "Petits changements, impact massif",
    StringKey.LANDING_SMALL_CHANGES_DESC to "Am\u00e9liorer votre taux d'\u00e9pargne de seulement 1% par mois semble peu. Mais sur 10 ans? C'est la diff\u00e9rence entre lutter et la libert\u00e9 financi\u00e8re.",
    StringKey.LANDING_EVERYTHING_YOU_NEED to "Tout ce dont vous avez besoin pour am\u00e9liorer votre chiffre",
    StringKey.LANDING_SEE_REAL_NUMBER to "Voyez votre vrai chiffre",
    StringKey.LANDING_SEE_REAL_NUMBER_DESC to "Connectez vos comptes et voyez instantan\u00e9ment votre v\u00e9ritable taux d'\u00e9pargne\u2014pas une estimation, pas une approximation, mais votre chiffre r\u00e9el calcul\u00e9 \u00e0 partir de vraies transactions.",
    StringKey.LANDING_UPDATED_REALTIME to "Mis \u00e0 jour en temps r\u00e9el",
    StringKey.LANDING_UNDERSTAND_WHAT_MOVES to "Comprenez ce qui l'influence",
    StringKey.LANDING_UNDERSTAND_DESC to "Notre IA cat\u00e9gorise chaque transaction et vous montre exactement quelles habitudes de d\u00e9penses affectent votre pourcentage. Sans jugement\u2014juste de la clart\u00e9.",
    StringKey.LANDING_AI_POWERED to "Analyses propuls\u00e9es par l'IA",
    StringKey.LANDING_IMPROVE_AUTOMATICALLY to "Am\u00e9liorez-le automatiquement",
    StringKey.LANDING_IMPROVE_DESC to "Recevez des conseils quotidiens adapt\u00e9s \u00e0 VOS habitudes de d\u00e9penses. Des astuces simples et r\u00e9alisables qui s'accumulent en grandes am\u00e9liorations.",
    StringKey.LANDING_PERSONALIZED to "Recommandations personnalis\u00e9es",
    StringKey.LANDING_BUILDING_HABITS to "Construire des habitudes durables",
    StringKey.LANDING_BUILDING_HABITS_DESC to "Conna\u00eetre votre chiffre est la premi\u00e8re \u00e9tape. L'am\u00e9liorer n\u00e9cessite de construire de nouvelles habitudes. C'est l\u00e0 qu'interviennent nos Cartes Conseils\u2014des informations personnalis\u00e9es, faciles \u00e0 dig\u00e9rer, livr\u00e9es au moment parfait.",
    StringKey.LANDING_ADVICE_FOOTER to "Ce ne sont pas des conseils g\u00e9n\u00e9riques\u2014ce sont des informations g\u00e9n\u00e9r\u00e9es \u00e0 partir de VOS d\u00e9penses r\u00e9elles.",
    StringKey.LANDING_PRICING_TITLE to "Tarification simple et transparente",
    StringKey.LANDING_PRICING_SUBTITLE to "Deux forfaits, chacun avec essai gratuit de 14 jours. Choisissez celui qui vous convient.",
    StringKey.LANDING_PRICING_CALCULATOR to "",
    StringKey.LANDING_WHATS_YOUR_NUMBER to "Quel est votre chiffre?",
    StringKey.LANDING_WHATS_YOUR_NUMBER_DESC to "En ce moment, vous \u00e9pargnez un certain pourcentage de vos revenus. Vous pensez peut-\u00eatre le conna\u00eetre\u2014mais la plupart des gens sont surpris quand ils voient le vrai chiffre.",
    StringKey.LANDING_WHATS_YOUR_NUMBER_SUBTEXT to "D\u00e9couvrez le v\u00f4tre en moins de 2 minutes. Aucune carte de cr\u00e9dit requise.",
    StringKey.LANDING_DATA_SAFE to "Vos donn\u00e9es sont prot\u00e9g\u00e9es. Votre chiffre est priv\u00e9.",
    StringKey.LANDING_PRIVACY_TITLE to "Vos donn\u00e9es sont prot\u00e9g\u00e9es. Votre chiffre est priv\u00e9.",
    StringKey.LANDING_MY_DASHBOARD to "Mon compte",

    // Pricing
    StringKey.PRICING_MONTHLY to "Mensuel",
    StringKey.PRICING_YEARLY to "Annuel",
    StringKey.PRICING_SAVE_14 to "Économisez 14%",
    StringKey.PRICING_EVERYTHING_YOU_NEED to "Tout ce dont vous avez besoin",
    StringKey.PRICING_START_FREE_TRIAL to "Commencer l'essai gratuit de 14 jours",
    StringKey.PRICING_CANCEL_ANYTIME to "Annulez à tout moment. Sans engagement.",
    StringKey.PRICING_FULL_HISTORY to "Historique complet des transactions",
    StringKey.PRICING_CROSS_PLATFORM to "Synchronisation multi-plateforme (iOS, Android, Web)",
    StringKey.PRICING_CATEGORY_CUSTOMIZATION to "Vérification et personnalisation des catégories",
    StringKey.PRICING_UNLIMITED_ACCOUNTS to "Connexions de comptes bancaires illimitées",
    StringKey.PRICING_REALTIME_TRACKING to "Suivi du taux d'épargne en temps réel",
    StringKey.PRICING_AI_ADVICE_CARDS to "Conseils de dépenses propulsés par l'IA",
    StringKey.PRICING_UNLIMITED_GOALS to "Objectifs d'épargne illimités",
    StringKey.PRICING_GOAL_OPTIMIZATION to "Optimisation du portefeuille d'objectifs",

    // Pro Tier
    StringKey.TIER_STANDARD to "Standard",
    StringKey.TIER_PRO to "Pro",
    StringKey.TIER_STANDARD_SUBTITLE to "Tout ce qu'il faut pour suivre vos finances",
    StringKey.TIER_PRO_SUBTITLE to "Outils avancés pour les budgets sérieux",
    StringKey.PRICING_PRO_MONTHLY to "12,99 $",
    StringKey.PRICING_PRO_YEARLY to "132 $",
    StringKey.PRICING_PRO_YEARLY_QUARTERLY_NOTE to "Facturé trimestriellement à 33 $/trimestre",
    StringKey.PRICING_SAVE_15 to "Économisez 15 %",
    StringKey.PRICING_BEST_VALUE to "Meilleure valeur",
    StringKey.PRICING_EVERYTHING_IN_STANDARD to "Tout ce qui est dans Standard",
    StringKey.PRICING_BATCH_TRANSACTION_ACTIONS to "Actions par lot sur les transactions",
    StringKey.PRICING_MULTI_SELECT_BULK_EDIT to "Sélection multiple et modification en lot",
    StringKey.PRICING_MULTI_SELECT_AND_BATCH to "Sélection multiple et actions par lot",
    StringKey.PRICING_TRANSACTION_NOTIFICATIONS to "Notifications de transactions (courriel et SMS)",
    StringKey.PRICING_SPLIT_TRANSACTIONS to "Fractionnement de transactions",
    StringKey.PRICING_DISTRIBUTE_TRANSACTIONS to "Distribution et d\u00e9placement de transactions",
    StringKey.PRICING_CUSTOMIZABLE_DASHBOARD to "Tableau de bord personnalisable",
    StringKey.PRICING_CONTEXT_FILTERS to "Filtrer par contextes (Vacances, Activit\u00e9 secondaire...)",
    StringKey.PRICING_MORE_PRO_COMING_SOON to "Plus de fonctionnalit\u00e9s Pro \u00e0 venir",
    StringKey.PRICING_UPGRADE_TO_PRO to "Passer à Pro",
    StringKey.PRICING_UPGRADE_NOW to "Passer à Pro",
    StringKey.PRICING_UPGRADE_SUBTITLE to "Débloquez les fonctionnalités Pro pour votre compte",
    StringKey.PRO_UPGRADE_PROMPT_TITLE to "Fonctionnalité Pro",
    StringKey.PRO_UPGRADE_PROMPT_MESSAGE to "Ceci est une fonctionnalité Pro. Passez à Pro pour débloquer les actions par lot et plus encore.",
    StringKey.PRO_UPGRADE_PROMPT_CTA to "Passer à Pro",

    // Dashboard
    StringKey.DASHBOARD_TITLE to "Tableau de bord",
    StringKey.DASHBOARD_SAVINGS_RATE to "Taux d'\u00e9pargne",
    StringKey.DASHBOARD_THIS_MONTH to "Ce mois-ci",
    StringKey.DASHBOARD_LAST_MONTH to "Le mois dernier",
    StringKey.DASHBOARD_INCOME to "Revenus",
    StringKey.DASHBOARD_EXPENSES to "D\u00e9penses",
    StringKey.DASHBOARD_SAVINGS to "Épargne",
    StringKey.DASHBOARD_BUDGET to "Budget",
    StringKey.DASHBOARD_MONTHLY_SUMMARY to "R\u00e9sum\u00e9 mensuel",
    StringKey.DASHBOARD_SPENDING_BY_CATEGORY to "D\u00e9penses par cat\u00e9gorie",
    StringKey.DASHBOARD_TRENDS to "Tendances",
    StringKey.DASHBOARD_PROJECTIONS to "Projections",
    StringKey.DASHBOARD_NO_DATA to "Aucune donn\u00e9e disponible",
    StringKey.DASHBOARD_CHART_VIEW to "Vue graphique",
    StringKey.DASHBOARD_CATEGORY_SPENDING to "D\u00e9penses par cat\u00e9gorie",
    StringKey.DASHBOARD_EXPENSE_DISTRIBUTION to "R\u00e9partition des d\u00e9penses",
    StringKey.DASHBOARD_EXPENSE_TIMELINE to "Chronologie des d\u00e9penses",
    StringKey.DASHBOARD_SAVINGS_TREND to "Tendance du taux d'\u00e9pargne",
    StringKey.DASHBOARD_SAVINGS_FOR_MONTH to "\u00e9pargne pour %s",
    StringKey.DASHBOARD_SAVINGS_SMOOTHED_INFO to "Bas\u00e9 sur votre revenu mensuel habituel, liss\u00e9 sur l'ann\u00e9e \u2014 pour qu'un mois avec une paie de plus ne gonfle pas le taux.",
    StringKey.DASHBOARD_SAVINGS_RECOMMENDATION to "Recommandation d'\u00e9pargne",
    StringKey.DASHBOARD_AVAILABLE_TO_SAVE to "Vous avez %s disponible \u00e0 \u00e9pargner en %s.",
    StringKey.DASHBOARD_RECOMMENDED_ALLOCATION to "R\u00e9partition recommand\u00e9e:",
    StringKey.DASHBOARD_MONTHS_LEFT to "%s mois restants",
    StringKey.DASHBOARD_YTD_AVG_SAVINGS to "YTD\u00a0: %s%\u00a0d\u2019\u00e9pargne moy.",
    StringKey.DASHBOARD_TARGET to "Cible",
    StringKey.DASHBOARD_ACTUAL_SAVINGS to "Taux d'\u00e9pargne r\u00e9el",
    StringKey.DASHBOARD_ACCOUNTS to "Comptes",
    StringKey.DASHBOARD_SELECTED_COUNT to "%s/%s s\u00e9lectionn\u00e9s",
    StringKey.DASHBOARD_COMPARISON to "Comparaison",
    StringKey.DASHBOARD_NEEDS_WANTS_FLOW to "Flux besoins et envies",
    StringKey.DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR to "\u00e9pargne moyenne pour %s",
    StringKey.DASHBOARD_SAVINGS_CURRENT_MONTH to "\u00e9pargne du mois courant",
    StringKey.DASHBOARD_MONTH_IN_PROGRESS to "en cours",
    StringKey.DASHBOARD_INCOME_EXPENSES_SAVINGS to "Revenus / D\u00e9penses / \u00c9pargne",
    StringKey.DASHBOARD_INCOME_EXPENSES to "Revenus / D\u00e9penses",
    StringKey.DASHBOARD_AVAILABLE_THIS_MONTH to "Vous avez %s disponible en %s.",
    StringKey.DASHBOARD_OVER_BUDGET_THIS_MONTH to "Vous d\u00e9passez le budget de %s en %s.",
    StringKey.DASHBOARD_OVER_BUDGET_REDUCE to "Vous d\u00e9passez le budget de %s ce mois-ci. Concentrez-vous sur la r\u00e9duction des d\u00e9penses avant d'allouer aux objectifs.",
    StringKey.DASHBOARD_SAVINGS_BY_MONTH to "Taux d'épargne par mois",
    StringKey.DASHBOARD_LINK_ACCOUNTS_SAVINGS to "Liez vos comptes pour suivre l'\u00e9pargne disponible.",
    StringKey.DASHBOARD_NUDGE_SPEND_LESS to "Vous d\u00e9pensez \u00e0 peu pr\u00e8s tout ce que vous gagnez en %s \u2014 il ne reste rien pour vos objectifs. M\u00eame couper une petite tranche (50 $, un abonnement inutilis\u00e9) suffit \u00e0 d\u00e9marrer une s\u00e9rie d'\u00e9pargne.",
    StringKey.DASHBOARD_VIEW_BUDGET to "Voir mon budget \u2192",
    StringKey.DASHBOARD_NO_MONTH_DATA to "Aucune transaction en %s, il n'y a donc rien sur quoi baser une recommandation.",
    StringKey.DASHBOARD_EARLY_MONTH_HINT to "Votre mois vient tout juste de commencer \u2014 les recommandations appara\u00eetront ici d\u00e8s vos premi\u00e8res transactions et revenus.",
    StringKey.DASHBOARD_EXPENSES_BEFORE_INCOME to "Vos paiements automatiques ont commenc\u00e9 \u00e0 passer mais votre revenu n'est pas encore arriv\u00e9 \u2014 nous vous sugg\u00e9rerons comment le r\u00e9partir entre vos objectifs d\u00e8s l'arriv\u00e9e de votre prochaine paie.",
    StringKey.DASHBOARD_CREATE_GOAL to "Cr\u00e9er un objectif \u2192",
    StringKey.DASHBOARD_GOAL_SUGGESTION to "Pensez \u00e0 cr\u00e9er des objectifs d'\u00e9pargne pour b\u00e2tir votre s\u00e9curit\u00e9 financi\u00e8re :",
    StringKey.DASHBOARD_EMERGENCY_FUND to "Fonds d'urgence",
    StringKey.DASHBOARD_VACATION to "Vacances",
    StringKey.DASHBOARD_HOME_DOWN_PAYMENT to "Mise de fonds",
    StringKey.DASHBOARD_RETIREMENT to "Retraite",
    StringKey.DASHBOARD_EMERGENCY_FUND_DESC to "3 \u00e0 6 mois de d\u00e9penses pour les impr\u00e9vus",
    StringKey.DASHBOARD_VACATION_DESC to "Commencez \u00e0 planifier votre prochaine escapade",
    StringKey.DASHBOARD_HOME_DOWN_PAYMENT_DESC to "\u00c9pargnez pour devenir propri\u00e9taire",
    StringKey.DASHBOARD_RETIREMENT_DESC to "Il n'est jamais trop t\u00f4t pour commencer",
    StringKey.DASHBOARD_DEMO_BADGE to "D\u00e9mo",
    StringKey.DASHBOARD_DEMO_CONNECT_TITLE to "Connectez votre premier compte bancaire",
    StringKey.DASHBOARD_DEMO_CONNECT_DESC to "Vous pouvez consulter des donn\u00e9es de d\u00e9mo dans le Tableau de bord. Liez votre banque pour voir vos vraies finances.",
    StringKey.DASHBOARD_DEMO_CONNECT_CTA to "Connecter un compte",
    StringKey.ACTIVITY_DEMO_MESSAGE to "Vous pouvez consulter des donn\u00e9es de d\u00e9mo dans le Tableau de bord. Liez votre banque pour voir vos vraies finances.",

    // Transactions
    StringKey.TRANSACTIONS_TITLE to "Transactions",
    StringKey.TRANSACTIONS_SEARCH to "Rechercher des transactions...",
    StringKey.TRANSACTIONS_SHOW_PENDING_HOLDS to "Afficher les retenues en attente",
    StringKey.TRANSACTIONS_ALL to "Toutes",
    StringKey.TRANSACTIONS_INCOME_ONLY to "Revenus",
    StringKey.TRANSACTIONS_EXPENSES_ONLY to "D\u00e9penses",
    StringKey.TRANSACTIONS_TODAY to "Aujourd'hui",
    StringKey.TRANSACTIONS_YESTERDAY to "Hier",
    StringKey.TRANSACTIONS_THIS_WEEK to "Cette semaine",
    StringKey.TRANSACTIONS_THIS_MONTH to "Ce mois-ci",
    StringKey.TRANSACTIONS_OLDER to "Plus anciennes",
    StringKey.TRANSACTIONS_NO_TRANSACTIONS to "Aucune transaction trouv\u00e9e",
    StringKey.TRANSACTIONS_LOADING to "Chargement des transactions...",
    StringKey.TRANSACTIONS_REFRESH to "Actualiser",
    StringKey.TRANSACTIONS_CATEGORY to "Cat\u00e9gorie",
    StringKey.TRANSACTIONS_AMOUNT to "Montant",
    StringKey.TRANSACTIONS_DATE to "Date",
    StringKey.TRANSACTIONS_PENDING to "En attente",
    StringKey.TRANSACTIONS_SPLIT to "Divisé",
    StringKey.TRANSACTIONS_DIST to "Réparti",
    StringKey.TRANSACTIONS_UNSPLIT to "Annuler la division",
    StringKey.TRANSACTIONS_EDIT to "Modifier",
    StringKey.TRANSACTIONS_DETAILS to "D\u00e9tails de la transaction",

    // Goals
    StringKey.GOALS_TITLE to "Objectifs d'\u00e9pargne",
    StringKey.GOALS_ADD_NEW to "Ajouter un objectif",
    StringKey.GOALS_TARGET to "Cible",
    StringKey.GOALS_CURRENT to "Actuel",
    StringKey.GOALS_PROGRESS to "Progr\u00e8s",
    StringKey.GOALS_DEADLINE to "\u00c9ch\u00e9ance",
    StringKey.GOALS_MONTHLY_CONTRIBUTION to "Contribution mensuelle",
    StringKey.GOALS_ON_TRACK to "En bonne voie",
    StringKey.GOALS_BEHIND to "En retard",
    StringKey.GOALS_AHEAD to "En avance",
    StringKey.GOALS_COMPLETE to "Termin\u00e9",
    StringKey.GOALS_DELETE to "Supprimer l'objectif",
    StringKey.GOALS_EDIT to "Modifier l'objectif",
    StringKey.GOALS_SAVE to "Enregistrer",
    StringKey.GOALS_CANCEL to "Annuler",
    StringKey.GOALS_NAME to "Nom de l'objectif",
    StringKey.GOALS_DESCRIPTION to "Description",
    StringKey.GOALS_NO_GOALS to "Aucun objectif encore",
    StringKey.GOALS_CREATE_FIRST to "Cr\u00e9ez votre premier objectif d'\u00e9pargne",
    StringKey.GOALS_YOUR_GOALS to "Vos objectifs",
    StringKey.GOALS_PERCENT_COMPLETE to "%s% termin\u00e9",
    StringKey.GOALS_DAYS_LEFT to "%s jours restants",

    // Budget
    StringKey.BUDGET_TITLE to "Planification budgétaire",
    StringKey.BUDGET_MONTHLY_BUDGET to "Budget mensuel",
    StringKey.BUDGET_SET_BUDGET to "D\u00e9finir le budget",
    StringKey.BUDGET_REMAINING to "Restant",
    StringKey.BUDGET_SPENT to "D\u00e9pens\u00e9",
    StringKey.BUDGET_OVER_BUDGET to "D\u00e9passement",
    StringKey.BUDGET_UNDER_BUDGET to "Sous le budget",
    StringKey.BUDGET_NO_BUDGET to "Aucun budget d\u00e9fini",
    StringKey.BUDGET_CREATE_PLAN to "Créer un plan budgétaire",
    StringKey.BUDGET_EXPORT_CSV to "Exporter CSV",
    StringKey.BUDGET_TOTAL_INCOME to "REVENU TOTAL",
    StringKey.BUDGET_TOTAL_EXPENSES to "DÉPENSES TOTALES",
    StringKey.BUDGET_NET_SAVINGS to "ÉPARGNE NETTE",
    StringKey.BUDGET_SAVINGS_PERCENT to "ÉPARGNE %",

    // Settings
    StringKey.SETTINGS_TITLE to "Param\u00e8tres",
    StringKey.SETTINGS_ACCOUNT to "Param\u00e8tres du compte",
    StringKey.SETTINGS_SAVINGS_TARGET to "Objectif d'\u00e9pargne",
    StringKey.SETTINGS_NOTIFICATIONS to "Notifications",
    StringKey.SETTINGS_PRIVACY to "Confidentialit\u00e9",
    StringKey.SETTINGS_SECURITY to "S\u00e9curit\u00e9",
    StringKey.SETTINGS_LANGUAGE to "Langue",
    StringKey.SETTINGS_LANGUAGE_ENGLISH to "English",
    StringKey.SETTINGS_LANGUAGE_FRENCH to "Fran\u00e7ais",
    StringKey.SETTINGS_COUNTRY to "Pays / R\u00e9gion",
    StringKey.SETTINGS_APPEARANCE to "Apparence",
    StringKey.SETTINGS_THEME_SYSTEM to "Syst\u00e8me",
    StringKey.SETTINGS_THEME_DARK to "Sombre",
    StringKey.SETTINGS_THEME_LIGHT to "Clair",
    StringKey.SETTINGS_MFA to "Authentification \u00e0 deux facteurs",
    StringKey.SETTINGS_MFA_ENABLE to "Activer l'A2F",
    StringKey.SETTINGS_MFA_DISABLE to "D\u00e9sactiver l'A2F",
    StringKey.SETTINGS_MFA_SETUP to "Configurer l'A2F",
    StringKey.SETTINGS_LINKED_ACCOUNTS to "Comptes li\u00e9s",
    StringKey.SETTINGS_ADD_ACCOUNT to "Ajouter un compte",
    StringKey.SETTINGS_REMOVE_ACCOUNT to "Supprimer le compte",
    StringKey.SETTINGS_DATA_EXPORT to "Exporter les donn\u00e9es",
    StringKey.SETTINGS_DELETE_ACCOUNT to "Supprimer le compte",
    StringKey.SETTINGS_ABOUT to "\u00c0 propos",
    StringKey.SETTINGS_VERSION to "Version",
    StringKey.SETTINGS_TERMS to "Conditions d'utilisation",
    StringKey.SETTINGS_PRIVACY_POLICY to "Politique de confidentialit\u00e9",

    // Account linking
    StringKey.ACCOUNT_LINKING_TITLE to "Connectez votre compte bancaire",
    StringKey.ACCOUNT_LINKING_DESCRIPTION to "Connectez votre compte bancaire en toute s\u00e9curit\u00e9 pour commencer \u00e0 suivre vos transactions et g\u00e9rer vos finances.",
    StringKey.ACCOUNT_LINKING_CONNECT to "Connecter le compte",
    StringKey.ACCOUNT_LINKING_CONNECTING to "Connexion...",
    StringKey.ACCOUNT_LINKING_FETCHING_HISTORY to "R\u00e9cup\u00e9ration de votre historique de transactions",
    StringKey.ACCOUNT_LINKING_SECURITY_ENCRYPTION to "Chiffrement de niveau bancaire",
    StringKey.ACCOUNT_LINKING_SECURITY_NO_CREDENTIALS to "Nous ne stockons jamais vos identifiants",
    StringKey.ACCOUNT_LINKING_SECURITY_READ_ONLY to "Acc\u00e8s en lecture seule aux transactions",
    StringKey.ACCOUNT_LINKING_SECURITY_PLAID to "Propuls\u00e9 par Plaid",
    StringKey.ACCOUNT_LINKING_PRIVACY_NOTICE to "En connectant votre compte, vous acceptez la politique de confidentialit\u00e9 de Plaid",
    StringKey.ACCOUNT_LINKING_COMPLETE to "termin\u00e9",
    StringKey.ACCOUNT_LINKING_PROGRESS to "Nous t\u00e9l\u00e9chargeons votre historique de transactions depuis votre banque. Cela peut prendre quelques minutes.",
    StringKey.ACCOUNT_LINKING_SKIP to "Passer pour l\u2019instant",
    StringKey.ACCOUNT_LINKING_DOWNLOADING to "Nous téléchargeons %s d'historique de transactions de votre banque. Cela peut prendre quelques minutes.",
    StringKey.ACCOUNT_LINKING_PERCENT_COMPLETE to "%s%% complété",
    StringKey.ACCOUNT_LINKING_CONNECTION_FAILED to "Échec de la connexion",

    // Time periods
    StringKey.PERIOD_DAY to "Jour",
    StringKey.PERIOD_WEEK to "Semaine",
    StringKey.PERIOD_MONTH to "Mois",
    StringKey.PERIOD_QUARTER to "Trimestre",
    StringKey.PERIOD_YEAR to "Ann\u00e9e",
    StringKey.PERIOD_ALL_TIME to "Tout le temps",
    StringKey.PERIOD_3_MONTHS to "3 mois",
    StringKey.PERIOD_6_MONTHS to "6 mois",
    StringKey.PERIOD_12_MONTHS to "12 mois",
    StringKey.PERIOD_24_MONTHS to "24 mois",

    // Month names
    StringKey.MONTH_JAN to "Jan",
    StringKey.MONTH_FEB to "F\u00e9v",
    StringKey.MONTH_MAR to "Mar",
    StringKey.MONTH_APR to "Avr",
    StringKey.MONTH_MAY to "Mai",
    StringKey.MONTH_JUN to "Juin",
    StringKey.MONTH_JUL to "Juil",
    StringKey.MONTH_AUG to "Ao\u00fbt",
    StringKey.MONTH_SEP to "Sep",
    StringKey.MONTH_OCT to "Oct",
    StringKey.MONTH_NOV to "Nov",
    StringKey.MONTH_DEC to "D\u00e9c",
    StringKey.MONTH_JANUARY to "Janvier",
    StringKey.MONTH_FEBRUARY to "F\u00e9vrier",
    StringKey.MONTH_MARCH to "Mars",
    StringKey.MONTH_APRIL to "Avril",
    StringKey.MONTH_MAY_FULL to "Mai",
    StringKey.MONTH_JUNE to "Juin",
    StringKey.MONTH_JULY to "Juillet",
    StringKey.MONTH_AUGUST to "Ao\u00fbt",
    StringKey.MONTH_SEPTEMBER to "Septembre",
    StringKey.MONTH_OCTOBER to "Octobre",
    StringKey.MONTH_NOVEMBER to "Novembre",
    StringKey.MONTH_DECEMBER to "D\u00e9cembre",

    // Common actions
    StringKey.ACTION_SAVE to "Enregistrer",
    StringKey.ACTION_CANCEL to "Annuler",
    StringKey.ACTION_DELETE to "Supprimer",
    StringKey.ACTION_EDIT to "Modifier",
    StringKey.ACTION_CLOSE to "Fermer",
    StringKey.ACTION_CONFIRM to "Confirmer",
    StringKey.ACTION_SUBMIT to "Soumettre",
    StringKey.ACTION_RETRY to "R\u00e9essayer",
    StringKey.ACTION_REFRESH to "Actualiser",
    StringKey.ACTION_SEARCH to "Rechercher",
    StringKey.ACTION_FILTER to "Filtrer",
    StringKey.ACTION_SORT to "Trier",
    StringKey.ACTION_CLEAR to "Effacer",
    StringKey.ACTION_CONTINUE to "Continuer",
    StringKey.ACTION_BACK to "Retour",
    StringKey.ACTION_NEXT to "Suivant",
    StringKey.ACTION_PREVIOUS to "Pr\u00e9c\u00e9dent",
    StringKey.ACTION_DONE to "Termin\u00e9",

    // Common labels
    StringKey.LABEL_LOADING to "Chargement...",
    StringKey.LABEL_ERROR to "Erreur",
    StringKey.LABEL_SUCCESS to "Succ\u00e8s",
    StringKey.LABEL_WARNING to "Avertissement",
    StringKey.LABEL_INFO to "Info",
    StringKey.LABEL_YES to "Oui",
    StringKey.LABEL_NO to "Non",
    StringKey.LABEL_OK to "OK",
    StringKey.LABEL_OPTIONAL to "optionnel",
    StringKey.LABEL_REQUIRED to "requis",
    StringKey.LABEL_TOTAL to "Total",
    StringKey.LABEL_AVERAGE to "Moyenne",
    StringKey.LABEL_MONTHLY to "Mensuel",
    StringKey.LABEL_WEEKLY to "Hebdomadaire",
    StringKey.LABEL_DAILY to "Quotidien",
    StringKey.LABEL_YEARLY to "Annuel",

    // Error messages
    StringKey.ERROR_GENERIC to "Une erreur s'est produite. Veuillez r\u00e9essayer.",
    StringKey.ERROR_NETWORK to "Erreur r\u00e9seau. V\u00e9rifiez votre connexion.",
    StringKey.ERROR_AUTH_FAILED to "\u00c9chec de l'authentification. Veuillez r\u00e9essayer.",
    StringKey.ERROR_INVALID_CREDENTIALS to "Adresse courriel ou mot de passe incorrect.",
    StringKey.ERROR_INVALID_EMAIL to "Veuillez entrer une adresse courriel valide",
    StringKey.ERROR_INVALID_PASSWORD to "Le mot de passe doit contenir au moins 8 caract\u00e8res avec une majuscule, une minuscule et un chiffre",
    StringKey.ERROR_PASSWORDS_DONT_MATCH to "Les mots de passe ne correspondent pas",
    StringKey.ERROR_REQUIRED_FIELD to "Ce champ est requis",
    StringKey.ERROR_CONNECTION_FAILED to "\u00c9chec de la connexion. Veuillez r\u00e9essayer.",
    StringKey.ERROR_SYNC_FAILED to "\u00c9chec de la synchronisation. Veuillez r\u00e9essayer.",
    StringKey.ERROR_LOAD_FAILED to "\u00c9chec du chargement des donn\u00e9es. Veuillez r\u00e9essayer.",

    // Offline
    StringKey.OFFLINE_BANNER to "Vous \u00eates hors ligne. Les donn\u00e9es ne peuvent \u00eatre mises \u00e0 jour.",
    StringKey.OFFLINE_EDIT_BLOCKED to "Impossible d'enregistrer les modifications hors ligne.",

    // Success messages
    StringKey.SUCCESS_SAVED to "Enregistr\u00e9 avec succ\u00e8s",
    StringKey.SUCCESS_DELETED to "Supprim\u00e9 avec succ\u00e8s",
    StringKey.SUCCESS_UPDATED to "Mis \u00e0 jour avec succ\u00e8s",
    StringKey.SUCCESS_CONNECTED to "Connect\u00e9 avec succ\u00e8s",
    StringKey.SUCCESS_LOGGED_IN to "Connexion r\u00e9ussie",
    StringKey.SUCCESS_REGISTERED to "Compte cr\u00e9\u00e9 avec succ\u00e8s",

    // Footer
    StringKey.FOOTER_COPYRIGHT to "\u00a9 2025 wiz\u2261r Inc. Tous droits r\u00e9serv\u00e9s.",
    StringKey.FOOTER_MADE_WITH_LOVE to "Fait avec \u2665 pour votre avenir financier",
    StringKey.FOOTER_PRODUCT to "Produit",
    StringKey.FOOTER_COMPANY to "Entreprise",
    StringKey.FOOTER_LEGAL to "L\u00e9gal",
    StringKey.FOOTER_CONNECT to "Contact",
    StringKey.FOOTER_FEATURES to "Fonctionnalit\u00e9s",
    StringKey.FOOTER_PRICING to "Tarifs",
    StringKey.FOOTER_SECURITY to "S\u00e9curit\u00e9",
    StringKey.FOOTER_FAQ to "FAQ",
    StringKey.FOOTER_ABOUT to "\u00c0 propos",
    StringKey.FOOTER_BLOG to "Blogue",
    StringKey.FOOTER_CAREERS to "Carri\u00e8res",
    StringKey.FOOTER_CONTACT to "Nous contacter",
    StringKey.FOOTER_TERMS to "Conditions",
    StringKey.FOOTER_PRIVACY to "Confidentialit\u00e9",
    StringKey.FOOTER_COOKIES to "Politique des t\u00e9moins",

    // Advice cards
    StringKey.ADVICE_DINING_ALERT to "Alerte restauration",
    StringKey.ADVICE_NEW_PERSONAL_BEST to "Nouveau record personnel!",
    StringKey.ADVICE_WEEKEND_TIP to "Conseil \u00e9pargne du week-end",
    StringKey.ADVICE_GOAL_UPDATE to "Mise \u00e0 jour de l'objectif",

    // Chart labels
    StringKey.CHART_INCOME to "Revenus",
    StringKey.CHART_EXPENSES to "D\u00e9penses",

    StringKey.CHART_BUDGET to "Budget",
    StringKey.CHART_FORECAST to "Pr\u00e9vision",
    StringKey.CHART_ACTUAL to "R\u00e9el",

    // Formula section
    StringKey.FORMULA_YOUR_SAVINGS_PERCENT to "Votre % d'\u00e9pargne",
    StringKey.FORMULA_INCOME_MINUS_EXPENSES to "\u00c9pargne objectifs",

    // Advice card content
    StringKey.ADVICE_DINING_SPENT to "Vous avez d\u00e9pens\u00e9 %s en restauration ce mois-ci\u2014soit %s de vos revenus.",
    StringKey.ADVICE_DINING_BOOST to "R\u00e9duire ceci de moiti\u00e9 augmenterait votre taux d'\u00e9pargne de %s.",
    StringKey.ADVICE_SEE_DINING to "Voir les transactions restauration",
    StringKey.ADVICE_PERSONAL_BEST_MESSAGE to "Votre argent disponible a atteint %s ce mois-ci\u2014en hausse de %s le mois dernier!",
    StringKey.ADVICE_KEEP_IT_UP to "Continuez et vous aurez %s de plus disponible cette ann\u00e9e.",
    StringKey.ADVICE_SHARE_WIN to "Partagez votre victoire",
    StringKey.ADVICE_WEEKEND_ACCOUNT to "Les week-ends repr\u00e9sentent %s de vos d\u00e9penses discr\u00e9tionnaires.",
    StringKey.ADVICE_WEEKEND_ACTIVITY to "Planifier une activit\u00e9 gratuite ce week-end pourrait vous \u00e9conomiser",
    StringKey.ADVICE_BROWSE_EVENTS to "Parcourir les \u00e9v\u00e9nements gratuits locaux",
    StringKey.ADVICE_VACATION_FUND to "Mise \u00e0 jour du fonds vacances",
    StringKey.ADVICE_VACATION_MESSAGE to "\u00c0 votre taux d'\u00e9pargne actuel de %s, vous atteindrez votre objectif de %s dans %s.",
    StringKey.ADVICE_VACATION_BOOST to "Passez \u00e0 %s et atteignez-le en %s!",
    StringKey.ADVICE_ADJUST_ALLOCATION to "Ajuster l'allocation d'\u00e9pargne",

    // Security badges
    StringKey.SECURITY_BANK_ENCRYPTION to "Encryption de niveau bancaire",
    StringKey.SECURITY_256_BIT to "Chiffrement AES 256 bits",
    StringKey.SECURITY_PLAID_PARTNERSHIP to "Partenariat Plaid",
    StringKey.SECURITY_TRUSTED_APPS to "Approuv\u00e9 par des milliers d'applications",
    StringKey.SECURITY_READ_ONLY to "Acc\u00e8s en lecture seule",
    StringKey.SECURITY_NO_MOVE_MONEY to "Nous ne pouvons jamais d\u00e9placer votre argent",
    StringKey.SECURITY_NO_CREDENTIAL to "Aucun stockage d'identifiants",
    StringKey.SECURITY_LOGIN_STAYS to "Vos identifiants restent avec votre banque",

    // Additional footer
    StringKey.FOOTER_CCPA_NOTICE to "Avis CCPA",
    StringKey.FOOTER_X to "X",
    StringKey.FOOTER_INSTAGRAM to "Instagram",
    StringKey.FOOTER_LINKEDIN to "LinkedIn",
    StringKey.FOOTER_EMAIL to "Courriel",

    // Last updated
    StringKey.LABEL_LAST_UPDATED to "Derni\u00e8re mise \u00e0 jour",

    // Primary Categories
    StringKey.CATEGORY_INCOME to "Revenus",
    StringKey.CATEGORY_TRANSFER_IN to "Virement entrant",
    StringKey.CATEGORY_TRANSFER_OUT to "Virement sortant",
    StringKey.CATEGORY_LOAN_PAYMENTS to "Paiements de pr\u00eats",
    StringKey.CATEGORY_BANK_FEES to "Frais bancaires",
    StringKey.CATEGORY_ENTERTAINMENT to "Divertissement",
    StringKey.CATEGORY_FOOD_AND_DRINK to "Alimentation et boissons",
    StringKey.CATEGORY_GENERAL_MERCHANDISE to "Marchandises g\u00e9n\u00e9rales",
    StringKey.CATEGORY_HOME_IMPROVEMENT to "Am\u00e9lioration de l'habitat",
    StringKey.CATEGORY_MEDICAL to "M\u00e9dical",
    StringKey.CATEGORY_PERSONAL_CARE to "Soins personnels",
    StringKey.CATEGORY_GENERAL_SERVICES to "Services g\u00e9n\u00e9raux",
    StringKey.CATEGORY_GOVERNMENT_AND_NON_PROFIT to "Gouvernement et OBNL",
    StringKey.CATEGORY_TRANSPORTATION to "Transport",
    StringKey.CATEGORY_TRAVEL to "Voyage",
    StringKey.CATEGORY_RENT_AND_UTILITIES to "Loyer et services publics",
    StringKey.CATEGORY_OTHER to "Autre",
    StringKey.CATEGORY_MISCELLANEOUS to "Divers",
    StringKey.CATEGORY_FAMILY to "Famille",
    StringKey.CATEGORY_FAMILY_CHILD_SUPPORT to "Pension alimentaire pour enfants",
    StringKey.CATEGORY_FAMILY_SPOUSAL_SUPPORT to "Pension alimentaire pour conjoint",
    StringKey.CATEGORY_FAMILY_ELDER_CARE to "Soins aux aînés",
    StringKey.CATEGORY_FAMILY_OTHER to "Autre soutien familial",

    // Sub-categories: Income
    StringKey.CATEGORY_INCOME_DIVIDENDS to "Dividendes",
    StringKey.CATEGORY_INCOME_INTEREST_EARNED to "Int\u00e9r\u00eats gagn\u00e9s",
    StringKey.CATEGORY_INCOME_RETIREMENT_PENSION to "Pension de retraite",
    StringKey.CATEGORY_INCOME_TAX_REFUND to "Remboursement d'imp\u00f4t",
    StringKey.CATEGORY_INCOME_UNEMPLOYMENT to "Assurance-ch\u00f4mage",
    StringKey.CATEGORY_INCOME_WAGES to "Salaires",
    StringKey.CATEGORY_INCOME_OTHER to "Autres revenus",

    // Sub-categories: Transfer In
    StringKey.CATEGORY_TRANSFER_IN_CASH_ADVANCES to "Avances de fonds et pr\u00eats",
    StringKey.CATEGORY_TRANSFER_IN_DEPOSIT to "D\u00e9p\u00f4t",
    StringKey.CATEGORY_TRANSFER_IN_INVESTMENT to "Investissement et fonds de retraite",
    StringKey.CATEGORY_TRANSFER_IN_SAVINGS to "\u00c9pargne",
    StringKey.CATEGORY_TRANSFER_IN_ACCOUNT to "Transfert de compte",
    StringKey.CATEGORY_TRANSFER_IN_OTHER to "Autre virement entrant",

    // Sub-categories: Transfer Out
    StringKey.CATEGORY_TRANSFER_OUT_INVESTMENT to "Investissement et fonds de retraite",
    StringKey.CATEGORY_TRANSFER_OUT_SAVINGS to "\u00c9pargne",
    StringKey.CATEGORY_TRANSFER_OUT_WITHDRAWAL to "Retrait",
    StringKey.CATEGORY_TRANSFER_OUT_ACCOUNT to "Transfert de compte",
    StringKey.CATEGORY_TRANSFER_OUT_OTHER to "Autre virement sortant",

    // Sub-categories: Loan Payments
    StringKey.CATEGORY_LOAN_CAR to "Paiement auto",
    StringKey.CATEGORY_LOAN_CREDIT_CARD to "Paiement carte de cr\u00e9dit",
    StringKey.CATEGORY_LOAN_PERSONAL to "Paiement pr\u00eat personnel",
    StringKey.CATEGORY_LOAN_MORTGAGE to "Paiement hypoth\u00e9caire",
    StringKey.CATEGORY_LOAN_STUDENT to "Paiement pr\u00eat \u00e9tudiant",
    StringKey.CATEGORY_LOAN_OTHER to "Autre paiement",

    // Sub-categories: Bank Fees
    StringKey.CATEGORY_BANK_ATM to "Frais de guichet",
    StringKey.CATEGORY_BANK_FOREIGN_TRANSACTION to "Frais de transaction \u00e9trang\u00e8re",
    StringKey.CATEGORY_BANK_INSUFFICIENT_FUNDS to "Fonds insuffisants",
    StringKey.CATEGORY_BANK_INTEREST_CHARGE to "Frais d'int\u00e9r\u00eat",
    StringKey.CATEGORY_BANK_OVERDRAFT to "Frais de d\u00e9couvert",
    StringKey.CATEGORY_BANK_OTHER to "Autres frais bancaires",

    // Sub-categories: Entertainment
    StringKey.CATEGORY_ENTERTAINMENT_GAMBLING to "Casinos et jeux de hasard",
    StringKey.CATEGORY_ENTERTAINMENT_MUSIC to "Musique et audio",
    StringKey.CATEGORY_ENTERTAINMENT_EVENTS to "\u00c9v\u00e9nements sportifs, parcs et mus\u00e9es",
    StringKey.CATEGORY_ENTERTAINMENT_TV_MOVIES to "T\u00e9l\u00e9 et cin\u00e9ma",
    StringKey.CATEGORY_ENTERTAINMENT_VIDEO_GAMES to "Jeux vid\u00e9o",
    StringKey.CATEGORY_ENTERTAINMENT_OTHER to "Autre divertissement",

    // Sub-categories: Food and Drink
    StringKey.CATEGORY_FOOD_ALCOHOL to "Bi\u00e8re, vin et spiritueux",
    StringKey.CATEGORY_FOOD_COFFEE to "Caf\u00e9",
    StringKey.CATEGORY_FOOD_FAST_FOOD to "Restauration rapide",
    StringKey.CATEGORY_FOOD_GROCERIES to "\u00c9picerie",
    StringKey.CATEGORY_FOOD_RESTAURANT to "Restaurant",
    StringKey.CATEGORY_FOOD_VENDING to "Distributeurs automatiques",
    StringKey.CATEGORY_FOOD_OTHER to "Autre alimentation",

    // Sub-categories: General Merchandise
    StringKey.CATEGORY_MERCHANDISE_BOOKS to "Librairies et kiosques",
    StringKey.CATEGORY_MERCHANDISE_CLOTHING to "V\u00eatements et accessoires",
    StringKey.CATEGORY_MERCHANDISE_CONVENIENCE to "D\u00e9panneurs",
    StringKey.CATEGORY_MERCHANDISE_DEPARTMENT to "Grands magasins",
    StringKey.CATEGORY_MERCHANDISE_DISCOUNT to "Magasins \u00e0 rabais",
    StringKey.CATEGORY_MERCHANDISE_ELECTRONICS to "\u00c9lectronique",
    StringKey.CATEGORY_MERCHANDISE_GIFTS to "Cadeaux et nouveaut\u00e9s",
    StringKey.CATEGORY_MERCHANDISE_OFFICE to "Fournitures de bureau",
    StringKey.CATEGORY_MERCHANDISE_ONLINE to "March\u00e9s en ligne",
    StringKey.CATEGORY_MERCHANDISE_PET to "Fournitures pour animaux",
    StringKey.CATEGORY_MERCHANDISE_SPORTING to "Articles de sport",
    StringKey.CATEGORY_MERCHANDISE_SUPERSTORES to "Grandes surfaces",
    StringKey.CATEGORY_MERCHANDISE_TOBACCO to "Tabac et vapotage",
    StringKey.CATEGORY_MERCHANDISE_OTHER to "Autres marchandises",

    // Sub-categories: Home Improvement
    StringKey.CATEGORY_HOME_FURNITURE to "Mobilier",
    StringKey.CATEGORY_HOME_HARDWARE to "Quincaillerie",
    StringKey.CATEGORY_HOME_REPAIR to "R\u00e9paration et entretien",
    StringKey.CATEGORY_HOME_SECURITY to "S\u00e9curit\u00e9",
    StringKey.CATEGORY_HOME_OTHER to "Autre am\u00e9lioration",

    // Sub-categories: Medical
    StringKey.CATEGORY_MEDICAL_DENTAL to "Soins dentaires",
    StringKey.CATEGORY_MEDICAL_EYE to "Soins oculaires",
    StringKey.CATEGORY_MEDICAL_NURSING to "Soins infirmiers",
    StringKey.CATEGORY_MEDICAL_PHARMACY to "Pharmacies et suppl\u00e9ments",
    StringKey.CATEGORY_MEDICAL_PRIMARY to "Soins primaires",
    StringKey.CATEGORY_MEDICAL_VETERINARY to "Services v\u00e9t\u00e9rinaires",
    StringKey.CATEGORY_MEDICAL_OTHER to "Autre m\u00e9dical",

    // Sub-categories: Personal Care
    StringKey.CATEGORY_PERSONAL_GYM to "Gyms et centres de conditionnement",
    StringKey.CATEGORY_PERSONAL_HAIR_BEAUTY to "Coiffure et beaut\u00e9",
    StringKey.CATEGORY_PERSONAL_LAUNDRY to "Blanchisserie et nettoyage \u00e0 sec",
    StringKey.CATEGORY_PERSONAL_OTHER to "Autres soins personnels",

    // Sub-categories: General Services
    StringKey.CATEGORY_SERVICES_ACCOUNTING to "Comptabilit\u00e9 et planification financi\u00e8re",
    StringKey.CATEGORY_SERVICES_AUTOMOTIVE to "Automobile",
    StringKey.CATEGORY_SERVICES_CHILDCARE to "Garde d'enfants",
    StringKey.CATEGORY_SERVICES_LEGAL to "Consultation et services juridiques",
    StringKey.CATEGORY_SERVICES_EDUCATION to "\u00c9ducation",
    StringKey.CATEGORY_SERVICES_INSURANCE to "Assurance",
    StringKey.CATEGORY_SERVICES_POSTAGE to "Poste et exp\u00e9dition",
    StringKey.CATEGORY_SERVICES_STORAGE to "Entreposage",
    StringKey.CATEGORY_SERVICES_OTHER to "Autres services g\u00e9n\u00e9raux",

    // Sub-categories: Government and Non-Profit
    StringKey.CATEGORY_GOVERNMENT_DONATIONS to "Dons",
    StringKey.CATEGORY_GOVERNMENT_AGENCIES to "Minist\u00e8res et organismes gouvernementaux",
    StringKey.CATEGORY_GOVERNMENT_TAX to "Paiement d'imp\u00f4t",
    StringKey.CATEGORY_GOVERNMENT_OTHER to "Autre gouvernement et OBNL",

    // Sub-categories: Transportation
    StringKey.CATEGORY_TRANSPORT_BIKES to "V\u00e9los et trottinettes",
    StringKey.CATEGORY_TRANSPORT_GAS to "Stations-service",
    StringKey.CATEGORY_TRANSPORT_PARKING to "Stationnement",
    StringKey.CATEGORY_TRANSPORT_PUBLIC to "Transport en commun",
    StringKey.CATEGORY_TRANSPORT_RIDESHARE to "Taxis et covoiturage",
    StringKey.CATEGORY_TRANSPORT_TOLLS to "P\u00e9ages",
    StringKey.CATEGORY_TRANSPORT_OTHER to "Autre transport",

    // Sub-categories: Travel
    StringKey.CATEGORY_TRAVEL_AIRLINES to "Compagnies a\u00e9riennes et aviation",
    StringKey.CATEGORY_TRAVEL_LODGING to "H\u00e9bergement",
    StringKey.CATEGORY_TRAVEL_RENTAL_CARS to "Location de voitures",
    StringKey.CATEGORY_TRAVEL_AGENCIES to "Agences de voyage et voyagistes",
    StringKey.CATEGORY_TRAVEL_OTHER to "Autre voyage",

    // Sub-categories: Rent and Utilities
    StringKey.CATEGORY_UTILITIES_GAS_ELECTRIC to "Gaz et \u00e9lectricit\u00e9",
    StringKey.CATEGORY_UTILITIES_INTERNET to "Internet et c\u00e2ble",
    StringKey.CATEGORY_UTILITIES_RENT to "Loyer",
    StringKey.CATEGORY_UTILITIES_SEWAGE to "\u00c9gouts et gestion des d\u00e9chets",
    StringKey.CATEGORY_UTILITIES_TELEPHONE to "T\u00e9l\u00e9phone",
    StringKey.CATEGORY_UTILITIES_WATER to "Eau",
    StringKey.CATEGORY_UTILITIES_OTHER to "Autres services publics",

    // Guide page
    StringKey.TOUR_BACK to "\u2190 Retour",
    StringKey.GUIDE_TITLE to "Comment fonctionne wiz\u2261r",
    StringKey.GUIDE_SUBTITLE to "Votre outil personnel de suivi du taux d'\u00e9pargne \u2014 automatis\u00e9 et sans effort.",
    StringKey.GUIDE_STEP1_TITLE to "Connectez vos comptes de d\u00e9penses",
    StringKey.GUIDE_STEP1_DESC to "Reliez votre <strong>compte ch\u00e8ques</strong> et vos <strong>cartes de cr\u00e9dit</strong> \u2014 les comptes o\u00f9 se font vos d\u00e9penses courantes. wiz\u2261r se connecte de fa\u00e7on s\u00e9curis\u00e9e via <strong>Plaid</strong>, la m\u00eame technologie utilis\u00e9e par les grandes banques et applis financi\u00e8res. Nous ne voyons et ne stockons jamais vos identifiants bancaires.",
    StringKey.GUIDE_STEP2_TITLE to "Cr\u00e9ez des objectifs d'\u00e9pargne",
    StringKey.GUIDE_STEP2_DESC to "D\u00e9finissez des objectifs pour ce qui compte \u2014 fonds d'urgence, vacances, mise de fonds, ou retraite. Pas besoin d'un compte distinct pour chaque objectif. L'important, c'est que l'argent <strong>quitte votre compte courant</strong> \u2014 sous forme de virement sortant vers toute \u00e9pargne, placement ou autre endroit que vous n'avez pas \u00e0 suivre ici. wiz\u2261r identifie ces virements sortants, les attribue \u00e0 vos objectifs, et les retire de ce qu'il vous reste \u00e0 d\u00e9penser.",
    StringKey.GUIDE_STEP3_TITLE to "Cat\u00e9gorisez les transferts comme contributions",
    StringKey.GUIDE_STEP3_DESC to "Quand vous voyez un transfert vers un compte d'\u00e9pargne dans vos transactions, assignez-le simplement \u00e0 un de vos objectifs. L'IA de wiz\u2261r apprendra vos habitudes et commencera \u00e0 cat\u00e9goriser automatiquement. <strong>C'est tout</strong> \u2014 votre suivi d'\u00e9pargne est maintenant enti\u00e8rement automatis\u00e9.",
    StringKey.GUIDE_STEP4_TITLE to "Regardez votre taux d'\u00e9pargne grandir",
    StringKey.GUIDE_STEP4_DESC to "Votre tableau de bord affiche votre <strong>taux d'\u00e9pargne</strong> \u2014 le pourcentage de revenus allou\u00e9 \u00e0 vos objectifs. Voyez les tendances mensuelles, obtenez des recommandations personnalis\u00e9es et fixez un taux d'\u00e9pargne cible. La plupart des experts financiers recommandent d'\u00e9pargner <strong>au moins 20\u00a0%</strong> de vos revenus (la r\u00e8gle 50/30/20).",
    StringKey.GUIDE_PRO_TIPS to "\uD83D\uDCA1 Astuces",
    StringKey.GUIDE_TIP_TRACK to "<strong>Pas besoin de suivre tous vos comptes</strong> \u2014 seulement ceux o\u00f9 les d\u00e9penses se font et o\u00f9 vous \u00eates pay\u00e9s (ch\u00e8ques + cartes de cr\u00e9dit). Tant que l'argent allou\u00e9 \u00e0 vos objectifs quitte ces comptes, le suivi est simple.",
    StringKey.GUIDE_TIP_ONE_GOAL to "<strong>Un objectif par compte d'\u00e9pargne</strong> fonctionne le mieux \u2014 \u00e7a rend le suivi automatique sans accroc. Mais ce n'est pas obligatoire. L'essentiel, c'est de voir que l'argent est mis de c\u00f4t\u00e9.",
    StringKey.GUIDE_TIP_CHECK_IN to "<strong>V\u00e9rifiez chaque semaine</strong> \u2014 un coup d'\u0153il rapide \u00e0 votre tableau de bord vous garde motiv\u00e9 et d\u00e9tecte les transactions mal cat\u00e9goris\u00e9es.",
    StringKey.GUIDE_TIP_BUDGET to "<strong>Utilisez le planificateur de budget</strong> pour fixer des limites de d\u00e9penses par cat\u00e9gorie et voir o\u00f9 r\u00e9duire pour \u00e9pargner plus.",
    StringKey.GUIDE_TIP_AI to "<strong>L'IA s'am\u00e9liore avec le temps</strong> \u2014 plus vous cat\u00e9gorisez, plus wiz≡r apprend vos habitudes.",
    StringKey.GUIDE_CTA to "Pr\u00eat \u00e0 prendre le contr\u00f4le de votre \u00e9pargne?",
    StringKey.GUIDE_BACK to "\u2039 Retour",

    // Subscribe page
    StringKey.SUBSCRIBE_CHOOSE_PLAN to "Choisissez votre forfait",
    StringKey.SUBSCRIBE_TRIAL_SUBTITLE to "Commencez avec un essai gratuit de 14 jours. Annulez \u00e0 tout moment.",
    StringKey.SUBSCRIBE_TRIALING_SUBTITLE to "Vous êtes en période d'essai. Abonnez-vous maintenant pour conserver votre accès complet.",
    StringKey.SUBSCRIBE_MONTHLY to "Mensuel",
    StringKey.SUBSCRIBE_YEARLY to "Annuel",
    StringKey.SUBSCRIBE_PER_MONTH to "/mois",
    StringKey.SUBSCRIBE_PER_YEAR to "/an",
    StringKey.SUBSCRIBE_TRIAL_INCLUDED to "Essai gratuit de 14 jours inclus",
    StringKey.SUBSCRIBE_START_FREE_TRIAL to "Commencer l'essai gratuit",
    StringKey.SUBSCRIBE_BEST_VALUE to "MEILLEURE VALEUR",
    StringKey.SUBSCRIBE_SAVE_PERCENT to "\u00c9conomisez 14\u00a0% \u2014 6,00\u00a0$/mois",
    StringKey.SUBSCRIBE_TRIAL_DISCLAIMER to "Aucun frais pendant votre essai de 14 jours. Annulez \u00e0 tout moment depuis vos param\u00e8tres de compte.",
    StringKey.SUBSCRIBE_SUCCESS_TITLE to "Vous \u00eates pr\u00eat!",
    StringKey.SUBSCRIBE_SUCCESS_DESC to "Votre abonnement est actif. Profitez de l'acc\u00e8s complet \u00e0 wiz≡r.",
    StringKey.SUBSCRIBE_GO_DASHBOARD to "Aller au tableau de bord",
    StringKey.SUBSCRIBE_CANCEL_TITLE to "Pas de souci!",
    StringKey.SUBSCRIBE_CANCEL_DESC to "Vous pouvez vous abonner \u00e0 tout moment pour d\u00e9bloquer l'acc\u00e8s complet.",
    StringKey.SUBSCRIBE_TRY_AGAIN to "R\u00e9essayer",
    StringKey.SUBSCRIBE_GO_BACK to "Retour",

    // Subscription banner
    StringKey.BANNER_FREE_TRIAL to "Essai gratuit",
    StringKey.BANNER_DAYS_REMAINING to "%d jours restants",
    StringKey.BANNER_DAY_REMAINING to "1 jour restant",
    StringKey.BANNER_SUBSCRIBE_NOW to "S'abonner maintenant",
    StringKey.BANNER_TRIAL_ENDED to "Votre essai est termin\u00e9 \u2014 le tableau de bord est en",
    StringKey.BANNER_READ_ONLY to "lecture seule",
    StringKey.BANNER_SUBSCRIBE_UNLOCK to "S'abonner pour d\u00e9bloquer",

    // Account re-link
    StringKey.ACCOUNT_RELINK_TITLE to "Connexion bancaire expir\u00e9e",
    StringKey.ACCOUNT_RELINK_DESC to "Votre banque exige une nouvelle authentification. Veuillez reconnecter votre compte pour reprendre la synchronisation.",
    StringKey.ACCOUNT_RELINK_BUTTON to "Reconnecter le compte",

    // Reset password
    StringKey.RESET_PASSWORD_TITLE to "R\u00e9initialiser le mot de passe",
    StringKey.RESET_PASSWORD_SUBTITLE to "Entrez votre courriel et un nouveau mot de passe.",
    StringKey.RESET_PASSWORD_EMAIL_PLACEHOLDER to "Adresse courriel",
    StringKey.RESET_PASSWORD_NEW_PASSWORD_PLACEHOLDER to "Nouveau mot de passe (min. 8 caract\u00e8res)",
    StringKey.RESET_PASSWORD_CONFIRM_PLACEHOLDER to "Confirmer le nouveau mot de passe",
    StringKey.RESET_PASSWORD_SUBMIT to "R\u00e9initialiser le mot de passe",
    StringKey.RESET_PASSWORD_BACK_TO_LOGIN to "\u2190 Retour \u00e0 la connexion",
    StringKey.RESET_PASSWORD_ENTER_EMAIL to "Veuillez entrer votre courriel.",
    StringKey.RESET_PASSWORD_MIN_LENGTH to "Le mot de passe doit contenir au moins 8 caract\u00e8res.",
    StringKey.RESET_PASSWORD_SUCCESS to "Le mot de passe a \u00e9t\u00e9 r\u00e9initialis\u00e9. Vous pouvez maintenant vous connecter.",
    StringKey.RESET_PASSWORD_FAILED to "\u00c9chec de la r\u00e9initialisation du mot de passe. Veuillez r\u00e9essayer.",

    // Settings extras
    StringKey.SETTINGS_CHOOSE_LANGUAGE to "Choisissez votre langue pr\u00e9f\u00e9r\u00e9e",
    StringKey.SETTINGS_ACCOUNT_ACTIONS to "Actions du compte",
    StringKey.SETTINGS_LOGOUT_DESC to "Ceci d\u00e9connectera votre compte bancaire et vous devrez vous reconnecter.",
    StringKey.SETTINGS_DANGER_ZONE to "Zone dangereuse",
    StringKey.SETTINGS_DELETE_ACCOUNT_DESC to "Supprimer d\u00e9finitivement votre compte et toutes les donn\u00e9es associ\u00e9es. Cette action est irr\u00e9versible.",
    StringKey.SETTINGS_DELETE_CONFIRM_TITLE to "\u26A0\uFE0F Supprimer le compte",
    StringKey.SETTINGS_DELETE_CONFIRM_DESC to "Ceci supprimera d\u00e9finitivement votre compte et toutes vos donn\u00e9es, incluant\u00a0:",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_ACCOUNTS to "Tous les comptes bancaires li\u00e9s",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_TRANSACTIONS to "L'historique des transactions",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_GOALS to "Les objectifs d'\u00e9pargne et progr\u00e8s",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_BUDGETS to "Les plans budg\u00e9taires",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_SUBSCRIPTION to "Votre abonnement (si actif)",
    StringKey.SETTINGS_DELETE_CANNOT_UNDO to "Cette action est irr\u00e9versible.",
    StringKey.SETTINGS_DELETE_FOREVER to "Supprimer d\u00e9finitivement",
    StringKey.SETTINGS_DELETING to "Suppression...",
    StringKey.SETTINGS_DELETE_REAUTH_TITLE to "Confirmez avec votre mot de passe",
    StringKey.SETTINGS_DELETE_REAUTH_MESSAGE to "Entrez votre mot de passe actuel pour confirmer la suppression de votre compte.",
    StringKey.SETTINGS_DELETE_REAUTH_PASSWORD_PLACEHOLDER to "Mot de passe actuel",
    StringKey.SETTINGS_DELETE_REAUTH_EMPTY_PASSWORD to "Veuillez entrer votre mot de passe actuel.",
    StringKey.SETTINGS_DELETE_REAUTH_INCORRECT_PASSWORD to "Mot de passe incorrect. R\u00e9essayez.",
    StringKey.SETTINGS_DELETE_REAUTH_RATE_LIMITED to "Trop de tentatives. Patientez une heure et r\u00e9essayez.",
    StringKey.SETTINGS_DELETE_REAUTH_GENERIC_ERROR to "Impossible de supprimer votre compte. R\u00e9essayez.",

    // MFA extras
    StringKey.MFA_BACKUP_CODE_HINT to "Vous pouvez aussi utiliser un code de secours",
    StringKey.MFA_BACK_TO_LOGIN to "Retour \u00e0 la connexion",
    StringKey.MFA_ENTER_VERIFICATION_CODE to "Entrez le code de v\u00e9rification\u00a0:",
    StringKey.MFA_VERIFICATION_CODE_LABEL to "Code de v\u00e9rification\u00a0:",
    StringKey.MFA_BACKUP_CODES_TITLE to "Codes de secours",
    StringKey.MFA_BACKUP_CODES_DESC to "Conservez ces codes en lieu s\u00fbr. Vous pouvez les utiliser pour vous connecter si vous perdez l'acc\u00e8s \u00e0 votre application d'authentification.",
    StringKey.MFA_ENABLE to "Activer l'A2F",
    StringKey.MFA_DISABLE_TITLE to "D\u00e9sactiver l'authentification \u00e0 deux facteurs",
    StringKey.MFA_DISABLE_DESC to "Entrez votre code d'authentification actuel pour d\u00e9sactiver l'authentification \u00e0 deux facteurs.",
    StringKey.MFA_ENABLED_DESC to "Votre compte est prot\u00e9g\u00e9 par l'authentification \u00e0 deux facteurs",
    StringKey.MFA_DISABLED_DESC to "Ajoutez une couche de s\u00e9curit\u00e9 suppl\u00e9mentaire \u00e0 votre compte",
    StringKey.MFA_ENABLED to "Activ\u00e9e",
    StringKey.MFA_DISABLED to "D\u00e9sactiv\u00e9e",
    StringKey.MFA_ENTER_CODE_ERROR to "Veuillez entrer un code \u00e0 6 chiffres",
    StringKey.MFA_SCAN_QR to "Scannez le code QR ci-dessous avec votre application d'authentification (Google Authenticator, Authy, etc.)",
    StringKey.MFA_MANUAL_CODE to "Ou entrez ce code manuellement :",

    // Category display names
    StringKey.CATEGORY_DISPLAY_SHOPPING to "Magasinage",
    StringKey.CATEGORY_DISPLAY_HEALTHCARE to "Sant\u00e9",
    StringKey.CATEGORY_DISPLAY_SERVICES to "Services",
    StringKey.CATEGORY_DISPLAY_TRANSFERS_OUT to "Virements sortants",
    StringKey.CATEGORY_DISPLAY_TRANSFERS_IN to "Virements entrants",
    StringKey.CATEGORY_DISPLAY_HOME to "Maison",
    StringKey.CATEGORY_DISPLAY_GOVERNMENT to "Gouvernement",
    StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE to "Loyer/Hypoth\u00e8que",
    StringKey.CATEGORY_DISPLAY_UTILITIES to "Services publics",
    StringKey.CATEGORY_DISPLAY_SUBSCRIPTIONS to "Abonnements",
    StringKey.CATEGORY_DISPLAY_INSURANCE to "Assurance",

    // Transaction detail
    StringKey.TRANSACTION_DETAILS_TITLE to "D\u00e9tails de la transaction",
    StringKey.TRANSACTION_CATEGORY to "Cat\u00e9gorie",
    StringKey.TRANSACTION_ACCOUNT to "Compte",
    StringKey.TRANSACTION_DEBIT to "D\u00e9bit",
    StringKey.TRANSACTION_CREDIT to "Cr\u00e9dit",
    StringKey.TRANSACTION_GOAL to "Objectif",
    StringKey.TRANSACTION_ACTIONS to "Actions",
    StringKey.TRANSACTION_CHANGE_CATEGORY to "Changer la cat\u00e9gorie",
    StringKey.TRANSACTION_UNSPLIT to "Annuler la division",
    StringKey.TRANSACTION_UNDISTRIBUTE to "Annuler la répartition",
    StringKey.TRANSACTION_SPLIT to "Diviser la transaction",
    StringKey.TRANSACTION_DISTRIBUTE to "R\u00e9partir sur plusieurs mois",
    StringKey.TRANSACTION_MOVE to "Déplacer la transaction",
    StringKey.TRANSACTION_MOVE_TO to "Déplacer vers",
    StringKey.TRANSACTION_MOVE_PREVIEW to "Aperçu du déplacement",
    StringKey.TRANSACTION_MOVED_BADGE to "Déplacée",
    StringKey.TRANSACTION_MOVED_DESC to "Cette transaction a été déplacée à un autre mois. L'élément déplacé est affiché dans la liste des transactions.",
    StringKey.TRANSACTION_MOVED_TITLE to "Transaction déplacée",
    StringKey.TRANSACTION_DISTRIBUTED_TITLE to "Transaction répartie",
    StringKey.TRANSACTION_DISTRIBUTED_DESC to "Cette transaction a été répartie sur plusieurs mois. Les éléments répartis sont affichés dans la liste des transactions.",
    StringKey.TRANSACTION_DISTRIBUTE_TOTAL to "Total :",
    StringKey.TRANSACTION_DISTRIBUTE_START_MONTH to "Mois de début",
    StringKey.TRANSACTION_DISTRIBUTE_END_MONTH to "Mois de fin",
    StringKey.TRANSACTION_DISTRIBUTE_EACH_MONTH to "Par mois",
    StringKey.TRANSACTION_DISTRIBUTE_PREVIEW to "Aperçu de la répartition",
    StringKey.TRANSACTION_DISTRIBUTE_MONTHS to "mois",
    StringKey.TRANSACTION_SPLIT_TITLE to "Diviser la transaction",
    StringKey.TRANSACTION_SPLIT_MANUAL_ENTRY to "Saisie manuelle",
    StringKey.TRANSACTION_SPLIT_CSV_IMPORT to "Import CSV",
    StringKey.TRANSACTION_SPLIT_ITEM to "Élément",
    StringKey.TRANSACTION_SPLIT_AMOUNT to "Montant",
    StringKey.TRANSACTION_SPLIT_AMOUNT_CREDIT to "Montant (crédit / remboursement)",
    StringKey.TRANSACTION_SPLIT_NOTE_OPTIONAL to "Note (optionnel)",
    StringKey.TRANSACTION_SPLIT_NOTE_PLACEHOLDER to "Ajouter une note...",
    StringKey.TRANSACTION_SPLIT_REMOVE to "Supprimer",
    StringKey.TRANSACTION_SPLIT_ADD_ITEM to "+ Ajouter un élément",
    StringKey.TRANSACTION_SPLIT_REMAINING_WARNING to "restant — ajustez les montants pour équilibrer avant de sauvegarder",
    StringKey.TRANSACTION_SPLIT_OVER_WARNING to "au-dessus du total — ajustez les montants pour équilibrer avant de sauvegarder",
    StringKey.TRANSACTION_GOAL_CREATE to "Créer un objectif",
    StringKey.TRANSACTION_GOAL_REMAINING to "restant",
    StringKey.TRANSACTION_NOTE_TAKE to "Ajouter une note",
    StringKey.TRANSACTION_ADD_NOTE to "Note",
    StringKey.TRANSACTION_NOT_FOUND to "Transaction introuvable",
    StringKey.TRANSACTION_SESSION_EXPIRED to "Votre session a expir\u00e9. Veuillez vous reconnecter.",

    // Misc
    StringKey.LABEL_AVAILABLE to "Disponible",
    StringKey.LABEL_AVAILABLE_BALANCE to "Solde disponible",
    StringKey.LABEL_BANK_ACCOUNTS to "Comptes bancaires",
    StringKey.LABEL_CONNECTION_ERROR to "Erreur de connexion",
    StringKey.LABEL_LOADING_CHART to "Chargement des donn\u00e9es du graphique...",
    StringKey.LABEL_SETTINGS to "Param\u00e8tres",
    StringKey.LABEL_AND_X_MORE to "... et %s de plus",

    // Chart titles & legends
    StringKey.CHART_SAVINGS_TREND_ROLLING to "Tendance épargne % (12 derniers mois)",
    StringKey.CHART_SAVINGS_TREND_YEAR to "Tendance \u00e9pargne % (%s)",
    StringKey.CHART_TARGET_LABEL to "Cible",
    StringKey.CHART_ACTUAL_SAVINGS_PERCENT to "Épargne réelle %",
    StringKey.CHART_TARGET_PERCENT to "Cible %s%",
    StringKey.CHART_CATEGORY_SPENDING_MONTH to "D\u00e9penses par cat\u00e9gorie (%s)",
    StringKey.CHART_NO_SPENDING_YET to "Aucune d\u00e9pense ce mois-ci",
    StringKey.CHART_LOADING_TRANSACTIONS to "Chargement des transactions...",
    StringKey.CHART_EXPENSE_DIST_ROLLING to "R\u00e9partition des d\u00e9penses (12 derniers mois)",
    StringKey.CHART_EXPENSE_DIST_YEAR to "R\u00e9partition des d\u00e9penses (%s)",
    StringKey.CHART_NO_DATA_ACCOUNTS to "Aucune donn\u00e9e pour les comptes s\u00e9lectionn\u00e9s",
    StringKey.CHART_EXPENSE_TIMELINE_ROLLING to "Chronologie des d\u00e9penses (12 derniers mois)",
    StringKey.CHART_EXPENSE_TIMELINE_YEAR to "Chronologie des d\u00e9penses (%s)",
    StringKey.CHART_NO_EXPENSE_DATA to "Aucune donn\u00e9e de d\u00e9penses disponible",
    StringKey.CHART_BUDGET_LEGEND to "Budget",
    StringKey.CHART_ACTUAL_LEGEND to "R\u00e9el",
    StringKey.CHART_OVER_BUDGET_LEGEND to "D\u00e9passement",
    StringKey.CHART_TOGGLE_BUDGET to "Budget",
    StringKey.CHART_PILL_ROLLING_12MO to "12 derniers mois",
    StringKey.CHART_PILL_SELECTED_MONTH to "Mois s\u00e9lectionn\u00e9",
    StringKey.CHART_UNDER_FORECAST to "%s% sous la pr\u00e9vision de %s",
    StringKey.CHART_OVER_FORECAST to "d\u00e9passe la pr\u00e9vision de %s de %s%",
    StringKey.CHART_TARGET_TOOLTIP to "Cible : %s%",

    // Dashboard recommendations
    StringKey.DASHBOARD_RECOMMENDED_ALLOCATION_HEADER to "R\u00e9partition recommand\u00e9e :",
    StringKey.DASHBOARD_TIME_DAYS_LEFT to "%s jours",
    StringKey.DASHBOARD_TIME_MONTHS_LEFT to "%s mois",

    // Goals page
    StringKey.GOALS_NO_GOALS_TITLE to "Aucun objectif d'\u00e9pargne",
    StringKey.GOALS_NO_GOALS_DESC to "Fixez un objectif d'\u00e9pargne et recevez des recommandations IA pour l'atteindre",
    StringKey.GOALS_LOADING to "Chargement des objectifs...",
    StringKey.GOALS_PRIORITY_MUST_HAVE to "ESSENTIEL",
    StringKey.GOALS_PRIORITY_NICE_TO_HAVE to "SOUHAITABLE",
    StringKey.GOALS_PRIORITY_STRETCH_GOAL to "OBJECTIF AMBITIEUX",
    StringKey.GOALS_PRIORITY_MAYBE to "PEUT-\u00caTRE",
    StringKey.GOALS_PER_MONTH to "/mois",
    StringKey.GOALS_TARGET_DATE to "Cible : %s",
    StringKey.GOALS_CREATED_ON to "Vous avez cr\u00e9\u00e9 cet objectif le %s.",

    // Goals encouragement
    StringKey.GOALS_ENCOURAGE_COMPLETE to "Vous l'avez fait! \ud83c\udf89 C'est le temps de c\u00e9l\u00e9brer!",
    StringKey.GOALS_ENCOURAGE_ALMOST to "Presque l\u00e0! Plus que %s\$ \u00e0 accumuler.",
    StringKey.GOALS_ENCOURAGE_HALFWAY to "\u00c0 mi-chemin! Gardez le rythme.",
    StringKey.GOALS_ENCOURAGE_DEADLINE_SOON to "Attention \u2014 l'\u00e9ch\u00e9ance approche vite. Il vous manque %s\$.",
    StringKey.GOALS_ENCOURAGE_FEW_MONTHS to "Quelques mois restants. Essayez %s\$/mois pour rester sur la bonne voie.",
    StringKey.GOALS_ENCOURAGE_ON_TRACK to "\u00c0 %s\$/mois, vous atteindrez cet objectif pile \u00e0 temps.",
    StringKey.GOALS_ENCOURAGE_EVERY_BIT to "Chaque petit montant compte!",

    // Financial tips
    StringKey.TIPS_TITLE to "Conseils financiers",
    StringKey.TIPS_SEARCH_PLACEHOLDER to "Rechercher des conseils...",
    StringKey.TIPS_PINNED to "Conseils \u00e9pingl\u00e9s",
    StringKey.TIPS_PIN_THIS to "\u00c9pingler ce conseil",
    StringKey.TIPS_WHY_IT_WORKS to "POURQUOI \u00c7A MARCHE",
    StringKey.TIPS_EXAMPLE to "EXEMPLE",
    StringKey.TIPS_GOT_IT to "Compris!",

    // Financial tips - Why it works
    StringKey.TIPS_WHY_50_30_20 to "Cette r\u00e9partition simple \u00e9limine la fatigue d\u00e9cisionnelle. Vous savez toujours exactement combien vous pouvez d\u00e9penser sans culpabilit\u00e9.",
    StringKey.TIPS_WHY_AUTOMATE to "Quand \u00e9pargner ne demande aucun effort, \u00e7a se fait vraiment. Votre futur vous remerciera.",
    StringKey.TIPS_WHY_24_HOUR to "Les achats impulsifs semblent urgents mais le sont rarement. Dormir dessus r\u00e9v\u00e8le ce que vous voulez vraiment.",
    StringKey.TIPS_WHY_COMPOUND to "Les int\u00e9r\u00eats sur les int\u00e9r\u00eats cr\u00e9ent une croissance exponentielle. Commencer t\u00f4t compte plus que commencer gros.",
    StringKey.TIPS_WHY_SMALL_WINS to "La psychologie montre que les petites victoires cr\u00e9ent de l'\u00e9lan. Les petites habitudes quotidiennes surpassent les gros efforts occasionnels.",
    StringKey.TIPS_WHY_EMERGENCY to "La tranquillit\u00e9 financi\u00e8re vient de savoir que les impr\u00e9vus ne vous feront pas d\u00e9railler. C'est une assurance que vous vous payez \u00e0 vous-m\u00eame.",
    StringKey.TIPS_WHY_TFSA to "Les cotisations ne sont pas d\u00e9ductibles, mais toute la croissance et les retraits sont 100% libres d'imp\u00f4t. Id\u00e9al pour l'\u00e9pargne flexible.",
    StringKey.TIPS_WHY_RRSP to "Les cotisations r\u00e9duisent votre revenu imposable maintenant. Vous payez l'imp\u00f4t plus tard au retrait \u00e0 la retraite (souvent \u00e0 un taux moindre).",
    StringKey.TIPS_WHY_FHSA to "Le meilleur des deux mondes : cotisations d\u00e9ductibles ET retraits libres d'imp\u00f4t pour l'achat de votre premi\u00e8re maison.",
    StringKey.TIPS_WHY_ACCOUNTS to "Chaque type de compte a des avantages fiscaux uniques. Utiliser le bon m\u00e9lange maximise votre patrimoine \u00e0 long terme.",
    StringKey.TIPS_WHY_DEFAULT to "De petites actions constantes produisent des r\u00e9sultats majeurs avec le temps.",

    // Financial tips - Example labels
    StringKey.TIPS_EXAMPLE_MONTHLY_INCOME to "Revenu mensuel",
    StringKey.TIPS_EXAMPLE_NEEDS to "Besoins (50%)",
    StringKey.TIPS_EXAMPLE_WANTS to "Envies (30%)",
    StringKey.TIPS_EXAMPLE_SAVINGS to "\u00c9pargne (20%)",
    StringKey.TIPS_EXAMPLE_PAYDAY to "Jour de paie",
    StringKey.TIPS_EXAMPLE_AUTO_TRANSFER to "Virement auto",
    StringKey.TIPS_EXAMPLE_YEARLY_SAVINGS to "\u00c9pargne annuelle",
    StringKey.TIPS_EXAMPLE_MONTHLY_CONTRIBUTION to "Cotisation mensuelle",
    StringKey.TIPS_EXAMPLE_TIME_PERIOD to "P\u00e9riode",
    StringKey.TIPS_EXAMPLE_AT_RETURN to "\u00c0 7% de rendement",
    StringKey.TIPS_EXAMPLE_DAILY_SAVINGS to "\u00c9pargne quotidienne",
    StringKey.TIPS_EXAMPLE_WEEKLY_TOTAL to "Total hebdomadaire",
    StringKey.TIPS_EXAMPLE_YEARLY_TOTAL to "Total annuel",
    StringKey.TIPS_EXAMPLE_MONTHLY_EXPENSES to "D\u00e9penses mensuelles",
    StringKey.TIPS_EXAMPLE_3_MONTH_FUND to "Fonds 3 mois",
    StringKey.TIPS_EXAMPLE_6_MONTH_FUND to "Fonds 6 mois",
    StringKey.TIPS_EXAMPLE_CONTRIBUTION_ROOM to "Droits de cotisation",
    StringKey.TIPS_EXAMPLE_WITHDRAWALS to "Retraits",
    StringKey.TIPS_EXAMPLE_TAX_FREE to "Libre d'imp\u00f4t",
    StringKey.TIPS_EXAMPLE_BEST_FOR to "Id\u00e9al pour",
    StringKey.TIPS_EXAMPLE_FLEXIBLE_SAVINGS to "\u00c9pargne flexible",
    StringKey.TIPS_EXAMPLE_CONTRIBUTION_LIMIT to "Plafond de cotisation",
    StringKey.TIPS_EXAMPLE_TAX_BENEFIT to "Avantage fiscal",
    StringKey.TIPS_EXAMPLE_DEDUCTIBLE_NOW to "D\u00e9ductible maintenant",
    StringKey.TIPS_EXAMPLE_RETIREMENT to "Retraite",
    StringKey.TIPS_EXAMPLE_ANNUAL_LIMIT to "Limite annuelle",
    StringKey.TIPS_EXAMPLE_LIFETIME_MAX to "Maximum \u00e0 vie",
    StringKey.TIPS_EXAMPLE_FIRST_HOME to "Premi\u00e8re maison",
    StringKey.TIPS_EXAMPLE_15TH_OF_MONTH to "15 du mois",
    StringKey.TIPS_EXAMPLE_20_YEARS to "20 ans",

    // Goal dialog
    StringKey.GOAL_DIALOG_TITLE to "Nouvel objectif d'\u00e9pargne",
    StringKey.GOAL_DIALOG_NAME_LABEL to "Nom de l'objectif *",
    StringKey.GOAL_DIALOG_NAME_PLACEHOLDER to "ex. : Fonds d'urgence",
    StringKey.GOAL_DIALOG_AMOUNT_LABEL to "Montant cible *",
    StringKey.GOAL_DIALOG_DATE_LABEL to "Date cible *",
    StringKey.GOAL_DIALOG_CATEGORY_LABEL to "Cat\u00e9gorie",
    StringKey.GOAL_DIALOG_PRIORITY_LABEL to "Priorit\u00e9",
    StringKey.GOAL_DIALOG_ICON_LABEL to "Ic\u00f4ne",
    StringKey.GOAL_DIALOG_CREATE to "Cr\u00e9er l'objectif",

    // Goal categories (dialog)
    StringKey.GOAL_CAT_EMERGENCY_FUND to "Fonds d'urgence",
    StringKey.GOAL_CAT_VACATION to "Vacances",
    StringKey.GOAL_CAT_HOME_PURCHASE to "Achat de maison",
    StringKey.GOAL_CAT_RETIREMENT to "Retraite",
    StringKey.GOAL_CAT_EDUCATION to "\u00c9ducation",
    StringKey.GOAL_CAT_MAJOR_PURCHASE to "Achat important",
    StringKey.GOAL_CAT_OTHER to "Autre",

    // Goal priorities (dialog)
    StringKey.GOAL_PRI_MUST_HAVE to "\ud83d\udd34 Essentiel",
    StringKey.GOAL_PRI_NICE_TO_HAVE to "\ud83d\udfe0 Souhaitable",
    StringKey.GOAL_PRI_STRETCH_GOAL to "\ud83d\udd35 Objectif ambitieux",
    StringKey.GOAL_PRI_MAYBE to "\u26aa Peut-\u00eatre",

    // Budget planning
    StringKey.BUDGET_COPY_FROM_YEAR to "Copier de %s",
    StringKey.BUDGET_LOGIN_TO_COPY to "Veuillez vous connecter pour copier les donn\u00e9es budg\u00e9taires",
    StringKey.BUDGET_COPY_SUCCESS to "Budget de %s copi\u00e9 vers %s avec succ\u00e8s!",
    StringKey.BUDGET_CATEGORY_CSV to "Cat\u00e9gorie",

    // Recommendations page
    StringKey.RECO_TITLE to "Recommandations IA de portefeuille",
    StringKey.RECO_OPTIMIZING to "Optimisation de votre plan d'\u00e9pargne...",
    StringKey.RECO_NO_GOALS_TITLE to "Aucun objectif",
    StringKey.RECO_NO_GOALS_DESC to "Cr\u00e9ez des objectifs d'\u00e9pargne pour obtenir des recommandations IA",
    StringKey.RECO_UNABLE_TO_GENERATE to "Impossible de g\u00e9n\u00e9rer le plan d'allocation",
    StringKey.RECO_PORTFOLIO_SUMMARY to "\ud83c\udfaf R\u00e9sum\u00e9 du portefeuille",
    StringKey.RECO_MONTHLY_ALLOCATION to "\ud83d\udcb0 Allocation mensuelle par objectif",
    StringKey.RECO_SMART_SAVINGS to "\ud83d\udcca Plan d'\u00e9pargne intelligent",
    StringKey.RECO_SMART_SAVINGS_DESC to "Chaque r\u00e9duction est automatiquement r\u00e9partie entre vos objectifs selon la priorit\u00e9 et l'urgence",
    StringKey.RECO_TOTAL_NEEDED to "Total n\u00e9cessaire/mois",
    StringKey.RECO_AVAILABLE_MONTH to "Disponible/mois",
    StringKey.RECO_OPTIMIZATION_SCORE to "Score d'optimisation",
    StringKey.RECO_FORECASTED_INCOME to "Revenu pr\u00e9vu",
    StringKey.RECO_FORECASTED_EXPENSES to "D\u00e9penses pr\u00e9vues",
    StringKey.RECO_FUNDING_GAP to "\u26a0\ufe0f \u00c9cart de financement d\u00e9tect\u00e9",
    StringKey.RECO_FUNDING_GAP_DESC to "Il vous manque %s\$ par mois pour financer tous vos objectifs. Les objectifs prioritaires seront financ\u00e9s en premier.",
    StringKey.RECO_PLAN_FEASIBLE to "\u2705 Le plan est r\u00e9alisable!",
    StringKey.RECO_PLAN_FEASIBLE_DESC to "Vous avez assez d'\u00e9pargne pour atteindre vos objectifs. Suivez le plan ci-dessous.",
    StringKey.RECO_PER_MONTH to "par mois",
    StringKey.RECO_FULLY_FUNDED to "\u2705 Enti\u00e8rement financ\u00e9",
    StringKey.RECO_PERCENT_OF_NEED to "%s% du besoin",
    StringKey.RECO_COMPLETE_IN to "Complet dans :",
    StringKey.RECO_COMPLETE_NEVER to "Jamais",
    StringKey.RECO_COMPLETE_MONTHS to "%s mois",
    StringKey.RECO_CURRENT to "Actuel : %s\$/mois",
    StringKey.RECO_CUT to "(r\u00e9duction de %s%)",
    StringKey.RECO_ALLOCATION_ACROSS_GOALS to "\ud83d\udca1 R\u00e9partition entre les objectifs :",

    // Advice card categories
    StringKey.ADVICE_CAT_SAVINGS_TIPS to "Conseils d'\u00e9pargne",
    StringKey.ADVICE_CAT_GOAL_SETTING to "Objectifs financiers",
    StringKey.ADVICE_CAT_ACCOUNTS to "Types de comptes",
    StringKey.ADVICE_CAT_ENCOURAGEMENT to "Encouragement",
    StringKey.ADVICE_CAT_BEHAVIORAL to "Habitudes financi\u00e8res",

    // Advice card titles and content
    StringKey.ADVICE_TIP_50_30_20_TITLE to "La r\u00e8gle 50/30/20",
    StringKey.ADVICE_TIP_50_30_20_CONTENT to "Allouez 50\u00a0% de vos revenus aux besoins (loyer, \u00e9picerie), 30\u00a0% aux envies (divertissement, restaurants) et 20\u00a0% \u00e0 l'\u00e9pargne et au remboursement de dettes.",
    StringKey.ADVICE_TIP_AUTOMATE_TITLE to "Automatisez votre \u00e9pargne",
    StringKey.ADVICE_TIP_AUTOMATE_CONTENT to "Configurez des virements automatiques le jour de paie. Quand l'\u00e9pargne se fait automatiquement, vous \u00eates moins tent\u00e9 de d\u00e9penser cet argent ailleurs.",
    StringKey.ADVICE_TIP_24_HOUR_TITLE to "La r\u00e8gle des 24 heures",
    StringKey.ADVICE_TIP_24_HOUR_CONTENT to "Avant de faire un achat non essentiel de plus de 50\u00a0$, attendez 24 heures. Cette p\u00e9riode de r\u00e9flexion vous aide \u00e0 distinguer les envies des besoins.",
    StringKey.ADVICE_GOAL_EMERGENCY_TITLE to "Commencez par un fonds d'urgence",
    StringKey.ADVICE_GOAL_EMERGENCY_CONTENT to "Visez \u00e0 \u00e9pargner 3 \u00e0 6 mois de d\u00e9penses courantes. Ce filet de s\u00e9curit\u00e9 vous prot\u00e8ge des co\u00fbts impr\u00e9vus et vous procure une tranquillit\u00e9 d'esprit financi\u00e8re.",
    StringKey.ADVICE_GOAL_SMART_TITLE to "Objectifs SMART",
    StringKey.ADVICE_GOAL_SMART_CONTENT to "Fixez des objectifs Sp\u00e9cifiques, Mesurables, Atteignables, R\u00e9alistes et Temporels. Au lieu de \u00ab\u00a0\u00e9pargner plus\u00a0\u00bb, essayez \u00ab\u00a0\u00e9pargner 200\u00a0$/mois pour les vacances d'ici d\u00e9cembre\u00a0\u00bb.",
    StringKey.ADVICE_GOAL_VISUALIZE_TITLE to "Visualisez votre r\u00e9ussite",
    StringKey.ADVICE_GOAL_VISUALIZE_CONTENT to "Nommez vos objectifs selon ce pour quoi vous \u00e9pargnez\u2014\u00ab\u00a0Vacances \u00e0 la plage 2026\u00a0\u00bb est plus motivant que \u00ab\u00a0Objectif d'\u00e9pargne #3\u00a0\u00bb.",
    StringKey.ADVICE_ACCOUNT_TFSA_TITLE to "Les bases du C\u00c9LI",
    StringKey.ADVICE_ACCOUNT_TFSA_CONTENT to "Le Compte d'\u00e9pargne libre d'imp\u00f4t permet \u00e0 vos placements de cro\u00eetre \u00e0 l'abri de l'imp\u00f4t. Les retraits sont flexibles et n'affectent pas les prestations gouvernementales. Id\u00e9al pour les objectifs \u00e0 court et moyen terme.",
    StringKey.ADVICE_ACCOUNT_RRSP_TITLE to "Le REER pour la retraite",
    StringKey.ADVICE_ACCOUNT_RRSP_CONTENT to "Le R\u00e9gime enregistr\u00e9 d'\u00e9pargne-retraite vous donne une d\u00e9duction fiscale maintenant, mais vous paierez de l'imp\u00f4t sur les retraits. Id\u00e9al si vous pr\u00e9voyez \u00eatre dans une tranche d'imposition inf\u00e9rieure \u00e0 la retraite.",
    StringKey.ADVICE_ACCOUNT_FHSA_TITLE to "Le CELIAPP pour votre premi\u00e8re maison",
    StringKey.ADVICE_ACCOUNT_FHSA_CONTENT to "Le Compte d'\u00e9pargne libre d'imp\u00f4t pour l'achat d'une premi\u00e8re propri\u00e9t\u00e9 combine le meilleur du C\u00c9LI et du REER\u2014d\u00e9duction fiscale sur les cotisations ET croissance libre d'imp\u00f4t. Parfait pour les premiers acheteurs.",
    StringKey.ADVICE_ACCOUNT_401K_TITLE to "Les bases du 401(k)",
    StringKey.ADVICE_ACCOUNT_401K_CONTENT to "Un 401(k) vous permet d'\u00e9pargner en dollars avant imp\u00f4t pour la retraite via votre employeur. Plusieurs employeurs \u00e9galent vos cotisations\u2014c'est de l'argent gratuit. Cotisez au moins assez pour obtenir la contrepartie compl\u00e8te.",
    StringKey.ADVICE_ACCOUNT_ROTH_IRA_TITLE to "L'avantage du Roth IRA",
    StringKey.ADVICE_ACCOUNT_ROTH_IRA_CONTENT to "Les Roth IRA sont financ\u00e9s avec des dollars apr\u00e8s imp\u00f4t, mais toute la croissance et les retraits \u00e0 la retraite sont libres d'imp\u00f4t. Id\u00e9al si vous pr\u00e9voyez \u00eatre dans une tranche d'imposition plus \u00e9lev\u00e9e plus tard.",
    StringKey.ADVICE_ACCOUNT_TRAD_IRA_TITLE to "L'IRA traditionnel",
    StringKey.ADVICE_ACCOUNT_TRAD_IRA_CONTENT to "Les IRA traditionnels vous donnent une d\u00e9duction fiscale sur les cotisations maintenant, mais vous paierez de l'imp\u00f4t sur les retraits \u00e0 la retraite. Id\u00e9al si vous pr\u00e9voyez \u00eatre dans une tranche d'imposition inf\u00e9rieure plus tard.",
    StringKey.ADVICE_ACCOUNT_HSA_TITLE to "HSA\u00a0: triple avantage fiscal",
    StringKey.ADVICE_ACCOUNT_HSA_CONTENT to "Les Health Savings Accounts offrent un triple avantage fiscal\u00a0: cotisations d\u00e9ductibles, croissance libre d'imp\u00f4t et retraits libres d'imp\u00f4t pour les frais m\u00e9dicaux. Apr\u00e8s 65 ans, il fonctionne comme un IRA traditionnel.",
    StringKey.ADVICE_ENCOURAGE_COMPOUND_TITLE to "La magie des int\u00e9r\u00eats compos\u00e9s",
    StringKey.ADVICE_ENCOURAGE_COMPOUND_CONTENT to "\u00c9pargner 200\u00a0$/mois \u00e0 un rendement annuel de 7\u00a0% atteint plus de 120\u00a0000\u00a0$ en 20 ans\u2014m\u00eame si vous n'avez contribu\u00e9 que 48\u00a0000\u00a0$. Le temps est votre meilleur alli\u00e9!",
    StringKey.ADVICE_ENCOURAGE_SMALL_WINS_TITLE to "Les petites victoires comptent",
    StringKey.ADVICE_ENCOURAGE_SMALL_WINS_CONTENT to "\u00c9pargner seulement 5\u00a0$ par jour repr\u00e9sente 1\u00a0825\u00a0$ par ann\u00e9e. De petites actions constantes cr\u00e9ent de grands r\u00e9sultats avec le temps.",
    StringKey.ADVICE_ENCOURAGE_DOING_GREAT_TITLE to "Vous \u00eates sur la bonne voie!",
    StringKey.ADVICE_ENCOURAGE_DOING_GREAT_CONTENT to "Chaque dollar \u00e9pargn\u00e9 est un pas vers la libert\u00e9 financi\u00e8re. En suivant vos d\u00e9penses et en fixant des objectifs, vous \u00eates d\u00e9j\u00e0 en avance sur la plupart des gens.",
    StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_TITLE to "\u00c9vitez l'inflation du style de vie",
    StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_CONTENT to "Quand vos revenus augmentent, r\u00e9sistez \u00e0 l'envie de tout am\u00e9liorer. Maintenez votre style de vie actuel et dirigez les augmentations vers l'\u00e9pargne.",
    StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_TITLE to "Payez-vous en premier",
    StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_CONTENT to "Traitez l'\u00e9pargne comme une facture\u2014payez-la avant les d\u00e9penses discr\u00e9tionnaires. Vous vous adapterez \u00e0 vivre avec ce qui reste plut\u00f4t que d'\u00e9pargner ce qui reste.",
    StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_TITLE to "D\u00e9sabonnez-vous des tentations",
    StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_CONTENT to "D\u00e9sabonnez-vous des courriels de d\u00e9taillants et cessez de suivre les marques sur les r\u00e9seaux sociaux. R\u00e9duire l'exposition au marketing facilite le respect de votre budget.",

    // Goal category display names (no emoji)
    StringKey.GOAL_DISPLAY_EMERGENCY_FUND to "Fonds d'urgence",
    StringKey.GOAL_DISPLAY_VACATION to "Vacances",
    StringKey.GOAL_DISPLAY_HOME_PURCHASE to "Achat de maison",
    StringKey.GOAL_DISPLAY_RETIREMENT to "Retraite",
    StringKey.GOAL_DISPLAY_EDUCATION to "\u00c9ducation",
    StringKey.GOAL_DISPLAY_MAJOR_PURCHASE to "Achat important",
    StringKey.GOAL_DISPLAY_INVESTMENT to "Investissement",
    StringKey.GOAL_DISPLAY_DEBT_PAYOFF to "Remboursement de dettes",
    StringKey.GOAL_DISPLAY_OTHER to "Autre",

    // Personalized advice cards (frontend)
    StringKey.ADVICE_PERSONALIZED_INSIGHTS to "Conseils personnalis\u00e9s",
    StringKey.ADVICE_BASED_ON_DAYS to "Bas\u00e9 sur %s jours de donn\u00e9es",
    StringKey.ADVICE_ANALYZING_PATTERNS to "Analyse de vos habitudes de d\u00e9penses...",
    StringKey.ADVICE_NO_ADVICE_YET to "Aucun conseil personnalis\u00e9 disponible pour le moment.",
    StringKey.ADVICE_KEEP_TRACKING to "Continuez \u00e0 suivre vos d\u00e9penses pour recevoir des conseils adapt\u00e9s!",
    StringKey.ADVICE_DISMISS to "Masquer",

    // Budget categories
    StringKey.BUDGET_CAT_SALARY to "Salaire (net)",
    StringKey.BUDGET_CAT_TIPS to "Pourboires/Bonis",
    StringKey.BUDGET_CAT_SIDE_HUSTLES to "Revenus secondaires",
    StringKey.BUDGET_CAT_FINANCIAL_AID to "Aide financière",
    StringKey.BUDGET_CAT_INVESTMENTS to "Placements",
    StringKey.BUDGET_CAT_OTHER_INCOME to "Autres revenus",
    StringKey.BUDGET_CAT_RENT_MORTGAGE to "Loyer/Hypothèque",
    StringKey.BUDGET_CAT_GROCERIES to "Épicerie",
    StringKey.BUDGET_CAT_TRANSPORTATION to "Transport",
    StringKey.BUDGET_CAT_UTILITIES to "Services publics",
    StringKey.BUDGET_CAT_CLOTHING to "Vêtements",
    StringKey.BUDGET_CAT_PHARMACY_MEDICAL to "Pharmacie/Médical",
    StringKey.BUDGET_CAT_SUBSCRIPTIONS to "Abonnements",
    StringKey.BUDGET_CAT_SPORTS_FITNESS to "Sports/Conditionnement",
    StringKey.BUDGET_CAT_VACATION to "Vacances",
    StringKey.BUDGET_CAT_GIFTS to "Cadeaux",
    StringKey.BUDGET_CAT_SCHOOL_EDUCATION to "École/Éducation",
    StringKey.BUDGET_CAT_FOOD_DINING_OUT to "Restaurants/Sorties",
    StringKey.BUDGET_CAT_ENTERTAINMENT to "Divertissement",
    StringKey.BUDGET_CAT_INSURANCE to "Assurances",
    StringKey.BUDGET_CAT_PERSONAL_CARE to "Soins personnels",
    StringKey.BUDGET_CAT_MISCELLANEOUS to "Divers",

    // Account staleness / relink
    StringKey.ACCOUNT_RECONNECT_NEEDED to "Reconnexion requise",
    StringKey.ACCOUNT_NO_NEW_DATA_DAYS to "Aucune donnée depuis %s jours",
    StringKey.ACCOUNT_RECONNECT_ACTION to "Reconnecter",
    StringKey.RELINK_FALLBACK_TITLE to "Impossible de mettre à jour cette connexion",
    StringKey.RELINK_FALLBACK_BODY to "Certaines banques exigent l'établissement d'un nouveau lien. Voulez-vous continuer?",
    StringKey.RELINK_FALLBACK_PROCEED to "Oui, continuer",
    StringKey.RELINK_FALLBACK_CANCEL to "Annuler",
    StringKey.CONNECT_FAILED_TITLE to "Connexion impossible",
    StringKey.CONNECT_FAILED_BODY to "L'institution peut être temporairement indisponible, ou la connexion de cette banque est actuellement défectueuse du côté de Plaid (particulièrement courant avec les banques canadiennes). Vous pouvez réessayer plus tard, choisir une autre banque, ou consulter status.plaid.com pour les incidents en cours.",
    StringKey.CONNECT_FAILED_DISMISS to "Compris",

    // Account custom naming
    StringKey.ACCOUNT_RENAME to "Renommer",
    StringKey.ACCOUNT_TYPE_CHEQUING to "Compte chèques",
    StringKey.ACCOUNT_TYPE_SAVINGS to "Épargne",
    StringKey.ACCOUNT_TYPE_CREDIT_CARD to "Carte de crédit",
    StringKey.ACCOUNT_TYPE_TFSA to "CELI",
    StringKey.ACCOUNT_TYPE_RRSP to "REER",
    StringKey.ACCOUNT_TYPE_FHSA to "CELIAPP",
    StringKey.ACCOUNT_TYPE_RESP to "REEE",
    StringKey.ACCOUNT_TYPE_RDSP to "REEI",
    StringKey.ACCOUNT_TYPE_RRIF to "FERR",
    StringKey.ACCOUNT_TYPE_LIRA to "CRI",
    StringKey.ACCOUNT_TYPE_BROKERAGE to "Courtage",
    StringKey.ACCOUNT_TYPE_MORTGAGE to "Hypothèque",
    StringKey.ACCOUNT_TYPE_GIC to "CPG",
    StringKey.ACCOUNT_TYPE_HSA to "HSA",
    StringKey.ACCOUNT_TYPE_MONEY_MARKET to "Marché monétaire",
    StringKey.ACCOUNT_TYPE_CASH_MANAGEMENT to "Gestion de trésorerie",
    StringKey.ACCOUNT_TYPE_401K to "401(k)",
    StringKey.ACCOUNT_TYPE_IRA to "IRA",
    StringKey.ACCOUNT_TYPE_LINE_OF_CREDIT to "Marge de crédit",
    StringKey.ACCOUNT_TYPE_AUTO_LOAN to "Prêt auto",
    StringKey.ACCOUNT_TYPE_STUDENT_LOAN to "Prêt étudiant",
    StringKey.ACCOUNT_TYPE_DEPOSITORY to "Compte bancaire",
    StringKey.ACCOUNT_TYPE_CREDIT to "Crédit",
    StringKey.ACCOUNT_TYPE_LOAN to "Prêt",
    StringKey.ACCOUNT_TYPE_INVESTMENT to "Placement",
    StringKey.ACCOUNT_TYPE_OTHER to "Autre",
    StringKey.ACCOUNT_RENAME_PLACEHOLDER to "Votre nom pour ce compte",
    StringKey.ACCOUNT_RENAME_HINT to "Laissez vide pour utiliser le nom de la banque.",
    StringKey.ACCOUNT_RENAME_DUPLICATE to "Vous avez déjà un compte nommé « %s ».",
    StringKey.ACCOUNT_RENAME_TOO_LONG to "Un nom de compte peut contenir au plus %s caractères.",
    StringKey.ACCOUNT_RENAME_NOT_FOUND to "Ce compte ne peut pas être renommé.",
    StringKey.ACCOUNT_RENAME_FORBIDDEN to "Vous n'êtes pas autorisé à renommer ce compte.",

    // Activity screen strings
    StringKey.ACTIVITY_NO_ACCOUNTS to "Aucun compte connecté",
    StringKey.ACTIVITY_ACCOUNTS to "Comptes",
    StringKey.ACTIVITY_RECENT_ACTIVITY to "Activité récente",
    StringKey.ACTIVITY_VIEW_ALL to "Voir tout →",
    StringKey.ACTIVITY_GOAL to "Objectif",
    StringKey.ACTIVITY_THIS_MONTH to "Ce mois-ci",
    StringKey.ACTIVITY_TOP_CATEGORIES to "Catégories principales",
    StringKey.ACTIVITY_ACCOUNT to "Compte",
    StringKey.ACTIVITY_TIP_SEE_MORE to "Voir plus de conseils dans la page Objectifs",
    StringKey.ACTIVITY_DEMO_ACCOUNT to "Compte démo",
    StringKey.ACTIVITY_UNKNOWN to "Inconnu",
    StringKey.ACTIVITY_ERROR_LOADING to "Erreur de chargement",
    StringKey.ACTIVITY_FETCHING to "Récupération de %s...",
    StringKey.ACTIVITY_LAST_UPDATED_JUST_NOW to "Dernière mise à jour de %s : à l'instant",
    StringKey.ACTIVITY_LAST_UPDATED_ONE_MINUTE to "Dernière mise à jour de %s : il y a 1 minute",
    StringKey.ACTIVITY_LAST_UPDATED_MINUTES to "Dernière mise à jour de %s : il y a %s minutes",
    StringKey.ACTIVITY_LAST_UPDATED_ONE_HOUR to "Dernière mise à jour de %s : il y a 1 heure",
    StringKey.ACTIVITY_LAST_UPDATED_HOURS to "Dernière mise à jour de %s : il y a %s heures",
    StringKey.ACTIVITY_SYNC to "↻ Synchroniser",
    StringKey.ACTIVITY_SYNCING to "⏳ Synchronisation...",
    StringKey.ACTIVITY_AVAILABLE_BALANCE to "%s • Solde disponible",
    StringKey.ACTIVITY_FILTERED_BY to "Filtré par :",
    StringKey.ACTIVITY_EXPENSES to "Dépenses",
    StringKey.ACTIVITY_NET to "Net",
    StringKey.ACTIVITY_VERIFY_CATEGORY to "Vérifier la catégorie",

    // Confidence labels
    StringKey.CONFIDENCE_AI to "IA : %s%",
    StringKey.CONFIDENCE_USER to "Moi : %s%",

    // Chart visibility states
    StringKey.CHART_VISIBILITY_OPEN to "Ouvert",
    StringKey.CHART_VISIBILITY_CLOSED to "Fermé",
    StringKey.CHART_VISIBILITY_HIDDEN to "Masqué",

    // Settings section headers
    StringKey.SETTINGS_SECTION_PROFILE to "Profil",
    StringKey.SETTINGS_SECTION_SAVINGS_GOAL to "Objectif d'épargne",
    StringKey.SETTINGS_SECTION_CHARTS to "Graphiques du tableau de bord",
    StringKey.SETTINGS_SECTION_PREFERENCES to "Préférences",
    StringKey.SETTINGS_SECTION_SUPPORT to "Assistance",
    StringKey.SETTINGS_SECTION_FINANCIAL to "Paramètres financiers",

    // Auth screen
    StringKey.AUTH_USERNAME to "Nom d'utilisateur",
    StringKey.AUTH_USERNAME_PLACEHOLDER to "Entrez votre nom d'utilisateur",
    StringKey.AUTH_EMAIL_PLACEHOLDER to "vous@exemple.com",
    StringKey.AUTH_PASSWORD_PLACEHOLDER to "Entrez votre mot de passe",
    StringKey.AUTH_OR_SEPARATOR to "ou",

    // Budget / Charts screen labels
    StringKey.CHART_HIGHEST_SPENDING to "Dépenses les plus élevées",
    StringKey.CHART_AVERAGE_PER_CATEGORY to "Moyenne par catégorie",
    StringKey.CHART_SPENDING_PATTERN to "Tendance des dépenses",
    StringKey.BUDGET_SECTION_SUMMARY to "Résumé",

    // Transaction detail labels
    StringKey.TRANSACTION_LABEL_SUBCATEGORY to "Sous-catégorie",
    StringKey.TRANSACTION_MANUAL_ENTRY to "Saisie manuelle",
    StringKey.TRANSACTION_CSV_IMPORT to "Importation CSV",
    StringKey.TRANSACTION_SAVE_SPLIT_ACTION to "Enregistrer la répartition",
    StringKey.TRANSACTION_NOTE_PLACEHOLDER to "Appuyez pour ajouter une note...",
    StringKey.TRANSACTION_ASSIGN_GOAL to "Assigner à un objectif",
    StringKey.TRANSACTION_REMOVE_GOAL to "Aucun",
    StringKey.TRANSACTION_CHANGE_CATEGORY_DESC to "Recatégoriser cette transaction",
    StringKey.TRANSACTION_ASSIGN_GOAL_DESC to "Suivre la progression vers un objectif d'épargne",
    StringKey.TRANSACTION_SPLIT_DESC to "Diviser le montant en plusieurs catégories",
    StringKey.TRANSACTION_DISTRIBUTE_DESC to "Répartir sur plusieurs mois ou déplacer à une autre date",
    StringKey.TRANSACTION_UNSPLIT_DESC to "Annuler la division et restaurer l'original",
    StringKey.TRANSACTION_UNDISTRIBUTE_DESC to "Annuler la répartition et restaurer l'original",
    StringKey.TRANSACTION_EXCLUDE to "Exclure la transaction",
    StringKey.TRANSACTION_EXCLUDE_DESC to "Exclure ce montant des calculs de revenus et dépenses",
    StringKey.TRANSACTION_INCLUDE to "Inclure la transaction",
    StringKey.TRANSACTION_INCLUDE_DESC to "Réintégrer ce montant dans les calculs de revenus et dépenses",
    StringKey.TRANSACTION_EXCLUDED_BADGE to "Exclue des calculs",
    StringKey.TRANSACTION_EXCLUDED_SHORT to "Exclue",
    StringKey.TRANSACTION_AUTO_EXCLUDED_SHORT to "Exclue (auto)",
    StringKey.TRANSACTION_EXCLUDE_SPLIT_WARNING to "Cette transaction a une division ou distribution active. L'exclure supprimera la division/distribution. Continuer ?",
    StringKey.CATEGORY_APPLY_ALL_TITLE to "Aussi mettre à jour les transactions similaires ?",
    StringKey.CATEGORY_APPLY_ALL to "Appliquer à toutes les similaires",
    StringKey.CATEGORY_APPLY_JUST_THIS to "Seulement cette transaction",
    StringKey.CATEGORY_APPLY_ALL_NOTE to "S'applique uniquement aux transactions non modifiées par vous",
    StringKey.CATEGORY_CHOOSE to "Choisir une catégorie",
    StringKey.CATEGORY_ALL_CATEGORIES to "Toutes les catégories",
    StringKey.CATEGORY_ALL_SUBCATEGORIES to "Toutes les sous-catégories",
    StringKey.CATEGORY_CHOOSE_SUBCATEGORY to "Choisir une sous-catégorie :",
    StringKey.CATEGORY_ADD_BUTTON to "(+ ajouter)",
    StringKey.CATEGORY_ADD_SUBCATEGORY to "Ajouter une sous-catégorie",
    StringKey.CATEGORY_EXPENSE_CATEGORIES to "Catégories de dépenses",
    StringKey.CATEGORY_ADD_PRIMARY_DESC to "Ajouter une nouvelle catégorie principale. Vous pourrez ensuite y ajouter des sous-catégories.",
    StringKey.CATEGORY_PREVIEW_TITLE to "Transactions qui seront mises \u00e0 jour",
    StringKey.CATEGORY_PREVIEW_CONFIRM to "Appliquer \u00e0 tous (%s)",
    StringKey.CATEGORY_PREVIEW_SELECT_ALL to "Tout s\u00e9lectionner",
    StringKey.CATEGORY_PREVIEW_DESELECT_ALL to "Tout d\u00e9s\u00e9lectionner",
    StringKey.CATEGORY_PREVIEW_EMPTY to "Aucune autre transaction correspondante",
    StringKey.CATEGORY_PREVIEW_LOADING to "Chargement de l'aper\u00e7u\u2026",
    StringKey.CATEGORY_MODIFY to "Modifier",
    StringKey.CATEGORY_ADD_PRIMARY to "Ajouter une cat\u00e9gorie principale",
    StringKey.CATEGORY_ADD_SUB_TITLE to "Ajouter une sous-cat\u00e9gorie",
    StringKey.CATEGORY_EDIT_SUB_TITLE to "Modifier la sous-cat\u00e9gorie",
    StringKey.CATEGORY_UNDER_PRIMARY to "sous %s",
    StringKey.CATEGORY_SUB_NAME_PLACEHOLDER to "Nom de la sous-cat\u00e9gorie",
    StringKey.CATEGORY_PRIMARY_NAME_LABEL to "Nom principal",
    StringKey.CATEGORY_PRIMARY_NAME_PLACEHOLDER to "Nom principal (ex. Loisirs)",
    StringKey.CATEGORY_FIRST_SUB_PLACEHOLDER to "Premi\u00e8re sous-cat\u00e9gorie (ex. Jeux de soci\u00e9t\u00e9)",
    StringKey.CATEGORY_COLOUR to "Couleur",
    StringKey.CATEGORY_CHOOSE_ICON to "Choisir une ic\u00f4ne",
    StringKey.CATEGORY_SUBCATEGORIES_LABEL to "Sous-cat\u00e9gories",
    StringKey.CATEGORY_FIRST_SUB_ICON_LABEL to "Ic\u00f4ne (utilis\u00e9e pour la cat\u00e9gorie principale et sa premi\u00e8re sous-cat\u00e9gorie)",
    StringKey.CATEGORY_DELETE_TITLE to "Supprimer \u00ab %s \u00bb ?",
    StringKey.CATEGORY_DELETE_USAGE_NONE to "Aucune transaction n'utilise cette sous-cat\u00e9gorie.",
    StringKey.CATEGORY_DELETE_USAGE_ONE to "1 transaction utilise cette sous-cat\u00e9gorie.",
    StringKey.CATEGORY_DELETE_USAGE_MANY to "%s transactions utilisent cette sous-cat\u00e9gorie.",
    StringKey.CATEGORY_DELETE_MOVE_TO to "D\u00e9placer les transactions affect\u00e9es vers\u00a0:",
    StringKey.CATEGORY_DELETE_REMOVE_OVERRIDE to "Retirer la surcharge \u2014 laisser l'IA recat\u00e9goriser",
    StringKey.CATEGORY_DELETE_AFFECTED_HEADER to "Transactions affect\u00e9es\u00a0:",
    StringKey.CATEGORY_DELETE_AFFECTED_HEADER_COUNT to "Transactions affect\u00e9es (affichage de %s sur %s)\u00a0:",
    StringKey.CATEGORY_DELETE_CASCADE_NOTE to "Il s'agit de la derni\u00e8re sous-cat\u00e9gorie de \u00ab %s \u00bb. La supprimer retirera aussi la cat\u00e9gorie principale.",

    // Contextual tour UI
    StringKey.TOUR_OPEN_TOOLTIP to "Afficher un survol rapide de cet \u00e9cran",
    StringKey.TOUR_BACK to "Pr\u00e9c\u00e9dent",
    StringKey.TOUR_NEXT to "Suivant",
    StringKey.TOUR_DONE to "Termin\u00e9",
    StringKey.TOUR_CLOSE to "Fermer",
    StringKey.TOUR_STEP_OF to "%s / %s",
    // Transactions-screen tour
    StringKey.TOUR_TXN_NAV_TITLE to "Naviguer dans l'app",
    StringKey.TOUR_TXN_NAV_BODY to "Vous \u00eates dans Activit\u00e9. Basculez vers Tableau de bord, Objectifs ou Budget \u00e0 tout moment.",
    StringKey.TOUR_TXN_SYNC_TITLE to "Synchroniser les transactions",
    StringKey.TOUR_TXN_SYNC_BODY to "R\u00e9cup\u00e9rez les derni\u00e8res transactions de votre banque.",
    StringKey.TOUR_TXN_ACCOUNTS_TITLE to "Comptes et contextes",
    StringKey.TOUR_TXN_ACCOUNTS_BODY to "Touchez un compte pour limiter la liste \u00e0 ses transactions. Les contextes (Pro) permettent d'\u00e9tiqueter et regrouper des transactions entre comptes.",
    StringKey.TOUR_TXN_ROW_TITLE to "D\u00e9tails de la transaction",
    StringKey.TOUR_TXN_ROW_BODY to "Touchez une ligne pour voir les d\u00e9tails, changer la cat\u00e9gorie, diviser la transaction ou l'assigner \u00e0 un objectif.",
    StringKey.TOUR_TXN_MULTISELECT_TITLE to "S\u00e9lection multiple pour actions en lot",
    StringKey.TOUR_TXN_MULTISELECT_BODY to "Touchez l'ic\u00f4ne pour s\u00e9lectionner plusieurs transactions. Une barre d'actions appara\u00eet \u2014 exclure, recat\u00e9goriser ou assigner \u00e0 un objectif en un seul geste.",
    StringKey.TOUR_TXN_AI_TITLE to "Cat\u00e9gorisation par IA",
    StringKey.TOUR_TXN_AI_BODY to "Nous cat\u00e9gorisons automatiquement gr\u00e2ce \u00e0 l'IA. Touchez le \u2713 pour confirmer ; cela entra\u00eene le syst\u00e8me pour les prochaines fois.",
    StringKey.TOUR_TXN_MODIFY_TITLE to "Personnaliser les cat\u00e9gories",
    StringKey.TOUR_TXN_MODIFY_BODY to "Touchez Modifier dans le s\u00e9lecteur de cat\u00e9gories pour recolorer toute cat\u00e9gorie, et ajouter ou renommer les v\u00f4tres.",
    // Dashboard-screen tour
    StringKey.TOUR_DASH_SAVINGS_RATE_TITLE to "Taux d'\u00e9pargne",
    StringKey.TOUR_DASH_SAVINGS_RATE_BODY to "Votre taux d'\u00e9pargne pour le mois choisi, ou une moyenne annuelle. Cliquez une barre de mois pour vous concentrer dessus.",
    StringKey.TOUR_DASH_MONTHLY_BARS_TITLE to "Vue mois par mois",
    StringKey.TOUR_DASH_MONTHLY_BARS_BODY to "Chaque mois d'un coup d'\u0153il \u2014 revenus, d\u00e9penses et ce qui reste. Survolez ou cliquez une barre pour les d\u00e9tails.",
    StringKey.TOUR_DASH_ACCOUNTS_TITLE to "Filtrer par compte",
    StringKey.TOUR_DASH_ACCOUNTS_BODY to "Filtrez tous les graphiques par compte. D\u00e9cochez-en un pour l'exclure des calculs.",
    StringKey.TOUR_DASH_CHART_TYPE_TITLE to "Choisir un graphique",
    StringKey.TOUR_DASH_CHART_TYPE_BODY to "Basculez entre Tendance d'\u00e9pargne, Camembert des d\u00e9penses, Histogramme, Chronologie et Comparaison d'ann\u00e9es.",
    StringKey.TOUR_DASH_ACTIVE_CHART_TITLE to "Graphique actif",
    StringKey.TOUR_DASH_ACTIVE_CHART_BODY to "Le graphique que vous choisissez s'affiche ici. S\u00e9lectionnez un autre type de graphique dans le panneau lat\u00e9ral pour changer ce qui est montr\u00e9.",
    StringKey.TOUR_DASH_GRID_CANVAS_TITLE to "D\u00e9placer et redimensionner vos widgets",
    StringKey.TOUR_DASH_GRID_CANVAS_BODY to "Glissez n'importe quel widget par sa barre de titre pour le d\u00e9placer. Tirez une ar\u00eate ou un coin pour le redimensionner. Les widgets g\u00eanants se tasseront pour faire de la place. Cliquez \u00d7 pour retirer un widget \u2014 il retourne dans la palette.",
    StringKey.TOUR_DASH_CUSTOMIZE_TITLE to "La barre Personnaliser",
    StringKey.TOUR_DASH_CUSTOMIZE_BODY to "Cliquez ici pour ouvrir la barre. Vous pouvez y ajouter ou retirer des widgets, sauvegarder l'arrangement actuel comme mise en page nomm\u00e9e, et verrouiller une mise en page pour \u00e9viter les modifications accidentelles.",
    StringKey.TOUR_DASH_LAYOUTS_SAVE_TITLE to "Sauvegardez vos mises en page",
    StringKey.TOUR_DASH_LAYOUTS_SAVE_BODY to "Sauvegardez l'arrangement actuel comme mise en page nomm\u00e9e. Les membres Pro peuvent en garder plusieurs \u2014 une pour la revue de fin de mois, une autre pour les v\u00e9rifications quotidiennes, et basculer entre elles en un clic.",
    StringKey.TOUR_DASH_LAYOUTS_SWITCH_TITLE to "Basculer en un clic",
    StringKey.TOUR_DASH_LAYOUTS_SWITCH_BODY to "Les mises en page \u00e9pingl\u00e9es apparaissent ici sous forme de puces. Cliquez l'une d'elles pour basculer tout l'arrangement instantan\u00e9ment. Id\u00e9al pour passer d'une v\u00e9rification KPI rapide \u00e0 une revue approfondie de fin de mois.",
    StringKey.TOUR_DASH_LAYOUT_LOCK_TITLE to "Verrouiller une mise en page",
    StringKey.TOUR_DASH_LAYOUT_LOCK_BODY to "Cliquez sur l'ic\u00f4ne cadenas pour figer une mise en page \u2014 les widgets ne sont plus d\u00e9pla\u00e7ables ni redimensionnables tant que vous ne d\u00e9verrouillez pas. Utile une fois que vous avez un arrangement \u00e0 ne pas perturber par accident.",
    StringKey.TOUR_DASH_SAVINGS_TREND_TITLE to "Votre taux d'\u00e9pargne dans le temps",
    StringKey.TOUR_DASH_SAVINGS_TREND_BODY to "Chaque point est le taux d'\u00e9pargne effectif d'un mois. La ligne pointill\u00e9e est votre cible \u2014 suivez la tendance, voyez quand vous prenez de l'avance ou perdez du terrain.",
    StringKey.TOUR_DASH_EXPENSE_TIMELINE_TITLE to "Vos d\u00e9penses mensuelles d'un coup d'\u0153il",
    StringKey.TOUR_DASH_EXPENSE_TIMELINE_BODY to "Vos d\u00e9penses totales chaque mois, trac\u00e9es sur les 12 derniers mois. Les pics mettent en \u00e9vidence les mois o\u00f9 quelque chose d'inhabituel s'est pass\u00e9 \u2014 vacances, grosse facture, achat ponctuel.",

    // Tour du Studio d'allocation
    StringKey.TOUR_STUDIO_HEADER_TITLE to "Le Studio d'allocation",
    StringKey.TOUR_STUDIO_HEADER_BODY to "C'est ici que vous planifiez un objectif en d\u00e9tail. Le titre montre le nom de l'objectif et la date cible \u2014 changez la date directement ici. \u270e Modifier le profil relance le court questionnaire pour les hypoth\u00e8ses qui pilotent chaque projection.",
    StringKey.TOUR_STUDIO_PROGRESS_TITLE to "Progression vers votre cible",
    StringKey.TOUR_STUDIO_PROGRESS_BODY to "\u00c0 quel point vous \u00eates pr\u00e8s du montant cible aujourd'hui. Se met \u00e0 jour quand vous bougez les curseurs ci-dessous ou quand de nouvelles contributions arrivent dans le compte li\u00e9.",
    StringKey.TOUR_STUDIO_STRIP_TITLE to "Comparer les types de compte",
    StringKey.TOUR_STUDIO_STRIP_BODY to "Chaque carte est un type de compte (CELI, REER, CELIAPP, non enregistr\u00e9, etc.) projet\u00e9 jusqu'\u00e0 votre date cible. Cliquez une carte pour \u00e9pingler ce v\u00e9hicule \u2014 le graphique et le taux d'\u00e9pargne plus bas se recalculent en fonction.",
    StringKey.TOUR_STUDIO_FORM_TITLE to "Ajuster les hypoth\u00e8ses",
    StringKey.TOUR_STUDIO_FORM_BODY to "Curseurs pour le montant cible, la dur\u00e9e d'\u00e9pargne, votre contribution mensuelle, et le taux de rendement. Chaque changement reprojete le graphique et met \u00e0 jour les cartes de v\u00e9hicules au-dessus. Rien n'est sauvegard\u00e9 tant que vous ne cliquez pas Sauvegarder en bas.",
    StringKey.TOUR_STUDIO_CHART_TITLE to "Croissance projet\u00e9e",
    StringKey.TOUR_STUDIO_CHART_BODY to "Votre solde dans le temps selon les hypoth\u00e8ses du v\u00e9hicule \u00e9pingl\u00e9 \u2014 contributions de base, croissance par les rendements, et la marge d'\u00e9pargne suppl\u00e9mentaire estim\u00e9e dans le temps selon vos informations actuelles.",
    StringKey.TOUR_STUDIO_SAVINGS_RATE_TITLE to "Impact sur le taux d'\u00e9pargne",
    StringKey.TOUR_STUDIO_SAVINGS_RATE_BODY to "Comment la contribution mensuelle de cet objectif affecte votre taux d'\u00e9pargne global. La barre s\u00e9pare ce qui sort de votre poche de la bonification quand les comptes enregistr\u00e9s (REER, CELIAPP) r\u00e9duisent votre facture d'imp\u00f4t \u2014 ensemble, c'est votre taux d'\u00e9pargne effectif. Un avertissement appara\u00eet si un curseur pousse le taux au-dessus de ce que vos revenus permettent.",
    StringKey.TOUR_STUDIO_FOOTER_TITLE to "Sauvegarder ou annuler",
    StringKey.TOUR_STUDIO_FOOTER_BODY to "Sauvegarder enregistre les nouvelles hypoth\u00e8ses dans l'objectif et le tableau de bord les reprend. Annuler abandonne tous les changements depuis l'ouverture du Studio.",

    // Tour du Studio de remboursement de dette
    StringKey.TOUR_DEBT_STUDIO_HEADER_TITLE to "Le Studio de remboursement de dette",
    StringKey.TOUR_DEBT_STUDIO_HEADER_BODY to "C'est ici que vous planifiez le remboursement d'une dette \u2014 hypoth\u00e8que, marge de cr\u00e9dit, pr\u00eat \u00e9tudiant, carte de cr\u00e9dit, n'importe quoi que vous devez. Le nom et le solde affich\u00e9s se synchronisent avec ce qui est configur\u00e9 dans la page Objectifs.",
    StringKey.TOUR_DEBT_STUDIO_GOAL_TITLE to "Conditions du pr\u00eat",
    StringKey.TOUR_DEBT_STUDIO_GOAL_BODY to "Les quatre chiffres qui d\u00e9finissent le pr\u00eat : nom, solde actuel, taux d'int\u00e9r\u00eat, et dur\u00e9e restante en mois. Modifiez-en n'importe lequel \u2014 les projections se recalculent instantan\u00e9ment. La priorit\u00e9 contr\u00f4le l'agressivit\u00e9 avec laquelle le tableau de bord sugg\u00e8re de r\u00e9allouer de l'argent vers cet objectif.",
    StringKey.TOUR_DEBT_STUDIO_STRIP_TITLE to "Comparer deux plans c\u00f4te \u00e0 c\u00f4te",
    StringKey.TOUR_DEBT_STUDIO_STRIP_BODY to "Plan A vs Plan B c\u00f4te \u00e0 c\u00f4te : date de remboursement, int\u00e9r\u00eats totaux, co\u00fbt total. L'\u00e9cart vert indique combien plus t\u00f4t vous serez libre de dette et combien d'int\u00e9r\u00eats vous \u00e9conomiserez par rapport \u00e0 l'autre plan. Cliquer une carte (ou sa case \u00e0 cocher) confirme ce plan comme strat\u00e9gie de l'objectif \u2014 il n'y a pas d'\u00e9tape de sauvegarde s\u00e9par\u00e9e.",
    StringKey.TOUR_DEBT_STUDIO_LEVERS_TITLE to "Les leviers des deux plans",
    StringKey.TOUR_DEBT_STUDIO_LEVERS_BODY to "Ajustez chaque plan ind\u00e9pendamment : fr\u00e9quence de paiement (mensuelle, semi-mensuelle, bimensuelle acc\u00e9l\u00e9r\u00e9e), montant du paiement, et tout versement forfaitaire ponctuel. La bimensuelle acc\u00e9l\u00e9r\u00e9e est le moyen discret le plus facile de finir des ann\u00e9es plus t\u00f4t \u2014 essayez-la.",
    StringKey.TOUR_DEBT_STUDIO_CHART_TITLE to "Voir le solde fondre",
    StringKey.TOUR_DEBT_STUDIO_CHART_BODY to "Votre solde restant dans le temps selon chaque plan vs l'\u00e9ch\u00e9ancier actuel. Les lignes qui convergent le plus vite sont les plans qui vous \u00e9conomisent le plus d'int\u00e9r\u00eats \u2014 l'\u00e9cart entre elles \u00e0 l'ann\u00e9e 5 ou 10, c'est de l'argent r\u00e9el dans votre poche.",
    StringKey.TOUR_DEBT_STUDIO_FOOTER_TITLE to "Comment la sauvegarde fonctionne ici",
    StringKey.TOUR_DEBT_STUDIO_FOOTER_BODY to "En mode \u00e9dition, seul un bouton Supprimer se trouve ici \u2014 vos donn\u00e9es d'objectif se sauvegardent automatiquement \u00e0 la sortie de chaque champ, et le plan que vous avez confirm\u00e9 plus haut demeure la strat\u00e9gie de l'objectif. En mode cr\u00e9ation, cette zone affiche plut\u00f4t Sauvegarder / Annuler, qui valident (ou abandonnent) ensemble le nouvel objectif et son plan s\u00e9lectionn\u00e9.",
    StringKey.TOUR_DASH_RECOMMENDATION_TITLE to "Suggestions d'\u00e9pargne",
    StringKey.TOUR_DASH_RECOMMENDATION_BODY to "Votre marge d'\u00e9pargne mensuelle, plus des objectifs sugg\u00e9r\u00e9s comme un fonds d'urgence ou des vacances.",
    StringKey.TOUR_DASH_GRID_HINT_TITLE to "Envie d'une mise en page personnalis\u00e9e\u00a0?",
    StringKey.TOUR_DASH_GRID_HINT_BODY to "Les membres Pro peuvent passer au mode Grille : glissez, redimensionnez, ajoutez ou retirez des widgets pour b\u00e2tir votre propre tableau de bord. Activez-le dans R\u00e9glages \u2192 Mode tableau de bord.",

    StringKey.TOUR_GOALS_ADD_TITLE to "Cr\u00e9er un objectif",
    StringKey.TOUR_GOALS_ADD_BODY to "Cliquez ici pour d\u00e9finir un objectif d'\u00e9pargne : nommez-le, choisissez un montant cible et une \u00e9ch\u00e9ance, et nous suivrons votre progression.",
    StringKey.TOUR_GOALS_CARD_TITLE to "Votre objectif en un coup d'\u0153il",
    StringKey.TOUR_GOALS_CARD_BODY to "Chaque carte montre la progression vers votre cible, l'\u00e9ch\u00e9ance, et combien il vous reste \u00e0 \u00e9pargner. Cliquez une carte pour ouvrir le Studio d'allocation \u2014 il y a une visite \u00ab i \u00bb d\u00e9di\u00e9e \u00e0 l'int\u00e9rieur qui pr\u00e9sente les outils de projection.",
    StringKey.TOUR_GOALS_INSIGHTS_TITLE to "Suggestions personnalis\u00e9es",
    StringKey.TOUR_GOALS_INSIGHTS_BODY to "Des conseils g\u00e9n\u00e9r\u00e9s par IA, adapt\u00e9s \u00e0 vos d\u00e9penses et \u00e0 vos objectifs. Ils se rafra\u00eechissent au fil de vos donn\u00e9es.",
    StringKey.TOUR_GOALS_INSIGHTS_BODY_WIZ3R to "Conseils personnalis\u00e9s par Wiz 3R, organis\u00e9s en R\u00e9v\u00e8le \u00b7 Raisonne \u00b7 R\u00e9alise. R\u00e9v\u00e8le met en lumi\u00e8re une tendance dans vos donn\u00e9es, Raisonne explique le calcul qui la sous-tend, et R\u00e9alise vous laisse d\u00e9cider de la suite. Cliquez sur une carte pour voir le d\u00e9tail complet ou poser une question de suivi \u00e0 Wiz 3R.",
    StringKey.TOUR_GOALS_TIPS_SEARCH_TITLE to "Rechercher des conseils",
    StringKey.TOUR_GOALS_TIPS_SEARCH_BODY to "Parcourez notre biblioth\u00e8que de conseils financiers. Tapez un mot-cl\u00e9 ou une cat\u00e9gorie pour trouver ce qui vous concerne.",
    StringKey.TOUR_GOALS_PIN_TITLE to "\u00c9pinglez vos favoris",
    StringKey.TOUR_GOALS_PIN_BODY to "Cliquez sur l'\u00e9pingle pour garder un conseil en haut de la page, pratique d\u00e8s que vous en avez besoin.",

    StringKey.TOUR_BUDGET_YEAR_TITLE to "Choisissez l'ann\u00e9e",
    StringKey.TOUR_BUDGET_YEAR_BODY to "Planifiez votre budget pour n'importe quelle ann\u00e9e : pass\u00e9e, actuelle ou \u00e0 venir. Changez ici \u00e0 tout moment.",
    StringKey.TOUR_BUDGET_INCOME_TITLE to "Revenus",
    StringKey.TOUR_BUDGET_INCOME_BODY to "Vos revenus pr\u00e9vus pour chaque mois, par cat\u00e9gorie. Les totaux se mettent \u00e0 jour \u00e0 mesure que vous saisissez.",
    StringKey.TOUR_BUDGET_EXPENSE_TITLE to "D\u00e9penses",
    StringKey.TOUR_BUDGET_EXPENSE_BODY to "Vos d\u00e9penses planifi\u00e9es, organis\u00e9es par cat\u00e9gorie. Cliquez une ligne pour l'\u00e9tendre et r\u00e9gler les sous-cat\u00e9gories.",
    StringKey.TOUR_BUDGET_CELL_TITLE to "Une cellule de budget",
    StringKey.TOUR_BUDGET_CELL_BODY to "\u00c9dition directe + Alt+\u2192 pour recopier rapidement au mois suivant. Gain de temps quand un montant se r\u00e9p\u00e8te.",
    StringKey.TOUR_BUDGET_COPY_YEAR_TITLE to "Partir de l'ann\u00e9e pass\u00e9e",
    StringKey.TOUR_BUDGET_COPY_YEAR_BODY to "Un clic recopie le budget de l'an dernier dans l'ann\u00e9e courante. Ajustez ensuite seulement ce qui a chang\u00e9.",
    StringKey.TOUR_BUDGET_NET_TITLE to "\u00c9pargne nette et taux",
    StringKey.TOUR_BUDGET_NET_BODY to "Revenus moins d\u00e9penses, par mois et par ann\u00e9e. Voyez imm\u00e9diatement l'effet de vos choix budg\u00e9taires sur votre taux d'\u00e9pargne.",

    // AI categorization
    StringKey.AI_CATEGORIZING to "Catégorisation par IA...",
    StringKey.AI_PARSE_AND_CATEGORIZE to "Analyser et catégoriser",
    StringKey.CATEGORY_ASSIGNING to "Attribution...",

    // Goals / Recommendations
    StringKey.GOALS_PERCENT_FUNDED to "% financé",
    StringKey.GOALS_REDUCE_BY to "Réduire de",
    StringKey.GOALS_INTEREST_COMPOUND to "Composé",
    StringKey.GOALS_INTEREST_SIMPLE to "Simple",
    StringKey.GOALS_SPLIT_ACROSS to "Répartir entre les objectifs :",
    StringKey.GOALS_DELETE_CONFIRM to "Êtes-vous sûr de vouloir supprimer \"%s\" ? Cette action est irréversible.",

    // Chart insight titles
    StringKey.CHART_TOTAL_EXPENSES to "Dépenses totales",

    // Settings — linked banks / unlink
    StringKey.SETTINGS_SAVINGS_TARGET_HELP to "Définissez votre objectif d'épargne mensuel. Le 20\u0025 par défaut provient de la règle budgétaire 50/30/20.",
    StringKey.SETTINGS_CHARTS_REORDER_HELP to "Glissez pour réorganiser les graphiques. Contrôlez l'état d'affichage par défaut de chaque graphique sur le tableau de bord.",
    StringKey.SETTINGS_LINKED_BANKS to "Banques liées",
    StringKey.SETTINGS_LINKED_BANKS_ERROR to "Impossible de charger les banques liées. Veuillez réessayer plus tard.",
    StringKey.SETTINGS_NO_BANKS_LINKED to "Aucune banque liée.",
    StringKey.SETTINGS_ACCOUNTS_COUNT to "compte(s)",
    StringKey.SETTINGS_UNLINK to "Délier",
    StringKey.SETTINGS_UNLINK_CONFIRM_TITLE to "Délier %s ?",
    StringKey.SETTINGS_UNLINK_WARNING to "Délier retire les comptes de cette banque de votre tableau de bord. Si vous reliez à nouveau la même banque plus tard, votre historique sera restauré automatiquement.",
    StringKey.SETTINGS_BIOMETRIC_UNLOCK to "Déverrouiller avec %s",
    StringKey.SETTINGS_RATE_APP to "Évaluer l'application",
    StringKey.SETTINGS_RATE_APP_DESC to "Vous aimez wiz\u2261r ? Laissez un avis",
    StringKey.SETTINGS_BIOMETRIC_TITLE to "Déverrouillage biométrique",
    StringKey.SETTINGS_PRIVACY_DESC to "Comment nous collectons, utilisons et protégeons vos données",
    StringKey.SETTINGS_SECURITY_DESC to "Comment nous protégeons vos données et comptes",
    StringKey.SETTINGS_GUIDE_DESC to "Guide étape par étape pour profiter au maximum de l'application",
    StringKey.SETTINGS_UNLINK_ACCOUNTS_REMOVED to "Comptes qui seront supprimés :",
    StringKey.SETTINGS_ABOUT_APP to "À propos",

    // About page
    StringKey.ABOUT_TITLE to "À propos de wiz\u2261r",
    StringKey.ABOUT_MISSION_TITLE to "Notre mission",
    StringKey.ABOUT_MISSION to "Rendre les finances personnelles si simples que tout le monde peut les maîtriser.",
    StringKey.ABOUT_PHILOSOPHY_TITLE to "La philosophie du chiffre unique",
    StringKey.ABOUT_PHILOSOPHY_INTRO to "Au cœur de wiz\u2261r, une idée simple : votre taux d'épargne est le seul chiffre qui compte.",
    StringKey.ABOUT_SAVINGS_RATE_FORMULA to "Votre taux d'épargne = Argent transféré vers vos objectifs \u00F7 Revenu",
    StringKey.ABOUT_SAVINGS_RATE_EXPLANATION to "Si ce n'est pas alloué à un objectif, ça sera dépensé. Aussi simple que ça.<br><strong>Fixez des objectifs. Transférez vers ceux-ci.</strong><br>En vous concentrant sur ce seul chiffre, tout le reste\u2014budget, décisions de dépenses, planification à long terme\u2014devient plus clair et plus simple.",
    StringKey.ABOUT_QUOTE to "Concentrez-vous sur un seul chiffre.\nRegardez votre patrimoine grandir.",
    StringKey.ABOUT_HOW_HELPS_TITLE to "Comment wiz\u2261r vous aide",
    StringKey.ABOUT_HOW_HELPS_INTRO to "wiz\u2261r se connecte automatiquement à vos comptes bancaires et cartes de crédit, calcule votre taux d'épargne et vous aide à l'améliorer grâce à :",
    StringKey.ABOUT_HELP_VISIBILITY to "Votre taux d'épargne montré en premier plan, mis à jour en temps réel",
    StringKey.ABOUT_HELP_INSIGHTS to "La catégorisation par IA qui montre exactement où va votre argent",
    StringKey.ABOUT_HELP_HABITS to "Des conseils quotidiens qui vous aident à bâtir de meilleures habitudes financières, un petit pas à la fois",
    StringKey.ABOUT_HELP_GOALS to "Des objectifs d'épargne dont vous suivez le progrès au fil du temps",
    StringKey.ABOUT_STORY_TITLE to "Notre histoire",
    StringKey.ABOUT_STORY_P1 to "wiz\u2261r est né d'une frustration. Après avoir essayé des dizaines d'applications de budget, de tableurs et d'outils financiers, nous avons réalisé qu'ils partageaient tous le même problème : ils rendaient les choses simples compliquées.",
    StringKey.ABOUT_STORY_P2 to "Nous nous sommes demandé : et si nous construisions une application qui se concentrait sur une seule chose\u2014aider les gens à épargner davantage ? Et si nous éliminions toute la complexité et le bruit pour créer quelque chose de si simple que tout le monde pourrait l'utiliser ?",
    StringKey.ABOUT_STORY_P3 to "Le résultat est wiz\u2261r\u2014une application de finances personnelles qui respecte votre temps, protège votre vie privée et vous aide vraiment à bâtir votre patrimoine.",
    StringKey.ABOUT_VALUES_TITLE to "Nos valeurs",
    StringKey.ABOUT_VALUE_SIMPLICITY to "La simplicité d'abord : si ce n'est pas essentiel, on ne l'ajoute pas.",
    StringKey.ABOUT_VALUE_PRIVACY to "Confidentialité par conception : vos données financières vous appartiennent. Nous ne les vendons jamais.",
    StringKey.ABOUT_VALUE_PRICING to "Tarification honnête : pas de frais cachés, pas de pièges, pas de plans « illimités » avec des limites.",
    StringKey.ABOUT_VALUE_RESULTS to "Les résultats avant les fonctionnalités : nous mesurons notre succès par le fait que vous épargnez réellement plus, pas par des listes de fonctionnalités.",
    StringKey.ABOUT_CONTACT_TITLE to "Nous contacter",
    StringKey.ABOUT_CONTACT_INTRO to "Nous serions ravis de vous entendre.",

    // Statement Accounts
    StringKey.SETTINGS_STATEMENT_ACCOUNTS to "Comptes de relevés",
    StringKey.SETTINGS_NO_STATEMENT_ACCOUNTS to "Aucun compte de relevé.",
    StringKey.SETTINGS_STATEMENT_ACCOUNTS_ERROR to "Impossible de charger les comptes de relevés.",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_TITLE to "Supprimer le compte",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_WARNING to "Cela supprimera définitivement le compte et toutes ses transactions importées. Cette action est irréversible.",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_CONFIRM to "Supprimer le compte",
    StringKey.SETTINGS_STATEMENT_TRANSACTIONS_COUNT to "transactions",

    // Chart names (for settings visibility picker)
    StringKey.CHART_NAME_PREVIOUS_YEAR to "Graphique de l'année précédente",
    StringKey.CHART_NAME_SAVINGS_TREND to "Tendance du taux d'épargne",
    StringKey.CHART_NAME_EXPENSE_TIMELINE to "Chronologie des dépenses",
    StringKey.CHART_NAME_EXPENSE_DISTRIBUTION to "Répartition des dépenses",
    StringKey.CHART_NAME_CATEGORY_SPENDING to "Dépenses par catégorie",

    // Subscription detection
    StringKey.SUBSCRIPTION_CHECK_TITLE to "Vérification des abonnements",
    StringKey.SUBSCRIPTION_CHECK_SUBTITLE to "Confirmez quels frais récurrents sont des abonnements. Seuls les confirmés comptent dans votre total.",
    StringKey.SUBSCRIPTION_NONE_DETECTED to "Aucun abonnement récurrent détecté.",
    StringKey.SUBSCRIPTION_MONTHLY_TOTAL to "Total mensuel confirmé",
    StringKey.SUBSCRIPTION_WEEKLY to "Hebdomadaire",
    StringKey.SUBSCRIPTION_BIWEEKLY to "Aux deux semaines",
    StringKey.SUBSCRIPTION_MONTHLY to "Mensuel",
    StringKey.SUBSCRIPTION_CONFIRM_SELECTION to "Confirmer la sélection",

    // Paywall
    StringKey.PAYWALL_TITLE to "Votre essai est terminé",
    StringKey.PAYWALL_BODY to "Abonnez-vous pour continuer à utiliser toutes les fonctionnalités de wiz≡r. Vous pouvez toujours consulter votre tableau de bord en lecture seule.",
    StringKey.PAYWALL_TITLE_UPGRADE to "Passer à Premium",
    StringKey.PAYWALL_BODY_UPGRADE to "Profitez de l'expérience wiz≡r complète — comptes illimités, analyses et budgets intelligents.",
    StringKey.PAYWALL_MAYBE_LATER to "Peut-être plus tard",
    StringKey.PAYWALL_SUBSCRIBE to "S'abonner maintenant",
    StringKey.PAYWALL_READ_ONLY to "Continuer en lecture seule",
    StringKey.READ_ONLY_ACTIONS_LOCKED to "Modifications désactivées. Abonnez-vous pour les activer.",
    StringKey.PAYWALL_RESTORE to "Restaurer l'achat",
    StringKey.PAYWALL_MONTHLY_LABEL to "Mensuel",
    StringKey.PAYWALL_YEARLY_LABEL to "Annuel",
    StringKey.PAYWALL_MONTHLY_PRICE to "6,99 $/mois",
    StringKey.PAYWALL_YEARLY_PRICE to "72 $/an",
    StringKey.PAYWALL_BEST_VALUE to "MEILLEURE VALEUR",
    StringKey.PAYWALL_SAVE_14 to "Économisez 14 % — 6,00 $/mois",
    StringKey.PAYWALL_SAVE_PREFIX to "Économisez",
    StringKey.PAYWALL_PER_MONTH_SHORT to "/mois",
    StringKey.PAYWALL_BLOCKED_TOAST to "Abonnez-vous pour continuer",
    StringKey.PAYWALL_BLOCKED_TOAST_CTA to "S'abonner",
    StringKey.PAYWALL_FREE_TRIAL to "Essai gratuit de 14 jours inclus",
    StringKey.PAYWALL_LOADING_PRODUCTS to "Chargement des forfaits...",
    StringKey.PAYWALL_PURCHASE_FAILED to "Achat échoué. Veuillez réessayer.",
    StringKey.PAYWALL_RESTORE_SUCCESS to "Votre abonnement a été restauré.",
    StringKey.PAYWALL_ALREADY_SUBSCRIBED to "Vous avez déjà un abonnement actif.",
    StringKey.PAYWALL_AUTO_RENEW_DISCLOSURE to "Les abonnements se renouvellent automatiquement au prix sélectionné jusqu'à annulation. Vous pouvez annuler à tout moment dans les paramètres de votre compte, au moins 24 heures avant la fin de la période en cours.",
    StringKey.PAYWALL_TERMS_OF_USE to "Conditions d'utilisation",
    StringKey.PAYWALL_PRIVACY_POLICY to "Politique de confidentialité",
    StringKey.SETTINGS_CONTACT_SUPPORT to "Contacter le soutien",
    StringKey.SETTINGS_VIEW_ON_WEB to "Voir la version complète sur le Web",
    StringKey.TERMS_OVERVIEW_BODY to "En utilisant wiz≡r, vous acceptez ces conditions d'utilisation. wiz≡r est une application de finances personnelles qui vous aide à suivre votre taux d'épargne grâce à un accès en lecture seule à vos comptes bancaires via Plaid. Nous ne déplaçons jamais votre argent et ne stockons jamais vos identifiants bancaires.",
    StringKey.TERMS_SUBSCRIPTION_BODY to "wiz≡r offre des abonnements à renouvellement automatique. Les forfaits Standard et Pro sont facturés mensuellement ou annuellement via votre identifiant Apple. Les abonnements se renouvellent automatiquement au prix sélectionné, sauf annulation au moins 24 heures avant la fin de la période en cours. Gérez ou annulez à tout moment dans les paramètres d'abonnement de votre identifiant Apple.",
    StringKey.TERMS_ACCEPTABLE_USE_BODY to "Vous acceptez de fournir des informations exactes, d'utiliser l'application uniquement à des fins légales, de ne pas tenter de désosser ou d'interférer avec le service, et de respecter les droits de propriété intellectuelle de wiz≡r et de ses fournisseurs tiers (y compris Plaid).",
    StringKey.TERMS_LIMITATION_BODY to "wiz≡r fournit de l'information et des analyses financières, et non des conseils financiers, fiscaux ou juridiques. Vous êtes seul responsable de vos décisions financières. Le service est fourni « tel quel » sans garantie d'aucune sorte. Dans la mesure maximale permise par la loi, la responsabilité totale de wiz≡r est limitée aux frais que vous avez payés au cours des 12 mois précédant la réclamation.",
    StringKey.TERMS_CONTACT_BODY to "Pour toute question concernant ces conditions ou votre compte, contactez support@wiz3r.com. Ces conditions sont régies par les lois de l'Ontario, Canada.",
    StringKey.AUTH_TERMS_AGREEMENT to "En créant un compte, vous acceptez nos [Conditions d'utilisation](https://www.apple.com/legal/internet-services/itunes/dev/stdeula/) et notre [Politique de confidentialité](https://wiz3r.com/privacy).",

    // Multi-select
    StringKey.MULTISELECT_COUNT to "%s sélectionnés",
    StringKey.MULTISELECT_CHANGE_CATEGORY to "Changer la catégorie",
    StringKey.MULTISELECT_ASSIGN_TO_GOAL to "Assigner à un objectif",
    StringKey.MULTISELECT_EXCLUDE to "Exclure",
    StringKey.MULTISELECT_CLEAR_PROMPT to "Effacer la sélection ? (%s éléments sélectionnés)",
    StringKey.MULTISELECT_CLEAR to "Effacer",
    StringKey.MULTISELECT_KEEP to "Garder",
    StringKey.MULTISELECT_RECATEGORIZED to "%s transactions recatégorisées",
    StringKey.MULTISELECT_ASSIGNED_TO_GOAL to "%s transactions assignées à %s",
    StringKey.MULTISELECT_EXCLUDED to "%s transactions exclues",
    StringKey.MULTISELECT_INCLUDE to "Inclure",
    StringKey.MULTISELECT_INCLUDED to "%s transactions incluses",
    StringKey.MULTISELECT_EXCLUDE_CONFIRM to "Exclure %s transactions des calculs ?",
    StringKey.MULTISELECT_PARTIAL_FAILURE to "%s sur %s mises à jour. %s échouées.",

    // Multi-select batch actions
    StringKey.BATCH_EXCLUDE_COUNT to "Exclure (%s)",
    StringKey.BATCH_INCLUDE_COUNT to "Inclure (%s)",

    // Contexts (Pro feature)
    StringKey.CONTEXT_TITLE to "Contextes",
    StringKey.CONTEXT_ALL to "Tous",
    StringKey.CONTEXT_ACTION_TITLE to "Contexte",
    StringKey.CONTEXT_ACTION_SUBTITLE to "Assigner \u00e0 un contexte pour un suivi ind\u00e9pendant",
    StringKey.CONTEXT_ASSIGN_TITLE to "Assigner des contextes",
    StringKey.CONTEXT_ASSIGN_SUBTITLE to "S\u00e9lectionner un ou plusieurs contextes",
    StringKey.CONTEXT_NAME_PLACEHOLDER to "Nom du contexte",
    StringKey.CONTEXT_EDIT_TITLE to "Modifier le contexte",
    StringKey.CONTEXT_DELETE_CONFIRM to "Supprimer \u00ab\u202f%s\u202f\u00bb\u202f?",
    StringKey.DASHBOARD_LAYOUT_NAME to "Nom de la disposition",
    StringKey.DASHBOARD_LAYOUT_DELETE_CONFIRM to "Supprimer la disposition \u00ab\u202f%s\u202f\u00bb\u202f?",
    StringKey.CONTEXT_DELETE_CONFIRM_MESSAGE to "%s transactions perdront ce contexte.",
    StringKey.CONTEXT_SHOWING_BOTH to "Affichage des transactions dans les deux contextes",
    StringKey.CONTEXT_SHOWING_ALL_N to "Affichage des transactions dans les %s contextes",
    StringKey.CONTEXT_EXPENSES_ONLY to "D\u00e9penses seulement",
    StringKey.CONTEXT_BATCH_ASSIGN to "Assigner un contexte",

    // Manual Account / Statement Import
    StringKey.IMPORT_FROM_STATEMENTS to "Importer à partir de relevés bancaires",
    StringKey.IMPORT_RECOMMEND_DIRECT to "Nous recommandons de vous connecter directement à votre banque pour des mises à jour automatiques. Si votre banque n'est pas prise en charge, vous pouvez importer des relevés CSV ou PDF.",
    StringKey.IMPORT_CSV_OR_PDF to "Importer des relevés CSV ou PDF",
    StringKey.IMPORT_ANALYZING to "Analyse de votre relevé...",
    StringKey.IMPORT_DETECTED to "Nous avons détecté :",
    StringKey.IMPORT_IS_CORRECT to "Est-ce correct ?",
    StringKey.IMPORT_LOOKS_GOOD to "C'est correct",
    StringKey.IMPORT_DOESNT_LOOK_RIGHT to "Ceci ne semble pas correct",
    StringKey.IMPORT_STATEMENT to "Importer un relevé",
    StringKey.IMPORT_MISMATCH_WARNING to "Ce relevé semble provenir de %s mais ce compte est %s",
    StringKey.IMPORT_ANYWAY to "Importer quand même",
    StringKey.IMPORT_SUCCESS to "%s transactions importées dans %s",
    StringKey.IMPORT_DUPLICATES_SKIPPED to "%s doublons ignorés",
    StringKey.IMPORT_INSTITUTION_NAME to "Institution",
    StringKey.IMPORT_ACCOUNT_NAME to "Nom du compte",
    StringKey.IMPORT_ACCOUNT_TYPE to "Type de compte",
    StringKey.IMPORT_TRANSACTIONS_PREVIEW to "Aperçu des transactions",
    StringKey.IMPORT_TOTAL_COUNT to "%s transactions trouvées",
    StringKey.IMPORT_DROP_FILE to "Déposez votre fichier de relevé ici",
    StringKey.IMPORT_SELECT_FILE to "Sélectionner un fichier",
    StringKey.IMPORT_SUPPORTED_FORMATS to "Formats supportés : CSV, TXT, PDF",
    StringKey.IMPORT_PRIVACY_NOTE to "Vos données sont traitées de manière sécurisée. Consultez notre Politique de confidentialité pour plus de détails.",
    StringKey.IMPORT_RETRY to "Essayer un autre format",
    StringKey.IMPORT_CANCELLED_MESSAGE to "Importation annulée. Si le fichier était très volumineux ou inhabituel, essayez une période plus courte ou un format différent.",
    StringKey.IMPORT_FAILED_GENERIC to "Échec de l'importation : %s",
    StringKey.IMPORT_MISMATCH_UNKNOWN to "Nous n'avons pas pu identifier la banque de ce relevé — veuillez confirmer qu'il appartient bien à %s avant de continuer.",
    StringKey.IMPORT_PASTE_MANUALLY to "Coller les transactions manuellement",
    StringKey.IMPORT_COLUMN_DATE to "Date",
    StringKey.IMPORT_COLUMN_DESCRIPTION to "Description",
    StringKey.IMPORT_COLUMN_AMOUNT to "Montant",
    StringKey.IMPORT_ACCOUNT_TYPE_CHECKING to "Chèques",
    StringKey.IMPORT_ACCOUNT_TYPE_SAVINGS to "Épargne",
    StringKey.IMPORT_ACCOUNT_TYPE_CREDIT to "Carte de crédit",

    // Tooltips
    StringKey.TOOLTIP_GO_HOME to "Accueil",
    StringKey.TOOLTIP_CHANGE_LANGUAGE to "Cliquer pour changer de langue",
    StringKey.TOOLTIP_HOW_IT_WORKS to "Comment ça fonctionne",

    // Time periods (display labels)
    StringKey.TIME_WEEKLY to "Hebdomadaire",
    StringKey.TIME_MONTHLY to "Mensuel",
    StringKey.TIME_QUARTERLY to "Trimestriel",

    // Transaction Notifications
    StringKey.NOTIF_SETTINGS_TITLE to "Notifications de transactions",
    StringKey.NOTIF_SETTINGS_ENABLE to "Activer les notifications",
    StringKey.NOTIF_SETTINGS_CHANNEL to "Notifier par",
    StringKey.NOTIF_SETTINGS_EMAIL to "Courriel",
    StringKey.NOTIF_SETTINGS_SMS to "SMS",
    StringKey.NOTIF_SETTINGS_BOTH to "Les deux",
    StringKey.NOTIF_SETTINGS_PHONE to "Numéro de téléphone",
    StringKey.NOTIF_SETTINGS_VERIFY to "Vérifier",
    StringKey.NOTIF_SETTINGS_PHONE_VERIFIED to "Vérifié",
    StringKey.NOTIF_SETTINGS_SMS_CONSENT to "En activant les SMS, vous acceptez de recevoir des messages transactionnels de wiz≡r. Les tarifs standard s'appliquent. Répondez STOP pour vous désabonner.",
    StringKey.NOTIF_SETTINGS_SMS_AGREE to "J'accepte de recevoir des notifications par SMS",
    StringKey.NOTIF_SETTINGS_UPGRADE_HINT to "Passez à Pro pour activer les notifications",
    StringKey.NOTIF_SETTINGS_ENTER_CODE to "Entrez le code de vérification",
    StringKey.NOTIF_SETTINGS_CODE_SENT to "Code envoyé à votre téléphone",
    StringKey.NOTIF_TEST_SEND to "Envoyer une notification test",
    StringKey.NOTIF_TEST_SENDING to "Envoi en cours…",
    // "courriel" rather than "e-mail" — Canadian French, consistent with the
    // rest of the app.
    StringKey.NOTIF_TEST_SENT to "Envoyée — vérifiez vos courriels ou votre téléphone d'ici quelques secondes.",
    StringKey.NOTIF_TEST_NOTHING_FIRED to "Rien n'a été envoyé. Vérifiez les journaux du serveur.",
    StringKey.NOTIF_TEST_FAILED to "Échec de l'envoi de la notification test",
    StringKey.NOTIF_EMAIL_SUBJECT to "wiz≡r — %s nouvelles transactions",
    StringKey.NOTIF_EMAIL_GREETING to "Bonjour %s,",
    StringKey.NOTIF_EMAIL_SUMMARY to "Vous avez %s nouvelles transactions (%s au total) dans %s comptes.",
    StringKey.NOTIF_EMAIL_SUMMARY_SINGLE_ACCOUNT to "Vous avez %s nouvelles transactions (%s au total).",
    StringKey.NOTIF_EMAIL_CONFIRM_ALL to "Tout confirmer",
    StringKey.NOTIF_EMAIL_REVIEW to "Voir dans l'app",
    StringKey.NOTIF_EMAIL_DISMISS to "Ignorer",
    StringKey.NOTIF_EMAIL_FOOTER to "Vous recevez ce message car vous avez activé les notifications de transactions. Gérez vos préférences dans les Paramètres.",
    StringKey.NOTIF_SMS_SUMMARY to "Wiz≡r: %s nouvelles transactions (%s total). Répondre 1=Tout confirmer 2=Voir dans l'app 3=Ignorer 4=Voir détails",
    StringKey.NOTIF_SMS_CONFIRM_DONE to "Fait! %s transactions confirmées.",
    StringKey.NOTIF_SMS_REVIEW_LINK to "Ouvrir wiz≡r: %s",
    StringKey.NOTIF_SMS_DISMISS_ACK to "Compris. Vous pouvez réviser à tout moment dans l'app.",
    StringKey.NOTIF_SMS_HELP to "Répondre 1=Tout confirmer 2=Voir dans l'app 3=Ignorer 4=Voir détails",
    StringKey.NOTIF_SMS_NO_PENDING to "Aucune notification en attente. Vérifiez l'app.",
    StringKey.NOTIF_SMS_EXPIRED to "Cette notification a expiré. Consultez l'app pour les dernières transactions.",
    StringKey.NOTIF_SMS_ALREADY_ACTED to "Vous avez déjà %s ces transactions.",
    StringKey.NOTIF_SMS_DETAILS_FOOTER to "Répondre 1=Tout confirmer 2=Voir dans l'app 3=Ignorer",
    StringKey.NOTIF_ACTION_CONFIRMED_TITLE to "Transactions confirmées",
    StringKey.NOTIF_ACTION_CONFIRMED_MSG to "%s transactions ont été confirmées.",
    StringKey.NOTIF_ACTION_DISMISSED_TITLE to "Notification ignorée",
    StringKey.NOTIF_ACTION_EXPIRED_TITLE to "Notification expirée",
    StringKey.NOTIF_ACTION_EXPIRED_MSG to "Cette notification a expiré. Consultez l'app pour les dernières transactions.",
    StringKey.NOTIF_ACTION_ALREADY_ACTED_TITLE to "Déjà traité",

    // Subscription status card (Settings)
    StringKey.SETTINGS_PLAN_PRO to "Plan Pro",
    StringKey.SETTINGS_PLAN_STANDARD to "Plan Standard",
    StringKey.SETTINGS_PLAN_TRIAL to "Essai gratuit",
    StringKey.SETTINGS_PLAN_ACTIVE to "Actif",
    StringKey.SETTINGS_PLAN_CANCELED to "Annulé — lecture seule",
    StringKey.SETTINGS_PLAN_EXPIRED to "Essai terminé — lecture seule",
    StringKey.SETTINGS_PLAN_PAST_DUE to "Paiement en retard",
    StringKey.SETTINGS_MANAGE_SUBSCRIPTION to "Gérer l'abonnement",
    StringKey.SETTINGS_PLAN_ALL_FEATURES to "Toutes les fonctionnalités actives",
    StringKey.SETTINGS_PLAN_TRIAL_ENJOY to "Profitez de toutes les fonctionnalités pendant votre essai. Choisissez un plan avant la fin pour continuer.",
    StringKey.SETTINGS_PLAN_CHOOSE to "Choisir un plan",
    StringKey.SETTINGS_PLAN_UPGRADE_DESC to "Passez à Pro pour les divisions, répartitions, alertes et plus.",
    StringKey.SETTINGS_PLAN_UPGRADE_CTA to "Passer à Pro",
    StringKey.SETTINGS_PLAN_THANK_YOU to "Merci de soutenir wiz\u2261r. Vous avez accès à toutes les fonctionnalités Pro.",
    StringKey.SETTINGS_DELETE_SOFT_CONFIRM to "Vous êtes sûr ? On aimerait vous aider si quelque chose ne va pas.",
    StringKey.SETTINGS_DELETE_YES_DELETE to "Oui, je veux supprimer",
) + mapOf(
    // Surcharge — voir le commentaire équivalent dans English.kt.
    StringKey.ACCOUNT_BANK_NOT_RETURNING_DATA to
        "La banque ne retourne pas de nouvelles transactions. Réessayez plus tard ou délier puis ajouter à nouveau ce compte.",
    StringKey.MERGE_RESTORED_TOAST_ONE to
        "%1\$s transactions restaurées depuis votre ancienne connexion %2\$s.",
    StringKey.MERGE_RESTORED_TOAST_MANY to
        "%1\$s transactions restaurées depuis %2\$s comptes précédents.",
    StringKey.MERGE_PENDING_CONFIRM_PROMPT to
        "Nous avons trouvé %1\$s compte(s) supplémentaire(s) qui pourraient correspondre à votre ancienne connexion. Restaurer cet historique dans vos nouveaux comptes ?",
    StringKey.INSTITUTION_DOWN_TITLE to "%1\$s est actuellement hors service",
    StringKey.INSTITUTION_DOWN_BODY to
        "%1\$s signale de nombreux échecs de connexion en ce moment. La reconnexion va très probablement échouer jusqu'à leur rétablissement. Le problème vient de leur côté, pas de Wiz3r.",
    StringKey.INSTITUTION_DEGRADED_TITLE to "%1\$s rencontre des problèmes",
    StringKey.INSTITUTION_DEGRADED_BODY to
        "%1\$s signale un nombre élevé d'échecs de connexion en ce moment. La reconnexion peut échouer ou être lente. Le problème vient de leur côté, pas de Wiz3r.",
    StringKey.INSTITUTION_TRY_ANYWAY to "Essayer quand même",
    StringKey.INSTITUTION_WAIT to "Attendre et réessayer plus tard",

    // Wiz 3R — étiquettes de section et écran détails
    StringKey.WIZ3R_SECTION_REVEAL to "RÉVÈLE",
    StringKey.WIZ3R_SECTION_REASON to "RAISONNE",
    StringKey.WIZ3R_SECTION_REALIZE to "RÉALISE",
    StringKey.WIZ3R_DETAIL_BACK to "← Retour",
    StringKey.WIZ3R_ASK to "Demander à Wiz 3R →",
    StringKey.WIZ3R_CARD_SEE_WHY to "Voir pourquoi — et poser vos questions →",
    StringKey.WIZ3R_CARD_SEE_WHY_NO_ASK to "Voir pourquoi →",
    StringKey.WIZ3R_ASK_COMING_SOON to "Bientôt disponible",
    StringKey.WIZ3R_LOADING_SOURCE_DATA to "Chargement des données sources…",
    StringKey.WIZ3R_KEY_INPUTS to "DONNÉES CLÉS",
    StringKey.WIZ3R_REASON_YEARLY_PROJECTION to "À ce rythme, sur 12 mois",
    StringKey.WIZ3R_REASON_PREVIOUS_BEST to "Meilleur mois précédent",
    StringKey.WIZ3R_REASON_EXTRA_SAVED to "Épargne de plus que le mois précédent",
    StringKey.WIZ3R_REASON_SAVED_SO_FAR to "Épargné à ce jour",
    StringKey.WIZ3R_REASON_REMAINING_TO_GOAL to "Restant pour atteindre l'objectif",
    StringKey.WIZ3R_CARD_NOT_LOADED to "Carte non chargée",
    StringKey.WIZ3R_CARD_NOT_LOADED_BODY to
        "Cette page a été ouverte directement. Retournez au tableau de bord et touchez à nouveau une section de carte.",
    StringKey.WIZ3R_BACK_TO_DASHBOARD to "← Retour au tableau de bord",
    // RÉVÈLE — constructeur
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_HEADING to "Transactions associées à un objectif ce mois-ci",
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_SUBHEADING to
        "Ce sont les transferts qui comptent dans votre taux d'épargne.",
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_EMPTY to "Aucune transaction associée à un objectif ce mois-ci.",
    StringKey.WIZ3R_REVEAL_THIS_MONTH to "Dernier mois complet",
    StringKey.WIZ3R_REVEAL_LAST_MONTH to "Mois précédent",
    StringKey.WIZ3R_REVEAL_VS_LAST_MONTH to "vs mois dernier",
    StringKey.WIZ3R_REVEAL_CATEGORY_HEADING to "Plus grosses transactions en %1\$s ce mois-ci",
    StringKey.WIZ3R_REVEAL_CATEGORY_HEADING_GENERIC to "Plus grosses transactions ce mois-ci",
    StringKey.WIZ3R_REVEAL_CATEGORY_SUBHEADING to "Les plus élevées d'abord. Maximum 10.",
    StringKey.WIZ3R_REVEAL_CATEGORY_TOTAL_THIS_MONTH to "Total du dernier mois complet",
    StringKey.WIZ3R_REVEAL_CATEGORY_SHOWN to "Affichées",
    StringKey.WIZ3R_REVEAL_CATEGORY_EMPTY to "Aucune transaction trouvée pour cette période.",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING to "Contributions vers « %1\$s »",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING_GENERIC to "Contributions récentes",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_SUBHEADING to "Deux derniers mois.",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_EMPTY to
        "Aucune contribution associée à cet objectif pour le moment.",
    StringKey.WIZ3R_REVEAL_CURRENT_BALANCE to "Solde actuel",
    StringKey.WIZ3R_REVEAL_TARGET to "Cible",
    StringKey.WIZ3R_REVEAL_FUNDED to "atteint",
    StringKey.WIZ3R_REVEAL_MERCHANT_HEADING to "Transactions récentes pour ces marchands",
    StringKey.WIZ3R_REVEAL_RECURRING_HEADING to "Transactions récurrentes",
    StringKey.WIZ3R_REVEAL_RECURRING_SUBHEADING to "Détectées d'après la fréquence des marchands.",
    StringKey.WIZ3R_REVEAL_NO_TRANSACTIONS to "Aucune transaction trouvée.",
    StringKey.WIZ3R_REVEAL_GENERIC_HEADING to "Données sources",
    StringKey.WIZ3R_REVEAL_GENERIC_EMPTY to
        "Le détail des chiffres pour cette carte n'est pas encore disponible.",
    // RAISONNE — constructeur
    StringKey.WIZ3R_REASON_SAVINGS_RATE_THIS to "Taux d'épargne ce mois-ci",
    StringKey.WIZ3R_REASON_SAVINGS_RATE_PREV to "Taux d'épargne le mois dernier",
    StringKey.WIZ3R_REASON_PERCENT_OF_INCOME to "Pourcentage du revenu",
    StringKey.WIZ3R_REASON_PERCENT_OF_BUDGET to "Pourcentage du budget utilisé",
    StringKey.WIZ3R_REASON_PLANNED_AMOUNT to "Montant prévu",
    StringKey.WIZ3R_REASON_CHANGE_VS_PRIOR to "Variation vs période précédente",
    StringKey.WIZ3R_REASON_AMOUNT to "Montant",
    StringKey.WIZ3R_REASON_GOAL_FUNDED to "Objectif atteint",
    StringKey.WIZ3R_REASON_EXPLAINER_SAVINGS to
        "Taux d'épargne = contributions aux objectifs ÷ revenu total du mois. Les revenus et les contributions excluent les parents fractionnés/distribués et les transactions exclues manuellement, comme dans le diagramme du tableau de bord.",
    StringKey.WIZ3R_REASON_EXPLAINER_CATEGORY to
        "Pourcentage du revenu = dépenses de la catégorie ÷ revenu total ce mois-ci. Une part qui grimpe signifie habituellement que la catégorie a grossi ou que le revenu a baissé.",
    StringKey.WIZ3R_REASON_EXPLAINER_PROJECTION to
        "La projection est une simulation Monte Carlo de 5 000 scénarios jusqu'à l'échéance de l'objectif. Les bandes ombrées représentent les plages 25–75 % et 10–90 % ; la ligne est la médiane.",
    // RÉALISE — constructeur
    StringKey.WIZ3R_REALIZE_REVIEW_TXNS to "Revoir les transactions",
    StringKey.WIZ3R_REALIZE_SEE_CATEGORY to "Voir cette catégorie",
    StringKey.WIZ3R_REALIZE_OPEN_GOAL to "Ouvrir cet objectif",
    StringKey.WIZ3R_REALIZE_CREATE_GOAL to "Créer un objectif",
    StringKey.WIZ3R_REALIZE_REVIEW_SUBSCRIPTIONS to "Revoir les abonnements",
    StringKey.WIZ3R_REALIZE_READ_GUIDE to "Lire le guide",
    StringKey.WIZ3R_REALIZE_OPEN_GOALS to "Ouvrir vos objectifs",
    StringKey.WIZ3R_REALIZE_SET_BUDGET to "Définir un budget pour cette catégorie",
    StringKey.WIZ3R_REALIZE_TAG_MORE_TRANSFERS to "Associer plus de transferts à cet objectif",
    StringKey.WIZ3R_REALIZE_INTRO_PERSONAL_BEST to
        "Maintenez la cadence — voyez quel objectif a besoin du prochain coup de pouce.",
    StringKey.WIZ3R_REALIZE_INTRO_SAVINGS_DROP to
        "Regardez ce qui a changé et déterminez si c'est volontaire.",
    StringKey.WIZ3R_REALIZE_INTRO_CATEGORY to
        "Pour faire baisser ce poste, voici les leviers.",
    StringKey.WIZ3R_REALIZE_INTRO_ALMOST_COMPLETE to
        "Vous y êtes presque — un petit effort de plus suffit.",
    StringKey.WIZ3R_REALIZE_INTRO_BEHIND to
        "Si l'échéance compte encore, accélérez le rythme ou repoussez la date.",

    // Wiz 3R — panneau de clavardage
    StringKey.WIZ3R_CHAT_TITLE to "Demander à Wiz 3R",
    StringKey.WIZ3R_CHAT_INPUT_PLACEHOLDER to "Posez une question complémentaire…",
    StringKey.WIZ3R_CHAT_SEND to "Envoyer",
    StringKey.WIZ3R_CHAT_CLEAR to "Effacer la conversation",
    StringKey.WIZ3R_CHAT_CLEAR_CONFIRM to "Effacer la conversation pour cette carte?",
    StringKey.WIZ3R_CHAT_EMPTY_PROMPT_TITLE to "Que voulez-vous explorer?",
    StringKey.WIZ3R_CHAT_EMPTY_PROMPT_BODY to
        "Demandez à Wiz 3R n'importe quoi sur cette carte ou vos finances. Les conversations restent privées.",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_1 to "Pourquoi mon taux d'épargne a-t-il changé?",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_2 to "Comment ma tranche d'imposition influence-t-elle ceci?",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_3 to "Que se passerait-il si je doublais ma contribution?",
    StringKey.WIZ3R_CHAT_THINKING to "Wiz 3R réfléchit…",
    StringKey.WIZ3R_CHAT_RATE_LIMITED to
        "Limite horaire atteinte (30 messages par heure). Réessayez dans un moment.",
    StringKey.WIZ3R_CHAT_PRO_REQUIRED to "Demander à Wiz 3R est offert avec Pro. Passez à Pro pour discuter.",
    StringKey.WIZ3R_CHAT_NETWORK_ERROR to "Impossible de joindre Wiz 3R. Vérifiez votre connexion et réessayez.",
    StringKey.WIZ3R_CHAT_DEMO_SIGNUP to "Demander à Wiz 3R fait partie d'un compte gratuit. Inscrivez-vous pour discuter de vos propres finances.",
    StringKey.WIZ3R_CHAT_CLOSE to "Fermer le clavardage",

    // 50/30/20 — étiquettes des paniers budgétaires
    StringKey.BUDGET_BUCKET_NEEDS to "Besoin",
    StringKey.BUDGET_BUCKET_WANTS to "Envie",
    StringKey.BUDGET_BUCKET_SAVINGS to "Épargne",
    StringKey.BUDGET_BUCKET_IGNORE to "Ignorer",
    StringKey.BUDGET_BUCKET_UNCLASSIFIED to "Non classé",
    StringKey.BUDGET_BUCKET_CHIP_TITLE to "Cliquez pour changer de panier (50/30/20)",
    StringKey.BUDGET_BUCKET_DEFAULT_HINT to "Par défaut — cliquez pour modifier",
    StringKey.BUDGET_BUCKET_OVERRIDE_HINT to "Votre choix",
    StringKey.BUDGET_BUCKET_MIXED to "Mixte",
    StringKey.BUDGET_BUCKET_MIXED_HINT to
        "Les sous-catégories appartiennent à des paniers différents par défaut. Cliquez pour assigner un seul panier à tout le groupe, ou développez pour modifier les sous-catégories individuellement.",
    StringKey.BUDGET_BUCKET_RESET to "Rétablir la valeur par défaut",
    StringKey.BUDGET_BUCKET_FOLLOWS_PRIMARY to
        "Ces transactions n'ont pas de sous-catégorie; elles suivent donc le panier choisi pour la catégorie ci-dessus.",
    // Widget Répartition besoins/envies — fenêtre d'ajustement des paniers (tâche 7)
    StringKey.BUDGET_EDIT_BUCKETS_CTA to "Ajuster besoins et envies",
    StringKey.BUDGET_EDIT_BUCKETS_TITLE to "Ajuster besoins et envies",
    StringKey.BUDGET_EDIT_BUCKETS_SHOW_ALL to "Afficher toutes les catégories",
    // Budget — bascule entre catégorie principale et sous-catégories
    StringKey.BUDGET_SWITCH_TO_SUB_TITLE to "Passer aux budgets par sous-catégorie?",
    StringKey.BUDGET_SWITCH_TO_SUB_BODY to
        "%1\$s a actuellement un budget défini au niveau principal. Si vous continuez, les sous-catégories deviendront la source de vérité et la ligne principale affichera la somme des sous-totaux (non modifiable). Les montants actuels du niveau principal seront effacés.",
    StringKey.BUDGET_SWITCH_TO_SUB_CONFIRM to "Passer aux sous-catégories",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_TITLE to "Remplacer les montants des sous-catégories?",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_BODY to
        "%1\$s a des budgets dans ses sous-catégories. Si vous continuez, le sous-total de chaque mois sera transféré sur la ligne principale (aucune donnée perdue), votre nouvelle valeur remplacera le mois que vous venez de modifier, et les montants des sous-catégories seront ensuite effacés.",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_CONFIRM to "Remplacer les sous-totaux",
    StringKey.BUDGET_TRANSITION_CANCEL to "Annuler",
    StringKey.BUDGET_PRIMARY_SUM_LABEL to "Σ somme des sous-catégories",
    StringKey.BUDGET_PRIMARY_SUM_HINT to
        "Les sous-catégories sont la source de vérité pour cette ligne. Modifiez les sous-cellules individuellement, ou cliquez ici pour revenir à un budget au niveau principal.",

    // Studio d'allocation d'objectifs — questionnaire d'intégration
    StringKey.STUDIO_PREVIEW_BANNER_TITLE to "Studio d'allocation d'objectifs (aperçu)",
    StringKey.STUDIO_PREVIEW_BANNER_BODY to "Parlez-nous un peu de votre situation pour que le Studio puisse vous présenter le calcul de chaque régime enregistré dès sa mise en service.",
    StringKey.STUDIO_PREVIEW_BANNER_CTA to "Configurer mon profil",
    StringKey.STUDIO_PREVIEW_BANNER_EDIT_CTA to "Modifier mon profil",
    StringKey.STUDIO_STEPPER_PROGRESS to "%s sur 7",
    StringKey.STUDIO_STEPPER_SKIP to "Passer — j'apprendrai en cours de route",
    StringKey.STUDIO_STEPPER_BACK to "Retour",
    StringKey.STUDIO_STEPPER_DONE_TITLE to "Tout est prêt",
    StringKey.STUDIO_STEPPER_DONE_BODY to "Votre profil Studio est enregistré. Quand le Studio sera disponible, il s'appuiera sur vos réponses pour vous montrer le calcul.",
    StringKey.STUDIO_STEPPER_DONE_DISMISS to "Terminé",
    StringKey.STUDIO_WHY_LABEL to "ⓘ Pourquoi on demande",
    StringKey.STUDIO_WHY_INCOME to "Votre revenu et votre province déterminent votre taux marginal d'imposition — la pièce maîtresse pour savoir quel compte vous avantage le plus.",
    StringKey.STUDIO_WHY_AGE to "L'âge précise les horizons et conditionne l'admissibilité au CELIAPP (qui expire 15 ans après la première cotisation).",
    StringKey.STUDIO_WHY_HOME to "Avoir déjà été propriétaire au Canada vous rend inadmissible au CELIAPP — on l'enlève alors des comparaisons.",
    StringKey.STUDIO_WHY_RRSP_ROOM to "Le Studio plafonne les projections selon votre vrai droit de cotisation REER, pour ne jamais suggérer un dépassement.",
    StringKey.STUDIO_WHY_TFSA_ROOM to "Idem pour le CELI — connaître votre droit évite des suggestions irréalistes.",
    StringKey.STUDIO_WHY_TENURE to "Selon que vous épargnez depuis peu ou depuis longtemps, le Studio change son ton — bases vs. accélération du taux d'épargne.",
    StringKey.STUDIO_WHY_PRIORITIES to "Votre priorité actuelle ajoute un badge « correspond à votre priorité » dans la comparaison — les calculs restent maîtres, mais ils tranchent en votre faveur.",
    StringKey.STUDIO_HOW_LABEL to "ⓘ Où trouver l'information",
    StringKey.STUDIO_HOW_FIND_ROOM to "Connectez-vous à Mon dossier de l'ARC → REER et CELI → repérez la limite de cotisation de l'année (REER) ou le droit disponible (CELI).",
    StringKey.STUDIO_Q_INCOME_TITLE to "Où habitez-vous et quel est votre revenu?",
    StringKey.STUDIO_Q_INCOME_PROVINCE_LABEL to "Province",
    StringKey.STUDIO_Q_INCOME_MODE_GROSS to "Revenu brut annuel",
    StringKey.STUDIO_Q_INCOME_MODE_PAYCHEQUE to "Paye moyenne",
    StringKey.STUDIO_Q_INCOME_MODE_BANK to "Détecter depuis mes payes",
    StringKey.STUDIO_Q_INCOME_MODE_BANK_COMING_SOON to "Bientôt",
    StringKey.STUDIO_Q_INCOME_GROSS_LABEL to "Brut annuel (CAD)",
    StringKey.STUDIO_Q_INCOME_PAYCHEQUE_LABEL to "Net par paye (CAD)",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_LABEL to "Fréquence",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_MONTHLY to "Mensuelle",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_BI_WEEKLY to "Aux 2 semaines",
    StringKey.STUDIO_Q_INCOME_ESTIMATED_GROSS to "Brut annuel estimé : ~%s",
    StringKey.STUDIO_Q_AGE_TITLE to "Quelle est votre tranche d'âge?",
    StringKey.STUDIO_Q_AGE_18_29 to "18 – 29",
    StringKey.STUDIO_Q_AGE_30_39 to "30 – 39",
    StringKey.STUDIO_Q_AGE_40_49 to "40 – 49",
    StringKey.STUDIO_Q_AGE_50_59 to "50 – 59",
    StringKey.STUDIO_Q_AGE_60_69 to "60 – 69",
    StringKey.STUDIO_Q_AGE_70_PLUS to "70 +",
    StringKey.STUDIO_Q_HOME_TITLE to "Avez-vous déjà été propriétaire d'une maison au Canada?",
    StringKey.STUDIO_Q_HOME_NEVER to "Non, jamais",
    StringKey.STUDIO_Q_HOME_BEFORE to "Oui, déjà",
    StringKey.STUDIO_Q_HOME_DECLINED to "Je préfère ne pas répondre",
    StringKey.STUDIO_Q_RRSP_ROOM_TITLE to "Connaissez-vous votre droit de cotisation REER?",
    StringKey.STUDIO_Q_TFSA_ROOM_TITLE to "Connaissez-vous votre droit de cotisation CELI?",
    StringKey.STUDIO_Q_ROOM_KNOWN to "Je le sais exactement",
    StringKey.STUDIO_Q_ROOM_ROUGH to "Approximativement",
    StringKey.STUDIO_Q_ROOM_UNKNOWN to "Je ne sais pas — montrez-moi comment",
    StringKey.STUDIO_Q_ROOM_AMOUNT_LABEL to "Montant (CAD)",
    StringKey.STUDIO_Q_TENURE_TITLE to "Depuis combien de temps épargnez-vous régulièrement?",
    StringKey.STUDIO_Q_TENURE_NEVER to "Jamais",
    StringKey.STUDIO_Q_TENURE_FEW_MONTHS to "Quelques mois",
    StringKey.STUDIO_Q_TENURE_ONE_TO_THREE to "1 à 3 ans",
    StringKey.STUDIO_Q_TENURE_FIVE_PLUS to "5 ans ou plus",
    StringKey.STUDIO_Q_PRIORITIES_TITLE to "Quelle priorité ressort le plus pour vous en ce moment?",
    StringKey.STUDIO_Q_PRIORITIES_HINT to "Choisissez jusqu'à 2.",
    StringKey.STUDIO_Q_PRIORITY_EMERGENCY to "Coussin d'urgence",
    StringKey.STUDIO_Q_PRIORITY_HOME to "Mise de fonds pour une maison",
    StringKey.STUDIO_Q_PRIORITY_RETIREMENT to "Retraite",
    StringKey.STUDIO_Q_PRIORITY_EDUCATION to "Études des enfants",
    StringKey.STUDIO_Q_PRIORITY_NONE to "Rien de précis",
    StringKey.STUDIO_Q_PRIORITIES_CONTINUE to "Continuer",
    StringKey.STUDIO_ROOM_CRA_LINK to "Ouvrir Mon dossier de l'ARC →",
    StringKey.STUDIO_ROOM_LEAVE_BLANK_NOTE to "On laisse vide pour l'instant — quand vous aurez votre chiffre, modifiez votre profil depuis la bannière sur la page Objectifs.",
    StringKey.STUDIO_Q_INCOME_PROVINCE_PLACEHOLDER to "Choisissez votre province",
    // Studio screen
    StringKey.STUDIO_CUSTOMIZE_ALLOCATION to "Comparer les options",
    StringKey.GOAL_CATEGORY_LABEL to "Catégorie",
    StringKey.GOAL_CATEGORY_HELPER to "Changer la catégorie met à jour les champs ci-dessous instantanément.",
    StringKey.GOAL_ACCOUNT_NOT_SET to "— non défini —",
    StringKey.GOAL_ACCOUNT_LABEL_HISA to "CEPI — compte épargne à intérêt élevé",
    StringKey.GOAL_ACCOUNT_LABEL_TFSA to "CELI",
    StringKey.GOAL_ACCOUNT_LABEL_RRSP to "REER",
    StringKey.GOAL_ACCOUNT_LABEL_FHSA to "CELIAPP (première habitation)",
    StringKey.GOAL_ACCOUNT_LABEL_RESP to "REEE (avec SCEE)",
    StringKey.GOAL_ACCOUNT_LABEL_RDSP to "REEI",
    StringKey.GOAL_ACCOUNT_LABEL_NON_REGISTERED to "Non enregistré (imposable)",
    StringKey.GOAL_ACCOUNT_LABEL_EMPLOYER_PENSION to "Régime de retraite / REER collectif",
    StringKey.GOAL_ACCOUNT_LABEL_CHEQUING to "Compte chèques",
    StringKey.GOAL_ACCOUNT_LABEL_OTHER to "Autre",
    StringKey.GOAL_DEBT_DETAILS_TITLE to "Détails de la dette (pour un calcul précis des économies d'intérêts)",
    StringKey.GOAL_DEBT_DETAILS_RATE to "Taux d'intérêt (%)",
    StringKey.GOAL_DEBT_DETAILS_BALANCE to "Solde restant ($)",
    StringKey.GOAL_DEBT_DETAILS_MONTHS to "Durée restante (mois)",
    StringKey.STUDIO_HEADER_TITLE to "Studio d'allocation d'objectifs",
    StringKey.STUDIO_HEADER_EDIT_PROFILE to "Modifier le profil",
    StringKey.STUDIO_VEHICLE_TFSA to "CELI",
    StringKey.STUDIO_VEHICLE_RRSP to "REER",
    StringKey.STUDIO_VEHICLE_FHSA to "CELIAPP",
    StringKey.STUDIO_VEHICLE_RESP to "REEE",
    StringKey.STUDIO_VEHICLE_RDSP to "REEI",
    StringKey.STUDIO_VEHICLE_NON_REGISTERED to "Non enregistré",
    StringKey.STUDIO_VEHICLE_HISA to "CEIE",
    StringKey.STUDIO_VEHICLE_HISA_INFLATION_NOTE to "L'argent comptant ne croît pas — l'inflation érode sa valeur avec le temps.",
    StringKey.STUDIO_ACCOUNT_TYPE_SECTION_TITLE to "Aide à la sélection du type de compte",
    StringKey.STUDIO_BADGE_BEST_FIT to "Meilleur choix",
    StringKey.STUDIO_BADGE_BACKUP to "Solution backup",
    StringKey.STUDIO_BADGE_NOT_IDEAL to "Peu adapté",
    StringKey.STUDIO_BADGE_INELIGIBLE to "Inadmissible",
    StringKey.STUDIO_NET_AT_GOAL to "Net à l'échéance",
    StringKey.STUDIO_ROOM_REMAINING to "Droit restant : ~%s",
    StringKey.STUDIO_LEVER_CONTRIBUTION to "Cotisation mensuelle",
    StringKey.STUDIO_LEVER_RETURN to "Rendement annuel attendu",
    StringKey.STUDIO_LEVER_HORIZON to "Durée jusqu'à l'objectif",
    StringKey.STUDIO_LEVER_HORIZON_SET to "Définir l'échéance",
    StringKey.STUDIO_LEVER_FUTURE_RATE to "Taux marginal futur au retrait",
    StringKey.STUDIO_LEVER_RESET to "Réinitialiser les valeurs",
    StringKey.STUDIO_PIN_VEHICLE to "Choisir ce véhicule",
    StringKey.STUDIO_PINNED_VEHICLE to "Choisi",
    StringKey.STUDIO_YOUR_PICK to "Mon choix",
    StringKey.STUDIO_NO_PROFILE_TITLE to "Configurez d'abord votre profil Studio",
    StringKey.STUDIO_NO_PROFILE_BODY to "On a besoin de votre contexte fiscal pour faire le calcul. Touchez « Créer un objectif » pour répondre aux 7 courtes questions.",
    StringKey.STUDIO_NO_VEHICLES_EMPTY_STATE to "Cette catégorie d'objectif n'utilise pas de compte enregistré (p. ex. remboursement de dette). La comparaison des véhicules du Studio ne s'applique pas ici.",
    StringKey.STUDIO_CHART_TITLE to "Croissance dans le temps",
    StringKey.STUDIO_CHART_LEGEND_NET_HINT to "Plein = dans le compte · Pointillé = patrimoine sur papier",
    StringKey.STUDIO_CHART_INCL_REFUNDS to "avec remb.",
    StringKey.STUDIO_CHART_NET to "net",
    StringKey.STUDIO_CHART_DASHED_NOTE to "Pointillé = solde + remboursements réinvestis. La chute à la date de l'objectif représente l'impôt au retrait (REER seulement).",
    StringKey.STUDIO_ROOM_CHART_TITLE to "Droit de cotisation restant",
    StringKey.STUDIO_ROOM_CHART_REMAINING to "%s\$ restant",
    StringKey.DEBT_STUDIO_TITLE to "Studio de remboursement de dette",
    StringKey.DEBT_STUDIO_CUSTOMIZE_CTA to "Comparer les stratégies de remboursement",
    StringKey.DEBT_STUDIO_SCENARIO_CURRENT to "Plan actuel",
    StringKey.DEBT_STUDIO_SCENARIO_ADJUSTED to "Plan ajusté",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT to "Paiement mensuel",
    StringKey.DEBT_STUDIO_LEVER_RATE to "Taux d'intérêt (refinancement hypothétique)",
    StringKey.DEBT_STUDIO_LEVER_LUMPSUM to "Versement forfaitaire / mise de fonds",
    StringKey.DEBT_STUDIO_STAT_PAYOFF to "Mois jusqu'au remboursement",
    StringKey.DEBT_STUDIO_STAT_INTEREST to "Intérêts totaux",
    StringKey.DEBT_STUDIO_STAT_TOTAL to "Coût total",
    StringKey.DEBT_STUDIO_CHART_TITLE to "Solde dans le temps",
    StringKey.DEBT_STUDIO_CHART_HINT to "Plan A contre Plan B",
    StringKey.DEBT_STUDIO_NEGATIVE_AMORT_WARNING to "Ce paiement ne couvre pas les intérêts — le solde augmenterait au lieu de diminuer. Essayez un paiement plus élevé.",
    StringKey.DEBT_STUDIO_PHASE2_NOTE to "Les projections à taux variable et la séparation terme/amortissement hypothécaire arriveront en phase 2.",
    StringKey.DEBT_STUDIO_PLAN_A to "Plan A",
    StringKey.DEBT_STUDIO_PLAN_B to "Plan B",
    StringKey.DEBT_STUDIO_FREQ_MONTHLY to "Mensuel",
    StringKey.DEBT_STUDIO_FREQ_SEMI_MONTHLY to "Bimensuel",
    StringKey.DEBT_STUDIO_FREQ_ACCEL_BIWEEKLY to "Aux deux semaines accéléré",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_MONTHLY to "Paiement mensuel",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_SEMI_MONTHLY to "Paiement bimensuel",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_ACCEL_BIWEEKLY to "Paiement aux deux semaines accéléré",
    StringKey.DEBT_STUDIO_PREPAY_NOTE to "La plupart des prêts amortis plafonnent vos paiements supplémentaires annuels (souvent 10 à 20 % du capital initial pour les prêts hypothécaires). Vérifiez votre contrat.",
    StringKey.DEBT_STUDIO_DELTA_SAVES to "économise %s",
    StringKey.DEBT_STUDIO_DELTA_FINISHES_SOONER to "termine %s plus tôt",
    StringKey.DEBT_STUDIO_APPLY_TO_GOAL to "Appliquer à l'objectif",
    StringKey.DEBT_STUDIO_APPLIED_CHIP to "✓ Appliqué",
    StringKey.DEBT_STUDIO_APPLIED_TOAST to "%s enregistré",
    StringKey.DEBT_STUDIO_UNDO to "Annuler",
    StringKey.STUDIO_OTHER_GOALS_NOTE to "D'autres objectifs utilisant ce véhicule peuvent réduire votre droit disponible.",
    StringKey.STUDIO_SAVINGS_RATE_TITLE to "Part de revenu allouée à cet objectif",
    StringKey.STUDIO_SAVINGS_RATE_OUT_OF_POCKET to "de votre poche",
    StringKey.STUDIO_SAVINGS_RATE_TAX_SHIFTED to "économie d'impôt",
    StringKey.STUDIO_SAVINGS_RATE_EXPLAINER to "% de votre revenu brut versé à cet objectif chaque année (limité par le droit de cotisation lorsqu'applicable). Le graphique de croissance ci-dessus montre quel véhicule finit le plus haut — une part plus élevée ici ne signifie pas un meilleur résultat.",
    StringKey.STUDIO_DISCLAIMER to "Wiz3r vous montre le calcul selon les hypothèses fournies. Ceci n'est pas un conseil financier.",
    StringKey.STUDIO_UNSAVED_CHIP to "non enregistré",
    StringKey.STUDIO_FORM_GOAL_NAME_LABEL to "Nom de l'objectif",
    StringKey.STUDIO_FORM_CATEGORY_LABEL to "Catégorie",
    StringKey.STUDIO_FORM_TARGET_LABEL to "Montant cible",
    StringKey.STUDIO_FORM_DEADLINE_LABEL to "Échéance",
    StringKey.STUDIO_FORM_TARGET_DATE_INLINE to "Cible :",
    StringKey.STUDIO_PROGRESS_TITLE to "Progrès",
    StringKey.STUDIO_SAVE_ACTION to "✓ Enregistrer",
    StringKey.STUDIO_CANCEL_ACTION to "✕ Annuler",
    StringKey.STUDIO_CANCEL_TOOLTIP to "Revenir aux valeurs enregistrées précédemment",
    StringKey.STUDIO_DELETE_GOAL to "Supprimer",
    StringKey.GOAL_TYPE_PICKER_TITLE to "Quel type d'objectif ?",
    StringKey.GOAL_TYPE_PICKER_SAVINGS to "Objectif d'épargne",
    StringKey.GOAL_TYPE_PICKER_DEBT to "Remboursement de dette",
    StringKey.DEBT_STUDIO_GOAL_NAME_LABEL to "Nom de l'objectif",
    StringKey.DEBT_STUDIO_GOAL_BALANCE_LABEL to "Solde de départ",
    StringKey.DEBT_STUDIO_GOAL_RATE_LABEL to "Taux d'intérêt (%)",
    StringKey.DEBT_STUDIO_GOAL_TERM_LABEL to "Durée initiale (mois)",
    StringKey.DEBT_STUDIO_PLAN_TOOLTIP to "Votre plan de paiement actif",

    // Labo d'objectifs
    StringKey.LAB_TITLE to "Labo d'objectifs",
    StringKey.LAB_NAV_BUTTON to "Labo",
    StringKey.GOAL_FORM_LAB_CTA_LABEL to "Explorer dans le Labo d'objectifs",
    StringKey.GOAL_FORM_LAB_CTA_BODY to "Comparez les comptes, les échéanciers et les scénarios — sans modifier votre objectif.",
    StringKey.LAB_APPLY_TO_GOAL to "Appliquer à l'objectif",
    StringKey.LAB_APPLY_TITLE to "Appliquer ces changements ?",
    StringKey.LAB_APPLY_CONFIRM to "Appliquer",
    StringKey.LAB_APPLY_NO_CHANGES to "Aucun changement à appliquer",
    StringKey.LAB_SAVE_AS_GOAL to "Enregistrer comme objectif",
    StringKey.LAB_CLOSE to "Fermer",
    StringKey.LAB_FIELD_TARGET to "Objectif",
    StringKey.LAB_FIELD_DEADLINE to "Échéance",
    StringKey.LAB_FIELD_MONTHLY to "Cotisation mensuelle",
    StringKey.LAB_FIELD_ACCOUNT to "Compte",

    // Dashboard insight pool — lignes secondaires du widget Recommandation.
    StringKey.INSIGHT_BUDGET_OVERAGE_TITLE to "Budget dépassé",
    StringKey.INSIGHT_BUDGET_OVERAGE_DETAIL to "%s au-dessus du plan — %s dépensés sur %s budgétés.",
    StringKey.INSIGHT_MOVER_UP_TITLE to "%s en hausse de %s",
    StringKey.INSIGHT_MOVER_DOWN_TITLE to "%s en baisse de %s",
    StringKey.INSIGHT_MOVER_DETAIL to "%s en %s contre %s en moyenne sur 6 mois.",
    StringKey.INSIGHT_GOAL_BEHIND_TITLE to "%s prend du retard",
    StringKey.INSIGHT_GOAL_BEHIND_DETAIL to "Il faut %s par mois pour arriver à temps — %s mis de côté jusqu'ici.",
    StringKey.INSIGHT_YOY_UP_TITLE to "Dépenses en hausse de %s vs l'an dernier",
    StringKey.INSIGHT_YOY_DOWN_TITLE to "Dépenses en baisse de %s vs l'an dernier",
    StringKey.INSIGHT_YOY_DETAIL to "%s en %s contre %s le même mois l'an dernier.",
    StringKey.INSIGHT_LARGEST_CATEGORY_TITLE to "%s est votre plus grande dépense",
    StringKey.INSIGHT_LARGEST_CATEGORY_DETAIL to "%s — %s de tout ce que vous avez dépensé en %s.",
    StringKey.INSIGHT_STREAK_TITLE to "%s mois sur la cible",
    StringKey.INSIGHT_STREAK_DETAIL to "Vous avez atteint votre cible d'épargne de %s pendant %s mois d'affilée.",
    StringKey.INSIGHT_ALSO_WORTH_NOTING to "À surveiller aussi",
    StringKey.INSIGHT_AVAILABLE_TO_SAVE_LABEL to "disponible à épargner en %s",
    StringKey.INSIGHT_OVER_BUDGET_LABEL to "de dépassement en %s",
    StringKey.INSIGHT_OVER_BUDGET_GUIDANCE to "Réduire les dépenses passe avant de financer les objectifs. Couper un seul poste récurrent suffit souvent à renverser le mois.",
)
